import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Edit2,
  Eye,
  FileText,
  Search,
  Trash2,
  X,
  Filter,
  MoreVertical,
  Check,
  CheckCircle,
  Save,
  RefreshCw,
  LayoutDashboard,
} from "lucide-react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";

import {
  branchService,
  companyService,
  departmentService,
  designationService,
  employeeService,
  subDepartmentService,
  gradesService,
  categoryService,
  reasonsService,
  employeeCodeStructureService,
  confirmationService,
  transferService,
  salaryService,
  resignationService,
  separationPolicyService,
} from "../../services/api";
import { socket } from "../../services/websocket";
import Reports from "./Reports";
import Filters from "./Filters";
import DatePicker from "../Common/DatePicker";
import SearchableSelect from "./SearchableSelect";

import EmployeeProfileDetails from "./create-employee/EmployeeProfileDetails";

import Pagination from "../Common/Pagination";
import ActionInsightsPanel from "./ActionInsightsPanel";
import EmployeeDashboardPanel from "./EmployeeDashboardPanel";

import { API_URL } from "../../services/api";
import { validateEmployeeImportFile } from "../../lib/importValidator";
import Can from "../../rbac/Can";
import { usePermissions } from "../../rbac/PermissionsContext";
import {
  getPersonalDetailsExportRows,
  getBankDetailsExportRows,
  getOrgDetailsExportRows,
  getAllEmployeesExportRows,
  getFamilyDetailsExportRows,
  getQualificationDetailsExportRows,
  getProfessionalDetailsExportRows,
  getStatutoryDetailsExportRows,
  applyGroupedExportMerges,
} from "../../lib/employeeExportColumns";

// Salary Structure Components
import SalaryStructureManager from "./employee-management/salary-structure/SalaryStructureManager.jsx";
import StatutoryRatesManager from "./employee-management/salary-structure/StatutoryRatesManager.jsx";
import SalaryStructureSimulator from "./employee-management/salary-structure/SalaryStructureSimulator.jsx";
import AdvancedFormulaManager from "./employee-management/salary-structure/AdvancedFormulaManager.jsx";
import FormulaDemo from "./employee-management/salary-structure/FormulaDemo.jsx";
import LettersManager from "./employee-management/salary-structure/LettersManager.jsx";
import EnhancedSalaryStructureManager from "./employee-list/EmployeeSalaryStructureManager.jsx";

import EmployeeCodeStructure from "./employee-list/EmployeeCodeStructure.jsx";
import OnboardingPolicyComponent from "./OnboardingPolicyComponent.jsx";
import ConfirmationPolicyComponent from "./ConfirmationPolicyComponent.jsx";
import TransferPolicyComponent from "./TransferPolicyComponent.jsx";
import SeparationPolicyComponent from "./SeparationPolicyComponent.jsx";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";
import VirtualEmployeeTable from "./VirtualEmployeeTable";
import {
  calculateProfileCompletion,
  isFieldFilled,
  getNameByIdFromList,
  formatDate,
  toTitleCase,
} from "../../lib/employeeHelpers";
const EMPLOYEE_INSIGHTS_KEY = "employeeActionInsightsLog";

// ===== Date Validation Helpers (Frontend) =====
const isSameOrAfter = (date1, date2) => new Date(date1) >= new Date(date2);

const isTodayOrFuture = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const given = new Date(date);
  given.setHours(0, 0, 0, 0);
  return given >= today;
};

const isYesterdayOrOlder = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const given = new Date(date);
  given.setHours(0, 0, 0, 0);
  return given <= yesterday;
};

const getTodayDateYYYYMMDD = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const EMPLOYEE_TABLE_FROZEN_COLUMNS = [
  "actions",
  "photo",
  "slNo",
  "empCode",
  "name",
];

const EMPLOYEE_TABLE_LAST_FROZEN_COLUMN = "name";

function parseEmployeeListDate(val) {
  if (val == null || val === "") return 0;
  const s = String(val).trim();
  const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
}

function splitAdvancedParamList(value) {
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeEmployeeListRows(employeesData) {
  if (!Array.isArray(employeesData)) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return employeesData.map((employee) => {
    const status = String(employee?.employmentStatus || "").trim();
    const relievingDateRaw = employee?.relievedDate || employee?.dateOfLeaving;
    if (status !== "Resigned" || !relievingDateRaw) return employee;
    const relievingDate = new Date(relievingDateRaw);
    if (Number.isNaN(relievingDate.getTime())) return employee;
    relievingDate.setHours(0, 0, 0, 0);
    if (relievingDate < today) {
      return { ...employee, employmentStatus: "Relieved" };
    }
    return employee;
  });
}

function computeStatusCountsFromEmployees(rows) {
  const nextCounts = {
    total: rows.length,
    probation: 0,
    confirmed: 0,
    resigned: 0,
    relieved: 0,
    terminated: 0,
  };
  rows.forEach((emp) => {
    const status = String(emp?.employmentStatus || "").trim().toLowerCase();
    if (status === "probation") nextCounts.probation += 1;
    else if (status === "confirmed") nextCounts.confirmed += 1;
    else if (status === "resigned") nextCounts.resigned += 1;
    else if (status === "relieved") nextCounts.relieved += 1;
    else if (status === "terminated") nextCounts.terminated += 1;
  });
  return nextCounts;
}

/** Show full value on one line (no ellipsis); user can widen column via resize. */
const EMPLOYEE_TABLE_FULL_TEXT_COLUMNS = new Set([
  "empCode",
  "doj",
  "dol",
  "mobile",
  "email",
]);

function buildEmployeeTableStickyLeftMap(columnWidths) {
  const map = {};
  let left = 0;
  for (const key of EMPLOYEE_TABLE_FROZEN_COLUMNS) {
    map[key] = left;
    left += columnWidths[key] ?? 0;
  }
  return map;
}

function employeeTableFrozenStyle(columnKey, stickyLeftMap, { darkMode, isHeader }) {
  const left = stickyLeftMap[columnKey];
  if (left === undefined) return {};
  return {
    position: "sticky",
    left: `${left}px`,
    zIndex: isHeader ? 50 : 30,
    backgroundColor: isHeader
      ? darkMode
        ? "#3a3a3a"
        : "#e6eaef"
      : darkMode
      ? "#2C2C2C"
      : "#fafafa",
  };
}

function employeeTableFrozenClass(columnKey, stickyLeftMap, isLastFrozen = false) {
  if (stickyLeftMap[columnKey] === undefined) return "";
  return `employee-table-sticky-col${isLastFrozen ? " employee-table-sticky-edge" : ""}`;
}

/** Single-line table cell; truncates with ellipsis unless truncate=false. */
function EmployeeTableTextCell({
  children,
  align = "left",
  title,
  className = "",
  truncate,
  columnKey,
  stickyLeftMap,
  darkMode = false,
  isHeader = false,
}) {
  const alignClass =
    align === "center"
      ? "text-center"
      : align === "right"
      ? "text-right"
      : "text-left";
  const label =
    title ??
    (children == null || children === false
      ? ""
      : typeof children === "string" || typeof children === "number"
      ? String(children)
      : "");
  const stickyStyle =
    columnKey && stickyLeftMap
      ? employeeTableFrozenStyle(columnKey, stickyLeftMap, { darkMode, isHeader })
      : {};
  const shouldTruncate =
    truncate === undefined
      ? !(columnKey && EMPLOYEE_TABLE_FULL_TEXT_COLUMNS.has(columnKey))
      : truncate;
  const stickyClass =
    columnKey && stickyLeftMap
      ? employeeTableFrozenClass(
          columnKey,
          stickyLeftMap,
          columnKey === EMPLOYEE_TABLE_LAST_FROZEN_COLUMN
        )
      : "";
  return (
    <td
      className={`employee-table-td-text px-2 py-2 font-normal align-middle ${alignClass} ${
        shouldTruncate ? "" : "employee-table-td-full"
      } ${columnKey === "empCode" ? "employee-table-td-emp-code" : ""} ${stickyClass} ${className}`}
      style={stickyStyle}
    >
      <div className="employee-table-td-inner" title={label || undefined}>
        {children}
      </div>
    </td>
  );
}

// Helper function to convert number to words (Indian currency format)
const numberToWords = (num) => {
  if (!num || num === 0) return "";

  const numValue = parseFloat(num);
  if (isNaN(numValue) || numValue < 0) return "";

  // Convert to integer for word conversion
  const integerPart = Math.floor(numValue);

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertHundreds = (n) => {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };

  if (integerPart === 0) return "";

  let result = "";
  let remaining = integerPart;

  // Crores
  if (remaining >= 10000000) {
    const crores = Math.floor(remaining / 10000000);
    result += convertHundreds(crores) + " Crore ";
    remaining %= 10000000;
  }

  // Lakhs
  if (remaining >= 100000) {
    const lakhs = Math.floor(remaining / 100000);
    result += convertHundreds(lakhs) + " Lakh ";
    remaining %= 100000;
  }

  // Thousands
  if (remaining >= 1000) {
    const thousands = Math.floor(remaining / 1000);
    result += convertHundreds(thousands) + " Thousand ";
    remaining %= 1000;
  }

  // Hundreds, Tens, Ones
  if (remaining > 0) {
    result += convertHundreds(remaining);
  }

  return result.trim();
};

// Helper function to calculate percentage change
const calculatePercentageChange = (current, revised) => {
  if (!current || !revised || current === 0) return null;
  const currentNum = parseFloat(current);
  const revisedNum = parseFloat(revised);
  if (isNaN(currentNum) || isNaN(revisedNum)) return null;

  const change = ((revisedNum - currentNum) / currentNum) * 100;
  return change;
};

// Helper function to format numbers in Indian format (e.g., 3,00,000)
const formatIndianNumber = (num) => {
  if (num === null || num === undefined || num === "") return "";

  const numValue = parseFloat(num);
  if (isNaN(numValue)) return "";

  // Convert to integer (remove decimals)
  const integerPart = Math.floor(numValue);

  // Convert to string and reverse for easier processing
  const numStr = integerPart.toString();
  const reversed = numStr.split("").reverse();

  let result = [];
  for (let i = 0; i < reversed.length; i++) {
    // Add comma after first 3 digits
    if (i === 3) {
      result.push(",");
    }
    // Add comma every 2 digits after the first 3
    if (i > 3 && (i - 3) % 2 === 0) {
      result.push(",");
    }
    result.push(reversed[i]);
  }

  // Reverse back and join
  return result.reverse().join("");
};

// ---------- Date helper: accepts dd-mm-yyyy, dd/mm/yyyy or yyyy-mm-dd and returns YYYY-MM-DD or null ----------
function parseDateFlexible(input) {
  if (!input && input !== 0) return null;
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    // Convert Date to YYYY-MM-DD
    const y = input.getFullYear();
    const m = String(input.getMonth() + 1).padStart(2, "0");
    const d = String(input.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const s = String(input).trim();
  if (!s) return null;

  // If already ISO-ish (yyyy-mm-dd or yyyy/mm/dd)
  const isoMatch = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (isoMatch) {
    const y = isoMatch[1];
    const m = String(isoMatch[2]).padStart(2, "0");
    const d = String(isoMatch[3]).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // dd-mm-yyyy or dd/mm/yyyy
  const dmyMatch = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmyMatch) {
    const d = String(dmyMatch[1]).padStart(2, "0");
    const m = String(dmyMatch[2]).padStart(2, "0");
    const y = dmyMatch[3];
    return `${y}-${m}-${d}`;
  }

  // Try Date parse fallback (unsafe, but last resort)
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return null;
}

// Normalize a given object by converting known date keys to YYYY-MM-DD
function normalizeDatesForPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  // list of possible date keys used across forms
  const dateKeys = [
    "dateOfBirth",
    "date_of_birth",
    "dob",
    "dateOfJoin",
    "date_of_join",
    "dateOfJoining",
    "date_of_joining",
    "dateOfLeaving",
    "relievingDate",
    "relievedDate",
    "resignationDate",
    "resignation_date",
    "effectiveDate",
    "arrearsEffectiveDate",
    "confirmationDate",
    "service_commences_on",
    // family members uses:
    "familyMembers", // handled specially below
  ];

  const out = { ...payload };

  for (const k of dateKeys) {
    if (
      Object.prototype.hasOwnProperty.call(out, k) &&
      out[k] !== undefined &&
      out[k] !== null &&
      out[k] !== ""
    ) {
      // leave arrays / objects to be handled separately
      if (k === "familyMembers") continue;
      const parsed = parseDateFlexible(out[k]);
      if (parsed) out[k] = parsed;
      // if parsing failed, leave original (backend can validate)
    }
  }

  // Handle familyMembers array of objects (common case)
  if (Array.isArray(out.familyMembers) && out.familyMembers.length > 0) {
    out.familyMembers = out.familyMembers.map((fm) => {
      if (!fm || typeof fm !== "object") return fm;
      const copy = { ...fm };
      // common family date keys
      const fmDateKeys = ["date_of_birth", "dateOfBirth", "dob"];
      for (const fk of fmDateKeys) {
        if (Object.prototype.hasOwnProperty.call(copy, fk) && copy[fk]) {
          const p = parseDateFlexible(copy[fk]);
          if (p) copy[fk] = p;
        }
      }
      return copy;
    });
  }

  return out;
}

// Structures Content Component for Employee
const EmployeeStructuresContent = ({
  activeStructureTab,
  darkMode,
  branchesList,
  departmentsList,
  designationsList,
  subDepartmentsList,
  gradesList,
  categoriesList,
  patternType,
  employees,
  separationPolicies,
  setSeparationPolicies,
  fetchSeparationPolicies,
}) => {
  const [policySettingsSubTab, setPolicySettingsSubTab] = useState("onboarding");

  const policyCommonProps = {
    darkMode,
    branchesList,
    departmentsList,
    designationsList,
    subDepartmentsList,
    gradesList,
    categoriesList,
    companyName: MASTERS_DOCUMENT_COMPANY_NAME,
    companyAddress: MASTERS_DOCUMENT_COMPANY_ADDRESS,
    policyPreparedBy: "Human Resources",
    policyApprovedBy: "Management",
  };

  const renderContent = () => {
    switch (activeStructureTab) {
      case "employeeCode":
        return patternType === "automated" ? (
          <EmployeeCodeStructure
            darkMode={darkMode}
            branches={branchesList}
            departments={departmentsList}
          />
        ) : (
          <div
            className={`p-4 rounded-md ${
              darkMode ? "bg-gray-800" : "bg-white"
            } shadow-md`}
          >
            <div
              className={`p-3 rounded-md ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              } mb-4`}
            >
              <h3
                className={`text-base font-semibold mb-2 ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Manual Pattern Selected
              </h3>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Employee codes will be manually assigned. No automated pattern
                configuration is available.
              </p>
            </div>
          </div>
        );

      case "policies":
        return (
          <div
            className={`flex flex-col h-full rounded-md ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div
              className={`flex flex-wrap flex-shrink-0 border-b ${
                darkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              {[
                ["onboarding", "Onboarding"],
                ["confirmation", "Confirmation"],
                ["transfer", "Transfer"],
                ["separation", "Separation"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPolicySettingsSubTab(id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    policySettingsSubTab === id
                      ? darkMode
                        ? "border-redhalo-red text-redhalo-mint"
                        : "border-redhalo-red text-redhalo-navy"
                      : darkMode
                      ? "border-transparent text-gray-400 hover:text-gray-200"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {policySettingsSubTab === "onboarding" && (
                <OnboardingPolicyComponent {...policyCommonProps} />
              )}
              {policySettingsSubTab === "confirmation" && (
                <ConfirmationPolicyComponent {...policyCommonProps} />
              )}
              {policySettingsSubTab === "transfer" && (
                <TransferPolicyComponent {...policyCommonProps} />
              )}
              {policySettingsSubTab === "separation" && (
                <SeparationPolicyComponent
                  {...policyCommonProps}
                  employees={employees}
                  policies={separationPolicies}
                  onPolicyChange={fetchSeparationPolicies}
                />
              )}
            </div>
          </div>
        );

      case "salaryStructure":
        return (
          <EnhancedSalaryStructureManager
            darkMode={darkMode}
            employees={employees}
            departmentsList={departmentsList}
          />
        );

      case "lettersManagement":
        return <LettersManager />;

      default:
        return null;
    }
  };

  return <>{renderContent()}</>;
};

// Function to calculate profile completion percentage and get pending sections
// const calculateProfileCompletion = (employee) => {
//   if (!employee) return { percentage: 0, pendingSections: [] };

//   // Debug logging for Prathinav
//   if (employee.user?.name === "Prathinav" || employee.empCode === "EMP001") {
//     console.log("=== DEBUGGING PRATHINAV PROFILE COMPLETION ===");
//     console.log("Employee data:", employee);
//     console.log("isOrphan:", employee.isOrphan);
//     console.log("lessThanPrimary:", employee.lessThanPrimary);
//     console.log("isFresher:", employee.isFresher);
//     console.log("familyMembers:", employee.familyMembers);
//     console.log("qualifications:", employee.qualifications);
//     console.log("experiences:", employee.experiences);
//     console.log("documents:", employee.documents);
//   }

//   // SIMPLE CONCEPT: Each section is either complete or incomplete
//   const sections = [
//     {
//       name: "Organization Details",
//       isComplete: () => {
//         // Check if most essential org fields are filled (allow some flexibility)
//         const essentialFields = [
//           employee.empCode,
//           employee.user?.name,
//           employee.user?.gender,
//           employee.user?.dateOfBirth,
//           employee.branchId,
//           employee.designationId,
//           employee.departmentId,
//           employee.gradeId,
//           employee.categoryId,
//           employee.reportingManagerId,
//           employee.dateOfJoin,
//           employee.employmentType,
//           employee.employmentStatus,
//           employee.user?.phone,
//           employee.user?.email,
//           employee.email,
//           employee.user?.bloodGroup,
//           employee.aadharNumber,
//           employee.panNumber,
//           employee.ctc,
//         ];
//         const filledCount = essentialFields.filter((field) =>
//           isFieldFilled(field)
//         ).length;
//         return filledCount >= 20; // Allow 1 missing field (sub department)
//       },
//       blankFields: () => {
//         const fields = [
//           { field: employee.empCode, name: "Employee Code" },
//           { field: employee.user?.name, name: "Name" },
//           { field: employee.user?.gender, name: "Gender" },
//           { field: employee.user?.dateOfBirth, name: "Date of Birth" },
//           { field: employee.branchId, name: "Branch" },
//           { field: employee.designationId, name: "Designation" },
//           { field: employee.departmentId, name: "Department" },
//           { field: employee.subDepartmentId, name: "Sub Department" },
//           { field: employee.gradeId, name: "Grade" },
//           { field: employee.categoryId, name: "Category" },
//           { field: employee.reportingManagerId, name: "Reporting Manager" },
//           { field: employee.dateOfJoin, name: "Date of Join" },
//           { field: employee.employmentType, name: "Employment Type" },
//           { field: employee.employmentStatus, name: "Employment Status" },
//           { field: employee.user?.phone, name: "Mobile Number" },
//           { field: employee.user?.email, name: "Personal Email" },
//           { field: employee.email, name: "Official Email" },
//           { field: employee.user?.bloodGroup, name: "Blood Group" },
//           { field: employee.aadharNumber, name: "Aadhar Number" },
//           { field: employee.panNumber, name: "PAN Number" },
//           { field: employee.ctc, name: "CTC" },
//         ];
//         return fields.filter((f) => !isFieldFilled(f.field)).length;
//       },
//     },
//     {
//       name: "Personal Details",
//       isComplete: () => {
//         const essentialFields = [
//           employee.user?.maritalStatus,
//           employee.address?.address,
//           employee.address?.city,
//           employee.address?.state,
//           employee.address?.pincode,
//           employee.emergencyContactName,
//           employee.emergencyContactPhone,
//           employee.emergencyContactRelation,
//           employee.user?.photo,
//         ];
//         const filledCount = essentialFields.filter((field) =>
//           isFieldFilled(field)
//         ).length;
//         return filledCount >= 9; // Allow some flexibility
//       },
//       blankFields: () => {
//         const fields = [
//           { field: employee.user?.maritalStatus, name: "Marital Status" },
//           { field: employee.address?.address, name: "Present Address" },
//           { field: employee.address?.city, name: "Present City" },
//           { field: employee.address?.state, name: "Present State" },
//           { field: employee.address?.pincode, name: "Present Pincode" },
//           {
//             field: employee.address?.permanentAddress,
//             name: "Permanent Address",
//           },
//           { field: employee.address?.permanentCity, name: "Permanent City" },
//           { field: employee.address?.permanentState, name: "Permanent State" },
//           {
//             field: employee.address?.permanentPincode,
//             name: "Permanent Pincode",
//           },
//           {
//             field: employee.emergencyContactName,
//             name: "Emergency Contact Name",
//           },
//           {
//             field: employee.emergencyContactPhone,
//             name: "Emergency Contact Phone",
//           },
//           {
//             field: employee.emergencyContactRelation,
//             name: "Emergency Contact Relation",
//           },
//           { field: employee.user?.photo, name: "Profile Photo" },
//         ];
//         return fields.filter((f) => !isFieldFilled(f.field)).length;
//       },
//     },
//     {
//       name: "Family Details",
//       isComplete: () => {
//         // If orphan checkbox is checked, section is complete
//         if (employee.isOrphan === true) return true;
//         // If family members array exists and has data, section is complete
//         if (
//           employee.familyMembers &&
//           Array.isArray(employee.familyMembers) &&
//           employee.familyMembers.length > 0
//         ) {
//           return employee.familyMembers.some(
//             (member) =>
//               member &&
//               typeof member === "object" &&
//               Object.values(member).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           );
//         }
//         return false;
//       },
//       blankFields: () => {
//         if (employee.isOrphan === true) return 0;
//         if (
//           employee.familyMembers &&
//           Array.isArray(employee.familyMembers) &&
//           employee.familyMembers.length > 0
//         ) {
//           return employee.familyMembers.some(
//             (member) =>
//               member &&
//               typeof member === "object" &&
//               Object.values(member).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           )
//             ? 0
//             : 1;
//         }
//         return 1;
//       },
//     },
//     {
//       name: "Bank Details",
//       isComplete: () => {
//         const essentialFields = [
//           employee.bankName,
//           employee.accountNumber,
//           employee.ifscCode,
//           employee.bankBranch,
//         ];
//         const filledCount = essentialFields.filter((field) =>
//           isFieldFilled(field)
//         ).length;
//         return filledCount >= 4; // All 4 fields required
//       },
//       blankFields: () => {
//         const fields = [
//           { field: employee.bankName, name: "Bank Name" },
//           { field: employee.accountNumber, name: "Account Number" },
//           { field: employee.ifscCode, name: "IFSC Code" },
//           { field: employee.bankBranch, name: "Branch" },
//         ];
//         return fields.filter((f) => !isFieldFilled(f.field)).length;
//       },
//     },
//     {
//       name: "Educational Details",
//       isComplete: () => {
//         // If less than primary checkbox is checked, section is complete
//         if (employee.lessThanPrimary === true) return true;
//         // If qualifications array exists and has data, section is complete
//         if (
//           employee.qualifications &&
//           Array.isArray(employee.qualifications) &&
//           employee.qualifications.length > 0
//         ) {
//           return employee.qualifications.some(
//             (qual) =>
//               qual &&
//               typeof qual === "object" &&
//               Object.values(qual).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           );
//         }
//         return false;
//       },
//       blankFields: () => {
//         if (employee.lessThanPrimary === true) return 0;
//         if (
//           employee.qualifications &&
//           Array.isArray(employee.qualifications) &&
//           employee.qualifications.length > 0
//         ) {
//           return employee.qualifications.some(
//             (qual) =>
//               qual &&
//               typeof qual === "object" &&
//               Object.values(qual).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           )
//             ? 0
//             : 1;
//         }
//         return 1;
//       },
//     },
//     {
//       name: "Professional Details",
//       isComplete: () => {
//         // If fresher checkbox is checked, section is complete
//         if (employee.isFresher === true) return true;
//         // If experiences array exists and has data, section is complete
//         if (
//           employee.experiences &&
//           Array.isArray(employee.experiences) &&
//           employee.experiences.length > 0
//         ) {
//           return employee.experiences.some(
//             (exp) =>
//               exp &&
//               typeof exp === "object" &&
//               Object.values(exp).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           );
//         }
//         return false;
//       },
//       blankFields: () => {
//         if (employee.isFresher === true) return 0;
//         if (
//           employee.experiences &&
//           Array.isArray(employee.experiences) &&
//           employee.experiences.length > 0
//         ) {
//           return employee.experiences.some(
//             (exp) =>
//               exp &&
//               typeof exp === "object" &&
//               Object.values(exp).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           )
//             ? 0
//             : 1;
//         }
//         return 1;
//       },
//     },
//     {
//       name: "Statutory Details",
//       isComplete: () => {
//         const essentialFields = [
//           employee.pfUANNumber,
//           employee.esiIPNumber,
//           employee.esiState,
//           employee.esiDistrict,
//           employee.esiDispensary,
//         ];
//         const filledCount = essentialFields.filter((field) =>
//           isFieldFilled(field)
//         ).length;
//         return filledCount >= 5; // All 5 fields required
//       },
//       blankFields: () => {
//         const fields = [
//           { field: employee.pfUANNumber, name: "PF UAN Number" },
//           { field: employee.esiIPNumber, name: "ESI IP Number" },
//           { field: employee.esiState, name: "ESI State" },
//           { field: employee.esiDistrict, name: "ESI District" },
//           { field: employee.esiDispensary, name: "ESI Dispensary" },
//         ];
//         return fields.filter((f) => !isFieldFilled(f.field)).length;
//       },
//     },
//     {
//       name: "Documents",
//       isComplete: () => {
//         // If documents array exists and has data, section is complete
//         if (
//           employee.documents &&
//           Array.isArray(employee.documents) &&
//           employee.documents.length > 0
//         ) {
//           return employee.documents.some(
//             (doc) =>
//               doc &&
//               typeof doc === "object" &&
//               Object.values(doc).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           );
//         }
//         return false;
//       },
//       blankFields: () => {
//         if (
//           employee.documents &&
//           Array.isArray(employee.documents) &&
//           employee.documents.length > 0
//         ) {
//           return employee.documents.some(
//             (doc) =>
//               doc &&
//               typeof doc === "object" &&
//               Object.values(doc).some(
//                 (value) =>
//                   value !== null &&
//                   value !== undefined &&
//                   value !== "" &&
//                   (typeof value !== "string" || value.trim() !== "")
//               )
//           )
//             ? 0
//             : 1;
//         }
//         return 1;
//       },
//     },
//   ];

//   let completedSections = 0;
//   const pendingSections = [];

//   sections.forEach((section) => {
//     const isComplete = section.isComplete();
//     const blankFields = section.blankFields();

//     // Debug logging for Prathinav
//     if (employee.user?.name === "Prathinav" || employee.empCode === "EMP001") {
//       console.log(
//         `${section.name}: isComplete = ${isComplete}, blankFields = ${blankFields}`
//       );
//     }

//     if (isComplete) {
//       completedSections++;
//     } else {
//       // Calculate actual completed fields and total fields for each section
//       let completedFields, totalFields, percentage;

//       if (section.name === "Organization Details") {
//         const fields = [
//           { field: employee.empCode, name: "Employee Code" },
//           { field: employee.user?.name, name: "Name" },
//           { field: employee.user?.gender, name: "Gender" },
//           { field: employee.user?.dateOfBirth, name: "Date of Birth" },
//           { field: employee.branchId, name: "Branch" },
//           { field: employee.designationId, name: "Designation" },
//           { field: employee.departmentId, name: "Department" },
//           { field: employee.subDepartmentId, name: "Sub Department" },
//           { field: employee.gradeId, name: "Grade" },
//           { field: employee.categoryId, name: "Category" },
//           { field: employee.reportingManagerId, name: "Reporting Manager" },
//           { field: employee.dateOfJoin, name: "Date of Join" },
//           { field: employee.employmentType, name: "Employment Type" },
//           { field: employee.employmentStatus, name: "Employment Status" },
//           { field: employee.user?.phone, name: "Mobile Number" },
//           { field: employee.user?.email, name: "Personal Email" },
//           { field: employee.email, name: "Official Email" },
//           { field: employee.user?.bloodGroup, name: "Blood Group" },
//           { field: employee.aadharNumber, name: "Aadhar Number" },
//           { field: employee.panNumber, name: "PAN Number" },
//           { field: employee.ctc, name: "CTC" },
//         ];
//         completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
//         totalFields = fields.length;
//         percentage = Math.round((completedFields / totalFields) * 100);
//       } else if (section.name === "Personal Details") {
//         const fields = [
//           { field: employee.user?.maritalStatus, name: "Marital Status" },
//           { field: employee.address?.address, name: "Present Address" },
//           { field: employee.address?.city, name: "Present City" },
//           { field: employee.address?.state, name: "Present State" },
//           { field: employee.address?.pincode, name: "Present Pincode" },
//           {
//             field: employee.address?.permanentAddress,
//             name: "Permanent Address",
//           },
//           { field: employee.address?.permanentCity, name: "Permanent City" },
//           { field: employee.address?.permanentState, name: "Permanent State" },
//           {
//             field: employee.address?.permanentPincode,
//             name: "Permanent Pincode",
//           },
//           {
//             field: employee.emergencyContactName,
//             name: "Emergency Contact Name",
//           },
//           {
//             field: employee.emergencyContactPhone,
//             name: "Emergency Contact Phone",
//           },
//           {
//             field: employee.emergencyContactRelation,
//             name: "Emergency Contact Relation",
//           },
//           { field: employee.user?.photo, name: "Profile Photo" },
//         ];
//         completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
//         totalFields = fields.length;
//         percentage = Math.round((completedFields / totalFields) * 100);
//       } else if (section.name === "Bank Details") {
//         const fields = [
//           { field: employee.bankName, name: "Bank Name" },
//           { field: employee.accountNumber, name: "Account Number" },
//           { field: employee.ifscCode, name: "IFSC Code" },
//           { field: employee.bankBranch, name: "Branch" },
//         ];
//         completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
//         totalFields = fields.length;
//         percentage = Math.round((completedFields / totalFields) * 100);
//       } else if (section.name === "Statutory Details") {
//         const fields = [
//           { field: employee.pfUANNumber, name: "PF UAN Number" },
//           { field: employee.esiIPNumber, name: "ESI IP Number" },
//           { field: employee.esiState, name: "ESI State" },
//           { field: employee.esiDistrict, name: "ESI District" },
//           { field: employee.esiDispensary, name: "ESI Dispensary" },
//         ];
//         completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
//         totalFields = fields.length;
//         percentage = Math.round((completedFields / totalFields) * 100);
//       } else {
//         // For single-field sections (Family, Educational, Professional, Documents)
//         completedFields = isFieldFilled(
//           section.name === "Family Details"
//             ? employee.familyMembers
//             : section.name === "Educational Details"
//             ? employee.qualifications
//             : section.name === "Professional Details"
//             ? employee.experiences
//             : employee.documents
//         )
//           ? 1
//           : 0;
//         totalFields = 1;
//         percentage = completedFields * 100;
//       }

//       pendingSections.push({
//         name: section.name,
//         completed: completedFields,
//         total: totalFields,
//         blankFields: blankFields,
//         percentage: percentage,
//       });
//     }
//   });

//   // Calculate percentage: completed sections / total sections
//   const percentage = Math.round((completedSections / sections.length) * 100);

//   // Debug logging for Prathinav
//   if (employee.user?.name === "Prathinav" || employee.empCode === "EMP001") {
//     console.log(`=== FINAL RESULT ===`);
//     console.log(`Completed Sections: ${completedSections}/${sections.length}`);
//     console.log(`Percentage: ${percentage}%`);
//     console.log(`Pending Sections:`, pendingSections);
//   }

//   return { percentage, pendingSections };
// };

// Helper function to check if a field is filled
// const isFieldFilled = (field) => {
//   // Basic null/undefined/empty checks
//   if (field === null || field === undefined || field === "") return false;
//   if (
//     typeof field === "string" &&
//     (field === "N/A" ||
//       field === "Not set" ||
//       field === "null" ||
//       field.trim() === "")
//   )
//     return false;

//   // For arrays (familyMembers, qualifications, experiences, documents)
//   if (Array.isArray(field)) {
//     if (field.length === 0) return false;
//     // Check if at least one item in the array has meaningful data
//     return field.some((item) => {
//       if (typeof item === "object" && item !== null) {
//         // For objects within arrays, check if they have meaningful data
//         const values = Object.values(item);
//         return values.some((value) => {
//           if (value === null || value === undefined || value === "")
//             return false;
//           if (typeof value === "string" && value.trim() === "") return false;
//           return true;
//         });
//       }
//       // For primitive values in arrays
//       if (typeof item === "string" && item.trim() === "") return false;
//       return item !== null && item !== undefined && item !== "";
//     });
//   }

//   // For objects (like address)
//   if (typeof field === "object" && field !== null) {
//     const values = Object.values(field);
//     return values.some((value) => {
//       if (value === null || value === undefined || value === "") return false;
//       if (typeof value === "string" && value.trim() === "") return false;
//       return true;
//     });
//   }

//   // For primitive values (strings, numbers, booleans)
//   return true;
// };

export default function Employee({ darkMode, setCurrentPage, toggleDarkMode }) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canViewEmployeeList =
    isAdmin || hasScreenAccess("employee", "employee_list", "view");
  const canViewEmployeeReports =
    isAdmin || hasScreenAccess("employee", "employee_reports", "view");
  const canDownloadReports =
    isAdmin || hasScreenAccess("employee", "employee_reports", "export");
  const canCreateEmployeeRecords =
    isAdmin ||
    hasScreenAccess("employee", "employee_list", "create") ||
    hasScreenAccess("employee", "create_employee", "create");
  const canExportEmployeeRecords =
    isAdmin || hasScreenAccess("employee", "employee_export", "view");
  const canDownloadExports =
    isAdmin || hasScreenAccess("employee", "employee_export", "export");
  const canEditEmployeeRecords =
    isAdmin ||
    hasScreenAccess("employee", "edit_employee", "edit") ||
    hasScreenAccess("employee", "edit_employee", "view");
  const canRunConfirmation =
    isAdmin ||
    hasScreenAccess("employee", "confirmation", "create") ||
    hasScreenAccess("employee", "confirmation", "approve");
  const canRunTransfer =
    isAdmin ||
    hasScreenAccess("employee", "transfer", "create") ||
    hasScreenAccess("employee", "transfer", "edit");
  const canRunResignation =
    isAdmin ||
    hasScreenAccess("employee", "resignation", "create") ||
    hasScreenAccess("employee", "resignation", "approve");
  const canOpenEmployeeSettings = canEditEmployeeRecords;
  const canOpenAnyRowAction =
    canEditEmployeeRecords ||
    canRunConfirmation ||
    canRunTransfer ||
    canRunResignation;

  // Modal visibility states
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [tableHeight, setTableHeight] = useState(600);

  const handleResize = useCallback(() => {
    setTableHeight(window.innerHeight * 0.88 - 290);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showSalaryRevisionModal, setShowSalaryRevisionModal] = useState(false);
  const [showResignationModal, setShowResignationModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAllDataExportConfirmPopup, setShowAllDataExportConfirmPopup] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const [employeePhotoUrlCache, setEmployeePhotoUrlCache] = useState(
    () => new Map()
  );
  const [showImportConfirmationModal, setShowImportConfirmationModal] =
    useState(false);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);
  const [showProfileCompletionModal, setShowProfileCompletionModal] =
    useState(false);
  const [showSalaryHistoryModal, setShowSalaryHistoryModal] = useState(false);
  const [showTransferHistoryModal, setShowTransferHistoryModal] =
    useState(false);
  const [showSalaryReductionWarning, setShowSalaryReductionWarning] =
    useState(false);

  // Move these up so they're initialized before any useEffect referencing them
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showEmployeeInsights, setShowEmployeeInsights] = useState(false);
  const [showEmployeeDashboard, setShowEmployeeDashboard] = useState(false);
  const [dashboardEmployees, setDashboardEmployees] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [showImportSummaryModal, setShowImportSummaryModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [employeeActionLog, setEmployeeActionLog] = useState(() => {
    try {
      const raw = localStorage.getItem(EMPLOYEE_INSIGHTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(EMPLOYEE_INSIGHTS_KEY, JSON.stringify(employeeActionLog));
    } catch {
      /* ignore */
    }
  }, [employeeActionLog]);
  const addEmployeeInsight = useCallback(({ action, employee, pendingAt }) => {
    const employeeName =
      employee?.name || employee?.user?.name || employee?.full_name || employee?.empName || "Employee";
    const employeeCode = employee?.empCode || employee?.emp_code || employee?.employeeCode || "";
    const submittedAt = new Date().toLocaleString();
    setEmployeeActionLog((prev) => [
      {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        action,
        employeeCode: employeeCode || "-",
        employeeName: employeeCode ? employeeName : "-",
        employee: employeeCode ? `${employeeName} (${employeeCode})` : employeeName,
        pendingWith: pendingAt || "Approver",
        pendingAt: submittedAt,
        submittedAt,
        status: "Pending",
        approvedBy: "-",
        when: "-",
      },
      ...prev,
    ]);
  }, []);
  const handleEmployeeInsightRowAction = useCallback((insightAction, row) => {
    // Wire: view → form/modal; approve / reject / remove → API or task queue
  }, []);
  const employeeInsightItems = useMemo(
    () =>
      employeeActionLog.length
        ? employeeActionLog
        : [
            {
              action: "Confirmation",
              employee: "-",
              employeeName: "-",
              employeeCode: "-",
              pendingWith: "Reporting Manager",
              pendingAt: "-",
              status: "Pending",
              approvedBy: "-",
              when: "-",
            },
            {
              action: "Transfer",
              employee: "John (E002)",
              employeeName: "John",
              employeeCode: "E002",
              pendingWith: "HRBP",
              pendingAt: "28/03/2026, 09:00:00",
              status: "Approved",
              approvedBy: "HRBP",
              when: "30/03/2026, 11:00:00",
              approvedOn: "30/03/2026, 11:00:00",
            },
            {
              action: "Salary Revision",
              employee: "-",
              employeeName: "-",
              employeeCode: "-",
              pendingWith: "Comp & Ben",
              pendingAt: "-",
              status: "Pending",
              approvedBy: "-",
              when: "-",
            },
            {
              action: "Resignation",
              employee: "Jane (E003)",
              employeeName: "Jane",
              employeeCode: "E003",
              pendingWith: "Department Head",
              pendingAt: "01/04/2026, 10:15:00",
              status: "Rejected",
              rejectedBy: "HR Manager",
              rejectedOn: "02/04/2026, 15:30:00",
              approvedBy: "-",
              when: "-",
            },
          ],
    [employeeActionLog]
  );
  const searchInputRef = React.useRef(null);
  // Ensures rapid successive fetches (typing/filtering) don't let stale responses overwrite newer data.
  const employeesFetchSeqRef = React.useRef(0);
  const skipEmployeesFetchAfterModalApplyRef = React.useRef(false);
  const [resignationReasonsList, setResignationReasonsList] = useState([]); // <-- ADD THIS
  const [terminationReasonsList, setTerminationReasonsList] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    probation: 0,
    confirmed: 0,
    resigned: 0,
    relieved: 0,
    terminated: 0,
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [advancedFiltersParams, setAdvancedFiltersParams] = useState({});
  const [advancedFiltersState, setAdvancedFiltersState] = useState({});

  const applyAdvancedFiltersFromModal = useCallback(
    (employeesList, filters, params) => {
      const nextParams = params || {};
      setAdvancedFiltersParams(nextParams);
      setAdvancedFiltersState(filters || {});
      setPage(1);

      setSelectedStatuses(splitAdvancedParamList(nextParams.employmentStatus));
      setSelectedDepartments(splitAdvancedParamList(nextParams.departmentId));
      setSelectedEmploymentTypes(splitAdvancedParamList(nextParams.employmentType));

      const normalized = normalizeEmployeeListRows(employeesList);
      setEmployees(normalized);
      setTotalEmployees(normalized.length);
      setStatusCounts(computeStatusCountsFromEmployees(normalized));

      try {
        localStorage.setItem("essEmployeeListCache", JSON.stringify(normalized));
        window.dispatchEvent(new Event("ess:employees-updated"));
      } catch (cacheError) {
        console.warn("Failed to cache employee list for ESS access:", cacheError);
      }

      skipEmployeesFetchAfterModalApplyRef.current = true;
      setShowFilterPopup(false);
    },
    []
  );

  const clearAdvancedFilters = useCallback(() => {
    setAdvancedFiltersParams({});
    setAdvancedFiltersState({});
  }, []);

  const [viewMode, setViewMode] = useState("list");
  const [transferEmployeesList, setTransferEmployeesList] = useState([]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      // Trim whitespace to avoid accidental space-only queries
      setDebouncedSearchTerm(
        typeof searchTerm === "string" ? searchTerm.trim() : searchTerm
      );
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Keep input focused after results refresh to avoid user losing cursor while typing
  const _mountedRef = React.useRef(false);
  useEffect(() => {
    if (!_mountedRef.current) {
      _mountedRef.current = true;
      return;
    }
    try {
      if (
        searchInputRef.current &&
        typeof searchInputRef.current.focus === "function"
      ) {
        const el = searchInputRef.current;
        const pos = el.selectionStart || el.value.length || 0;
        el.focus();
        // restore caret position if possible
        try {
          el.setSelectionRange(pos, pos);
        } catch (e) {
          console.debug("Failed to restore caret position", e);
        }
      }
    } catch (e) {
      console.debug("Failed to refocus search input", e);
    }
  }, [debouncedSearchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearchTerm,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    itemsPerPage,
  ]);

  // Fetch all employees for transfer modal (reporting manager dropdown)
  useEffect(() => {
    const fetchTransferEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          page: 1,
          limit: 10000,
        });
        if (response.success && response.employees) {
          setTransferEmployeesList(response.employees);
        } else if (response.success && response.data) {
          setTransferEmployeesList(response.data);
        }
      } catch (err) {
        console.error("Error fetching employees for transfer:", err);
      }
    };
    fetchTransferEmployees();
  }, []);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Action dropdown state (for floating dropdown menu)
  const [openActionDropdown, setOpenActionDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const actionButtonRefs = React.useRef({});

  const [showForm, setShowForm] = useState(false);

  // Rejoin (Add New from existing employee): checkbox, dropdown, prefill data
  const [isRejoin, setIsRejoin] = useState(false);
  const [rejoinSelectedEmployeeId, setRejoinSelectedEmployeeId] = useState(null);
  const [rejoinPrefillData, setRejoinPrefillData] = useState(null);
  const [rejoinEmployeeList, setRejoinEmployeeList] = useState([]);
  const [rejoinListLoading, setRejoinListLoading] = useState(false);
  const [rejoinFetchingEmployee, setRejoinFetchingEmployee] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);

  const [showFormulaManager, setShowFormulaManager] = useState(false);
  const [dateOfJoiningFilter, setDateOfJoiningFilter] = useState("");
  const [dateOfLeavingFilter, setDateOfLeavingFilter] = useState("");

  // ...existing code...

  const [departmentsList, setDepartments] = useState([]);

  const [branchesList, setBranches] = useState([]);

  const [designationsList, setDesignations] = useState([]);

  const [subDepartmentsList, setSubDepartments] = useState([]);

  const [gradesList, setGrades] = useState([]);

  const [categoriesList, setCategories] = useState([]);

  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  const [searchParams] = useSearchParams();

  // Tab state for Employees and Reports (synced with ?tab=reports)
  const [activeEmployeeTab, setActiveEmployeeTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("tab") === "reports"
        ? "reports"
        : "employees";
    } catch {
      return "employees";
    }
  });
  const [employeesMenuOpen, setEmployeesMenuOpen] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [activeEmployeeReportsSubTab, setActiveEmployeeReportsSubTab] = useState("employees");
  const [employeeReportsSubTabTouched, setEmployeeReportsSubTabTouched] = useState(false);
  const [activeEmployeeExportSubTab, setActiveEmployeeExportSubTab] = useState("org");
  const [selectedExportEmployeeIds, setSelectedExportEmployeeIds] = useState([]);
  const employeeExportSections = useMemo(
    () => [
      { key: "org", label: "Organization Details" },
      { key: "personal", label: "Personal Details" },
      { key: "family", label: "Family Details" },
      { key: "bank", label: "Bank Details" },
      { key: "educational", label: "Education Details" },
      { key: "professional", label: "Professional Details" },
      { key: "statutory", label: "Statutory Details" },
      { key: "all", label: "All Employee Data" },
    ],
    []
  );

  useEffect(() => {
    const tab = searchParams.get("tab") === "reports" ? "reports" : "employees";
    setActiveEmployeeTab(tab);
    if (tab !== "reports") {
      setReportsMenuOpen(false);
      setExportMenuOpen(false);
    }
    if (tab === "employees") {
      setEmployeesMenuOpen(true);
    } else {
      setEmployeesMenuOpen(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeEmployeeTab === "reports" && !canViewEmployeeReports) {
      setActiveEmployeeTab("employees");
    }
    if (
      activeEmployeeTab === "employees" &&
      !canViewEmployeeList &&
      canViewEmployeeReports
    ) {
      setActiveEmployeeTab("reports");
    }
  }, [activeEmployeeTab, canViewEmployeeReports, canViewEmployeeList]);

  // New state for structures
  const [showStructuresModal, setShowStructuresModal] = useState(false);
  const [activeStructureTab, setActiveStructureTab] = useState("generalSettings");
  // General settings (ESS) - synced with company data
  const [enableESS, setEnableESS] = useState(true);
  const [enableESSByDefault, setEnableESSByDefault] = useState(false);
  const [generalSettingsSaving, setGeneralSettingsSaving] = useState(false);
  //const [sortField, setSortField] = useState(""); // currently sorted field
  const [sortOrder, setSortOrder] = useState("asc"); // asc or desc
  // Pattern type state for employee code structure
  const [patternType, setPatternType] = useState("automated");

  // Separation policies state
  const [separationPolicies, setSeparationPolicies] = useState([]);
  const [loadingSeparationPolicies, setLoadingSeparationPolicies] = useState(false);

  const getCompanyCodeForExcel = useCallback(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return "COMPANY";
      const company = typeof raw === "string" ? JSON.parse(raw) : raw;
      const code = String(company?.code || "")
        .trim()
        .replace(/[^A-Za-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
      return code || "COMPANY";
    } catch {
      return "COMPANY";
    }
  }, []);

  const buildModuleExcelFilename = useCallback((name, month = null, year = null) => {
    const code = getCompanyCodeForExcel();
    const now = new Date();
    const mm = String(
      Number.isFinite(Number(month)) && Number(month) >= 1 && Number(month) <= 12
        ? Number(month)
        : now.getMonth() + 1
    ).padStart(2, "0");
    const yyyy =
      Number.isFinite(Number(year)) && Number(year) > 0
        ? Number(year)
        : now.getFullYear();
    const safeName = String(name || "Export")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `${code}_${safeName}_${mm}_${yyyy}.xlsx`;
  }, [getCompanyCodeForExcel]);

  // Sync General Settings (Enable ESS) from company data (on mount and when modal opens)
  const syncGeneralSettingsFromCompany = useCallback(() => {
    const raw = localStorage.getItem("companyData");
    if (!raw) return;
    try {
      const company = JSON.parse(raw);
      if (company && typeof company.enableESS !== "undefined") {
        console.log("company.enableESS", company.enableESS);
        setEnableESS(company.enableESS === true || company.enableESS === "true");
      }
      if (company && typeof company.enableESSByDefault !== "undefined") {
        setEnableESSByDefault(company.enableESSByDefault === true || company.enableESSByDefault === "true");
      }
    } catch (_) {
      // ignore invalid or missing companyData
    }
  }, []);
  useEffect(syncGeneralSettingsFromCompany, [syncGeneralSettingsFromCompany]);
  useEffect(() => {
    if (showStructuresModal) syncGeneralSettingsFromCompany();
  }, [showStructuresModal, syncGeneralSettingsFromCompany]);
  useEffect(() => {
    const onCompanyDataUpdated = () => {
      const raw = localStorage.getItem("companyData");
      if (!raw) return;
      try {
        const company = JSON.parse(raw);
        if (company && typeof company.enableESS !== "undefined") {
          setEnableESS(company.enableESS === true || company.enableESS === "true");
        }
        if (company && typeof company.enableESSByDefault !== "undefined") {
          setEnableESSByDefault(company.enableESSByDefault === true || company.enableESSByDefault === "true");
        }
      } catch (_) {
        // ignore invalid or missing companyData
      }
    };
    window.addEventListener("companyDataUpdated", onCompanyDataUpdated);
    return () => window.removeEventListener("companyDataUpdated", onCompanyDataUpdated);
  }, []);

  // Fetch separation policies from backend
  const fetchSeparationPolicies = useCallback(async () => {
    try {
      setLoadingSeparationPolicies(true);
      const response = await separationPolicyService.getSeparationPolicies();
      if (response.success) {
        console.log("Separation policies fetched:", response.data);
        setSeparationPolicies(response.data || []);
      } else {
        toast.error("Failed to fetch separation policies");
        setSeparationPolicies([]);
      }
    } catch (error) {
      console.error("Error fetching separation policies:", error);
      toast.error("Failed to fetch separation policies");
      setSeparationPolicies([]);
    } finally {
      setLoadingSeparationPolicies(false);
    }
  }, []);

  // Helper function to find applicable separation policy for an employee
  // This function properly checks:
  // 1. Policy status (must be active)
  // 2. Effective date (must be on or before the reference date)
  // 3. Assignment criteria (department, employment type, status, employees, allEmployees)
  // 4. Returns the most applicable policy (prioritized by effective date)
  const findApplicableSeparationPolicy = useCallback(
    (employee, referenceDate = null, isRetroResignation = false) => {
      if (!employee || !separationPolicies || separationPolicies.length === 0) {
        return null;
      }

      // Use current date if no reference date provided
      const refDate = referenceDate
        ? new Date(referenceDate)
        : new Date();
      refDate.setHours(0, 0, 0, 0);

      // Filter policies that are:
      // 1. Active
      // 2. Effective on or before the reference date
      // 3. Allow retro resignations if needed
      const eligiblePolicies = separationPolicies.filter((policy) => {
        // Must be active
        if (policy.status !== "active") {
          return false;
        }

        // Check effective date
        if (policy.effectiveDate) {
          const effectiveDate = new Date(policy.effectiveDate);
          effectiveDate.setHours(0, 0, 0, 0);
          if (effectiveDate > refDate) {
            return false;
          }
        }

        // Check if retro resignation is allowed
        if (isRetroResignation && !policy.allowResignationsPriorToCurrentDate) {
          return false;
        }

        return true;
      });

      // Find matching policies based on assignment criteria
      const matchingPolicies = eligiblePolicies.filter((policy) => {
        const assignedTo = policy.assignedTo || {};

        // Check if assigned to all employees
        if (assignedTo.allEmployees) {
          return true;
        }

        // Check department match
        const deptMatch =
          assignedTo.departments &&
          assignedTo.departments.length > 0 &&
          assignedTo.departments.includes(employee.departmentId);

        // Check employment type match
        const empTypeMatch =
          assignedTo.employmentTypes &&
          assignedTo.employmentTypes.length > 0 &&
          assignedTo.employmentTypes.includes(employee.employmentType);

        // Check employment status match
        const empStatusMatch =
          assignedTo.employmentStatuses &&
          assignedTo.employmentStatuses.length > 0 &&
          assignedTo.employmentStatuses.includes(employee.employmentStatus);

        // Check specific employee match
        const empMatch =
          assignedTo.employees &&
          assignedTo.employees.length > 0 &&
          assignedTo.employees.includes(employee.id);

        // Policy matches if any assignment criteria matches
        return deptMatch || empTypeMatch || empStatusMatch || empMatch;
      });

      // If no matching policies, return null
      if (matchingPolicies.length === 0) {
        return null;
      }

      // Sort by effective date (most recent first), then by creation date
      // This ensures the most recent applicable policy is used
      matchingPolicies.sort((a, b) => {
        const dateA = new Date(a.effectiveDate || 0);
        const dateB = new Date(b.effectiveDate || 0);
        if (dateB.getTime() !== dateA.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        // If effective dates are same, prefer more specific assignments
        // (employee > department > employment type > status > all employees)
        const getSpecificity = (policy) => {
          const at = policy.assignedTo || {};
          if (at.employees && at.employees.length > 0) return 5;
          if (at.departments && at.departments.length > 0) return 4;
          if (at.employmentTypes && at.employmentTypes.length > 0) return 3;
          if (at.employmentStatuses && at.employmentStatuses.length > 0)
            return 2;
          if (at.allEmployees) return 1;
          return 0;
        };
        return getSpecificity(b) - getSpecificity(a);
      });

      return matchingPolicies[0];
    },
    [separationPolicies]
  );

  // Fetch separation policies on mount
  useEffect(() => {
    fetchSeparationPolicies();
  }, [fetchSeparationPolicies]);

  // Manual pattern effective date state
  const [manualEffectiveDate, setManualEffectiveDate] = useState("");

  const [showHistory, setShowHistory] = useState(false);

  const [history, setHistory] = useState([]);

  const [showHistoryPreviewModal, setShowHistoryPreviewModal] = useState(false);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);

  // Add this useEffect to watch the history state
  useEffect(() => {
    console.log("HISTORY STATE CHANGED:", history);
  }, [history]); // This runs *only* when the 'history' state variable itself changes

  // Fetch latest salary effective date
  const fetchLatestSalaryEffectiveDate = useCallback(async (employeeId) => {
    try {
      const response = await salaryService.getSalaryHistory(employeeId);
      if (response.success && response.data && response.data.length > 0) {
        // Get the most recent salary revision (they are ordered by created_at DESC)
        const latestSalary = response.data[0];
        setLatestSalaryEffectiveDate(latestSalary.effectiveDate);
      } else {
        setLatestSalaryEffectiveDate(null);
      }
    } catch (error) {
      console.error("Error fetching latest salary effective date:", error);
      setLatestSalaryEffectiveDate(null);
    }
  }, []);

  // Update button text when history state changes
  useEffect(() => {
    const button = document.getElementById("viewHistoryButton");
    if (button) {
      button.textContent = showHistory ? "Hide History" : "View History";
    }
  }, [showHistory]);

  // Get today's date for effective date validation (IST)
  const getTodayDate = useCallback(() => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    return istTime.toISOString().split("T")[0];
  }, []);

  // Initialize manual effective date to today
  useEffect(() => {
    if (!manualEffectiveDate) {
      setManualEffectiveDate(getTodayDate());
    }
  }, [manualEffectiveDate]);

  // Dropdown open states

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);

  const [employmentTypeDropdownOpen, setEmploymentTypeDropdownOpen] =
    useState(false);

  // Sorting and filtering states

  const [sortField, setSortField] = useState("dateOfJoin");

  const [sortDirection, setSortDirection] = useState("desc");

  const [columnFilters, setColumnFilters] = useState({});

  const [showColumnFilters, setShowColumnFilters] = useState({});

  // Column widths and row height for resizable table

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem("employeeTableColumnWidths");

    return saved
      ? JSON.parse(saved)
      : {
          profileCompletion: 80,

          actions: 60,

          photo: 60,

          slNo: 60,

          empCode: 100,

          name: 160,

          designation: 120,

          department: 120,

          branch: 100,

          doj: 120,

          dol: 120,

          mobile: 100,

          email: 160,

          employmentType: 100,

          status: 100,
        };
  });

  /** Default row height (px) when nothing saved in localStorage; increase for slightly taller rows */
  const DEFAULT_EMPLOYEE_TABLE_ROW_HEIGHT = 38;

  const [rowHeight, setRowHeight] = useState(() => {
    const saved = localStorage.getItem("employeeTableRowHeight");

    return saved ? parseInt(saved, 10) : DEFAULT_EMPLOYEE_TABLE_ROW_HEIGHT;
  });

  // Available options

  const statusOptions = [
    "Probation",
    "Confirmed",
    "Resigned",
    "Relieved",
    "Terminated",
  ];

  const employmentTypeOptions = [
    "Full-time",
    // "Part-time",
    // "Contract",
    // "Intern",
    // "Consultant",
    "External",
  ];

  // Functions to save column widths and row height

  const saveColumnWidths = useCallback((newWidths) => {
    setColumnWidths(newWidths);

    localStorage.setItem(
      "employeeTableColumnWidths",
      JSON.stringify(newWidths)
    );
  }, []);

  const saveRowHeight = useCallback((newHeight) => {
    setRowHeight(newHeight);

    localStorage.setItem("employeeTableRowHeight", newHeight.toString());
  }, []);

  // Handle column resize

  const handleColumnResize = useCallback(
    (column, newWidth) => {
      const updatedWidths = {
        ...columnWidths,
        [column]: Math.max(50, newWidth),
      };

      saveColumnWidths(updatedWidths);
    },
    [columnWidths, saveColumnWidths]
  );

  const employeeTableStickyLeft = useMemo(
    () => buildEmployeeTableStickyLeftMap(columnWidths),
    [columnWidths]
  );

  const employeeTableMinWidth = useMemo(
    () =>
      Object.values(columnWidths).reduce(
        (sum, w) => sum + (Number(w) || 0),
        0
      ),
    [columnWidths]
  );

  // Handle row height resize

  const handleRowHeightResize = useCallback(
    (newHeight) => {
      saveRowHeight(Math.max(30, Math.min(80, newHeight)));
    },
    [saveRowHeight]
  );

  useEffect(() => {
    const handleToggleHistory = async () => {
      console.log("handleToggleHistory called - toggling history modal");
      setShowHistory((prevShowHistory) => {
        console.log("Previous showHistory state:", prevShowHistory);
        if (!prevShowHistory) {
          // console.log("Opening history - fetching data from API");
          // When opening history, fetch the data from API
          employeeCodeStructureService
            .getStructureHistory()
            .then((data) => {
              console.log("History data received:", data);
              setHistory(data.data || []);
            })
            .catch((error) => {
              console.error(
                "Error fetching employee code structure history:",
                error
              );
              toast.error("Failed to load history");
            });
        }
        return !prevShowHistory;
      });
    };

    window.addEventListener("toggleHistory", handleToggleHistory);
    return () => {
      window.removeEventListener("toggleHistory", handleToggleHistory);
    };
  }, []); // Remove showHistory from dependency array

  // Fetch employee counts by status (independent of pagination)
  const fetchEmployeeCounts = useCallback(async () => {
    try {
      const params = {
        // Exclude UI pagination - we want counts for all matching records
        page: 1,
        limit: 10000,
        search: debouncedSearchTerm,
        employmentStatus: selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
        department: selectedDepartments.length > 0 ? selectedDepartments.join(",") : undefined,
        employmentType: selectedEmploymentTypes.length > 0 ? selectedEmploymentTypes.join(",") : undefined,

        // Date filters
        dateOfJoinFrom: columnFilters.dateOfJoinFrom,
        dateOfJoinTo: columnFilters.dateOfJoinTo,
        dateOfRelievingFrom: columnFilters.dateOfRelievingFrom,
        dateOfRelievingTo: columnFilters.dateOfRelievingTo,
        joiningDate: dateOfJoiningFilter,
        leavingDate: dateOfLeavingFilter,

        // Advanced filters from Filters modal
        ...advancedFiltersParams,
      };

      // Use employees list response to compute counts so footer always matches
      // currently applied filters (status/department/type/date/advanced/search).
      const response = await employeeService.getEmployees(params);
      if (response.success) {
        const rows = response?.data || response?.employees || [];
        const nextCounts = {
          total: rows.length,
          probation: 0,
          confirmed: 0,
          resigned: 0,
          relieved: 0,
          terminated: 0,
        };

        rows.forEach((emp) => {
          const status = String(emp?.employmentStatus || "").trim().toLowerCase();
          if (status === "probation") nextCounts.probation += 1;
          else if (status === "confirmed") nextCounts.confirmed += 1;
          else if (status === "resigned") nextCounts.resigned += 1;
          else if (status === "relieved") nextCounts.relieved += 1;
          else if (status === "terminated") nextCounts.terminated += 1;
        });

        setStatusCounts(nextCounts);
      }
    } catch (error) {
      console.error("Error fetching employee counts:", error);
      // Don't show error toast for counts - it's not critical
    }
  }, [
    debouncedSearchTerm,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    columnFilters,
    dateOfJoiningFilter,
    dateOfLeavingFilter,
    advancedFiltersParams,
  ]);

  const fetchEmployees = useCallback(async () => {
    const startTime = performance.now();
    console.log("📊 [Employee Fetch] Starting...");
    const fetchId = ++employeesFetchSeqRef.current;

    try {
      setLoading(true);

      const params = {
        page,
        limit: itemsPerPage === "all" ? 10000 : itemsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortField,
        sortOrder: sortDirection,
        employmentStatus: selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
        department: selectedDepartments.length > 0 ? selectedDepartments.join(",") : undefined,
        employmentType: selectedEmploymentTypes.length > 0 ? selectedEmploymentTypes.join(",") : undefined,

        // Date filters
        dateOfJoinFrom: columnFilters.dateOfJoinFrom,
        dateOfJoinTo: columnFilters.dateOfJoinTo,
        dateOfRelievingFrom: columnFilters.dateOfRelievingFrom,
        dateOfRelievingTo: columnFilters.dateOfRelievingTo,
        joiningDate: dateOfJoiningFilter,
        leavingDate: dateOfLeavingFilter,

        // Advanced filters from Filters modal
        ...advancedFiltersParams,
      };

      const apiStartTime = performance.now();
      const response = await employeeService.getEmployees(params);
      const apiEndTime = performance.now();
      const apiTime = ((apiEndTime - apiStartTime) / 1000).toFixed(2);

      console.log(`🌐 [Employee Fetch] API call completed in ${apiTime}s`);
      console.log("📦 [Employee Fetch] API Response:", response);

      // If another fetch started after this one, ignore this stale response.
      if (fetchId !== employeesFetchSeqRef.current) {
        console.log("⏭️ [Employee Fetch] Ignoring stale response");
        return;
      }

      const employeesData = response.employees || response.data || [];
      const normalizedEmployeesData = normalizeEmployeeListRows(employeesData);

      setEmployees(normalizedEmployeesData);

      // Persist auto-resolved status transitions in background so DB stays aligned.
      const statusUpdates = normalizedEmployeesData.filter((employee, index) => {
        const original = employeesData[index];
        return (
          String(original?.employmentStatus || "").trim() === "Resigned" &&
          String(employee?.employmentStatus || "").trim() === "Relieved"
        );
      });
      if (statusUpdates.length > 0) {
        const statusUpdateIds = statusUpdates.map((employee) => employee.id);
        employeeService
          .bulkAutoRelieveEmployees(statusUpdateIds)
          .then(() => fetchEmployeeCounts())
          .catch((err) =>
            console.warn("Failed persisting auto relieved statuses:", err)
          );
      }
      try {
        localStorage.setItem("essEmployeeListCache", JSON.stringify(employeesData));
        window.dispatchEvent(new Event("ess:employees-updated"));
      } catch (cacheError) {
        console.warn("Failed to cache employee list for ESS access:", cacheError);
      }

      // Set total count from response
      if (response.totalCount !== undefined)
        setTotalEmployees(response.totalCount);
      else if (response.meta?.total !== undefined)
        setTotalEmployees(response.meta.total);
      else if (response.total !== undefined) setTotalEmployees(response.total);
      else setTotalEmployees(normalizedEmployeesData.length); // Fallback

      const endTime = performance.now();
      const totalTime = ((endTime - startTime) / 1000).toFixed(2);
      console.log(
        `✅ [Employee Fetch] Total time: ${totalTime}s | Loaded ${normalizedEmployeesData.length} employees`
      );
    } catch (error) {
      // If another fetch started after this one, ignore errors from stale requests.
      if (fetchId !== employeesFetchSeqRef.current) return;
      const endTime = performance.now();
      const totalTime = ((endTime - startTime) / 1000).toFixed(2);
      console.error(`❌ [Employee Fetch] Failed after ${totalTime}s:`, error);
      toast.error("Failed to load employee data.");
    } finally {
      // Only clear loading for the latest in-flight request
      if (fetchId === employeesFetchSeqRef.current) setLoading(false);
    }
  }, [
    page,
    itemsPerPage,
    debouncedSearchTerm,
    sortField,
    sortDirection,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    columnFilters,
    dateOfJoiningFilter,
    dateOfLeavingFilter,
    advancedFiltersParams,
    fetchEmployeeCounts,
  ]);

  // Function to check and update employment status based on relieving date

  const checkAndUpdateEmploymentStatus = useCallback(async (employee) => {
    const relievingDateRaw = employee.relievedDate || employee.dateOfLeaving;
    if (employee.employmentStatus === "Resigned" && relievingDateRaw) {
      const today = new Date();

      const relievingDate = new Date(relievingDateRaw);

      // Set time to start of day for accurate comparison

      today.setHours(0, 0, 0, 0);

      relievingDate.setHours(0, 0, 0, 0);

      // If relieving date has passed (strictly before today), update status to Relieved

      if (relievingDate < today) {
        try {
          const updatedEmployee = { ...employee, employmentStatus: "Relieved" };

          await employeeService.updateEmployee(employee.id, updatedEmployee);

          // Update local state

          setEmployees((prevEmployees) =>
            prevEmployees.map((emp) =>
              emp.id === employee.id
                ? { ...emp, employmentStatus: "Relieved" }
                : emp
            )
          );

          return true; // Status was updated
        } catch (error) {
          console.error("Error updating employment status:", error);

          return false;
        }
      }
    }

    // Check if pending transfer should be applied (transfer effective date has passed)
    if (
      employee.transferEffectiveDate &&
      (employee.pendingBranchId ||
        employee.pendingDepartmentId ||
        employee.pendingSubDepartmentId ||
        employee.pendingDesignationId ||
        employee.pendingGradeId ||
        employee.pendingCategoryId)
    ) {
      const today = new Date();
      const transferDate = new Date(employee.transferEffectiveDate);

      // Set time to start of day for accurate comparison
      today.setHours(0, 0, 0, 0);
      transferDate.setHours(0, 0, 0, 0);

      // If transfer effective date has passed or is today, apply the transfer
      if (transferDate <= today) {
        try {
          const updatedEmployee = { ...employee };

          // Apply only the pending fields that exist
          if (employee.pendingBranchId) {
            updatedEmployee.branchId = employee.pendingBranchId;
            updatedEmployee.pendingBranchId = null;
          }
          if (employee.pendingDepartmentId) {
            updatedEmployee.departmentId = employee.pendingDepartmentId;
            updatedEmployee.pendingDepartmentId = null;
          }
          if (employee.pendingSubDepartmentId) {
            updatedEmployee.subDepartmentId = employee.pendingSubDepartmentId;
            updatedEmployee.pendingSubDepartmentId = null;
          }
          if (employee.pendingDesignationId) {
            updatedEmployee.designationId = employee.pendingDesignationId;
            updatedEmployee.pendingDesignationId = null;
          }
          if (employee.pendingGradeId) {
            updatedEmployee.gradeId = employee.pendingGradeId;
            updatedEmployee.pendingGradeId = null;
          }
          if (employee.pendingCategoryId) {
            updatedEmployee.categoryId = employee.pendingCategoryId;
            updatedEmployee.pendingCategoryId = null;
          }

          await employeeService.updateEmployee(employee.id, updatedEmployee);

          // Update local state
          setEmployees((prevEmployees) =>
            prevEmployees.map((emp) =>
              emp.id === employee.id ? { ...emp, ...updatedEmployee } : emp
            )
          );

          return true; // Transfer was applied
        } catch (error) {
          console.error("Error applying pending transfer:", error);
          return false;
        }
      }
    }

    return false; // No update needed
  }, []);

  // Function to check all employees and update their status

  const checkAllEmployeesStatus = useCallback(async () => {
    // Get current employees from state

    const currentEmployees = employees;

    if (currentEmployees.length === 0) return;

    const updates = [];

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    for (const employee of currentEmployees) {
      let shouldUpdate = false;

      let newStatus = employee.employmentStatus;

      // Check for resignation to relieved status change

      const relievingDateRaw = employee.relievedDate || employee.dateOfLeaving;
      if (employee.employmentStatus === "Resigned" && relievingDateRaw) {
        const relievingDate = new Date(relievingDateRaw);

        relievingDate.setHours(0, 0, 0, 0);

        if (relievingDate < today) {
          newStatus = "Relieved";

          shouldUpdate = true;
        }
      }

      // Check for probation to confirmed status change

      if (
        employee.employmentStatus === "Probation" &&
        employee.confirmationDate
      ) {
        const confirmDate = new Date(employee.confirmationDate);

        confirmDate.setHours(0, 0, 0, 0);

        if (confirmDate <= today) {
          newStatus = "Confirmed";

          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        updates.push({ ...employee, employmentStatus: newStatus });
      }
    }

    // Update all employees that need status change

    if (updates.length > 0) {
      try {
        const updatePromises = updates.map((employee) =>
          employeeService.updateEmployee(employee.id, {
            ...employee,
            employmentStatus: "Relieved",
          })
        );

        await Promise.all(updatePromises);

        // Update local state

        setEmployees((prevEmployees) =>
          prevEmployees.map((emp) => {
            const shouldUpdate = updates.find((u) => u.id === emp.id);

            return shouldUpdate
              ? { ...emp, employmentStatus: "Relieved" }
              : emp;
          })
        );

        console.log(`Updated ${updates.length} employee(s) status to Relieved`);

        toast.success(
          `Updated ${updates.length} employee(s) status to Relieved`
        );
        
        // Refresh counts after status update
        fetchEmployeeCounts();
      } catch (error) {
        console.error("Error updating employee statuses:", error);

        toast.error("Error updating employee statuses");
      }
    }
  }, [employees]);

  // Fetch initial data - OPTIMIZED: Fetch all in parallel
  useEffect(() => {
    const fetchAllMasterData = async () => {
      const startTime = performance.now();

      // Check cache first (valid for 5 minutes)
      const cacheKey = "employee_master_data";
      const cacheTimestampKey = "employee_master_data_timestamp";
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes

      if (cachedData && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < cacheValidDuration) {
          // Use cached data
          const data = JSON.parse(cachedData);
          setDepartments(data.departments || []);
          setBranches(data.branches || []);
          setDesignations(data.designations || []);
          setSubDepartments(data.subDepartments || []);
          setGrades(data.grades || []);
          setCategories(data.categories || []);
          setResignationReasonsList(data.resignationReasons || []);
          setTerminationReasonsList(data.terminationReasons || []);
          console.log(
            "✅ Loaded master data from cache in",
            ((performance.now() - startTime) / 1000).toFixed(2) + "s"
          );
          return;
        }
      }

      try {
        // Fetch all master data in parallel
        const [
          departmentsRes,
          branchesRes,
          designationsRes,
          subDepartmentsRes,
          gradesRes,
          categoriesRes,
          reasonsRes,
          terminationReasonsRes,
        ] = await Promise.all([
          departmentService
            .getDepartments()
            .catch((err) => ({ success: false, error: err })),
          branchService
            .getBranches()
            .catch((err) => ({ success: false, error: err })),
          designationService
            .getDesignations()
            .catch((err) => ({ success: false, error: err })),
          subDepartmentService
            .getAllSubDepartments()
            .catch((err) => ({ success: false, error: err })),
          gradesService
            .getGrades()
            .catch((err) => ({ success: false, error: err })),
          categoryService
            .getAllCategories()
            .catch((err) => ({ success: false, error: err })),
          reasonsService
            .getAllResignationReasons()
            .catch((err) => ({ success: false, error: err })),
          reasonsService
            .getAllTerminationReasons()
            .catch((err) => ({ success: false, error: err })),
        ]);

        // Set all data
        if (departmentsRes.success) setDepartments(departmentsRes.data);
        if (branchesRes.success) setBranches(branchesRes.data);
        if (designationsRes.success) setDesignations(designationsRes.data);
        if (subDepartmentsRes.success)
          setSubDepartments(subDepartmentsRes.data);
        if (gradesRes.success) setGrades(gradesRes.data);
        if (categoriesRes.success) setCategories(categoriesRes.data);
        if (reasonsRes.success) setResignationReasonsList(reasonsRes.data);
        if (terminationReasonsRes.success)
          setTerminationReasonsList(terminationReasonsRes.data);

        // Cache the results
        const cacheData = {
          departments: departmentsRes.data || [],
          branches: branchesRes.data || [],
          designations: designationsRes.data || [],
          subDepartments: subDepartmentsRes.data || [],
          grades: gradesRes.data || [],
          categories: categoriesRes.data || [],
          resignationReasons: reasonsRes.data || [],
          terminationReasons: terminationReasonsRes.data || [],
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        localStorage.setItem(cacheTimestampKey, Date.now().toString());

        const fetchTime = ((performance.now() - startTime) / 1000).toFixed(2);
        console.log(`✅ Fetched all master data in parallel: ${fetchTime}s`);
      } catch (error) {
        console.error("Error fetching master data:", error);
        toast.error("Failed to load some master data");
      }
    };

    fetchAllMasterData();
  }, []);

  // Fetch employee counts when filters change (not pagination)
  useEffect(() => {
    fetchEmployeeCounts();
  }, [
    debouncedSearchTerm,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    columnFilters,
    dateOfJoiningFilter,
    dateOfLeavingFilter,
    advancedFiltersParams,
    fetchEmployeeCounts,
  ]);

  // Fetch employees when filters or pagination change
  useEffect(() => {
    if (skipEmployeesFetchAfterModalApplyRef.current) {
      skipEmployeesFetchAfterModalApplyRef.current = false;
      return;
    }
    fetchEmployees();
  }, [
    page,
    itemsPerPage,
    debouncedSearchTerm,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    sortField,
    sortDirection,
    columnFilters,
    dateOfJoiningFilter,
    dateOfLeavingFilter,
    advancedFiltersParams,
    fetchEmployees,
  ]);

  useEffect(() => {
    if (!showEmployeeDashboard) return;
    let cancelled = false;
    setDashboardLoading(true);
    employeeService
      .getEmployees({ page: 1, limit: 10000 })
      .then((response) => {
        if (cancelled) return;
        const employeesData = response?.employees || response?.data || [];
        setDashboardEmployees(
          normalizeEmployeeListRows(
            Array.isArray(employeesData) ? employeesData : []
          )
        );
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load workforce dashboard data.");
        }
      })
      .finally(() => {
        if (!cancelled) setDashboardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showEmployeeDashboard]);

  // Fetch relieved employees for Rejoin dropdown when Add form is open and Rjoin is checked
  useEffect(() => {
    if (!showForm || selectedEmployee?.id || !isRejoin) return;
    let cancelled = false;
    setRejoinListLoading(true);
    employeeService
      .getRelievedEmployees()
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data ?? [];
        setRejoinEmployeeList(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setRejoinEmployeeList([]);
      })
      .finally(() => {
        if (!cancelled) setRejoinListLoading(false);
      });
    return () => { cancelled = true; };
  }, [showForm, isRejoin, selectedEmployee?.id]);

  // Search relieved employees (called when user clicks Search in Rjoin section)
  const onRejoinSearch = useCallback(async (searchText) => {
    setRejoinListLoading(true);
    try {
      const res = await employeeService.getRelievedEmployees({
        search: searchText ? String(searchText).trim() : undefined,
      });
      const raw = res?.data ?? [];
      setRejoinEmployeeList(Array.isArray(raw) ? raw : []);
    } catch {
      setRejoinEmployeeList([]);
    } finally {
      setRejoinListLoading(false);
    }
  }, []);

  // Check and update employment status when employees data changes

  useEffect(() => {
    if (employees.length > 0) {
      checkAllEmployeesStatus();
    }
  }, [employees.length]); // Only run when employees are first loaded

  // Set up daily check for status updates
  useEffect(() => {
    //console.log("Employees state has been updated:", employees);
    console.log("Number of employees in state:", employees.length);
  }, [employees]);

  useEffect(() => {
    if (employees.length === 0) return;
    // Check status immediately on component mount

    const timer = setTimeout(() => {
      checkAllEmployeesStatus();
    }, 2000); // Small delay to ensure employees are loaded

    // Set up interval to check daily (every 24 hours) ONLY when socket is NOT connected.
    // If a real-time socket is connected, it will push updates and we can avoid repeated polling.
    let dailyCheck = null;
    const startDaily = () => {
      if (!dailyCheck) {
        dailyCheck = setInterval(() => {
          checkAllEmployeesStatus();
        }, 24 * 60 * 60 * 1000); // 24 hours
      }
    };
    const stopDaily = () => {
      if (dailyCheck) {
        clearInterval(dailyCheck);
        dailyCheck = null;
      }
    };

    // Start polling only if socket is not connected
    if (!socket || !socket.connected) startDaily();

    // When socket connects, stop the polling; when it disconnects, restart it
    const onConnect = () => stopDaily();
    const onDisconnect = () => startDaily();
    socket && socket.on && socket.on("connect", onConnect);
    socket && socket.on && socket.on("disconnect", onDisconnect);

    return () => {
      clearTimeout(timer);
      stopDaily();
      socket && socket.off && socket.off("connect", onConnect);
      socket && socket.off && socket.off("disconnect", onDisconnect);
    };
  }, [employees.length]); // Only depend on length to avoid infinite loops

  // WebSocket: listen for employee and confirmation updates and patch single item in state
  useEffect(() => {
    const handleEmployeeUpdate = (payload) => {
      if (!payload) return;
      const id = payload.id || payload.employeeId || payload.employeeId;
      if (!id) return;
      setEmployees((prev) =>
        prev.map((e) =>
          String(e.id) === String(id) || String(e.employeeId) === String(id)
            ? { ...e, ...payload }
            : e
        )
      );
    };

    socket.on("employee-updated", handleEmployeeUpdate);
    socket.on("confirmation-updated", handleEmployeeUpdate);

    return () => {
      socket.off("employee-updated", handleEmployeeUpdate);
      socket.off("confirmation-updated", handleEmployeeUpdate);
    };
  }, []);

  // Check status when page becomes visible (user returns to tab)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && employees.length > 0) {
        checkAllEmployeesStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [employees.length]);

  // Listen for custom event to close employee form
  useEffect(() => {
    const handleCloseEmployeeForm = () => {
      if (showForm) {
        setShowForm(false);
        setSelectedEmployee(null);
        setError(null);
      }
    };

    window.addEventListener("closeEmployeeForm", handleCloseEmployeeForm);
    return () => {
      window.removeEventListener("closeEmployeeForm", handleCloseEmployeeForm);
    };
  }, [showForm]);

  // Handle click outside to close actions panel
  // Handle click outside to close actions panel - CORRECTED VERSION (Copy-Paste Ready)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 1. Check if any relevant modal is currently displayed
      const isAnyModalOpen =
        showConfirmationModal ||
        showTransferModal ||
        showSalaryRevisionModal ||
        showSalaryHistoryModal ||
        showTransferHistoryModal ||
        showResignationModal ||
        showViewModal ||
        showImportModal ||
        showExportModal ||
        showAllDataExportConfirmPopup ||
        showPhotoModal ||
        showStructuresModal ||
        showImportConfirmationModal ||
        showImportResultsModal ||
        showProfileCompletionModal ||
        showImportSummaryModal; // Make sure all modals are listed

      // 2. Check if the click happened inside what looks like a modal overlay
      const clickedInsideModalOverlay = event.target.closest(
        ".fixed.inset-0.bg-black.bg-opacity-50" // Standard modal background selector
      );

      // 3. Get a reference to the actions panel itself (if it exists)
      const actionsPanel = document.querySelector("[data-actions-panel]");

      // 4. Determine if the click was *outside* the actions panel
      const clickedOutsideActionsPanel =
        actionsPanel && !actionsPanel.contains(event.target);

      // 5. Apply the closing logic ONLY if all these conditions are true:
      //    - An employee is selected.
      //    - The main employee *form* (`showForm`) is NOT open.
      //    - NO modal (`isAnyModalOpen`) is currently open.
      //    - The click was NOT inside a modal overlay.
      //    - The click *was* outside the actions panel.
      if (
        selectedEmployee &&
        !showForm &&
        !isAnyModalOpen && // Use the combined check here
        !clickedInsideModalOverlay &&
        clickedOutsideActionsPanel
      ) {
        setSelectedEmployee(null); // Close the actions panel
      }
    };

    // --- Conditional Listener Attachment ---
    // Only attach the listener if the actions panel might be visible AND no modal is open.
    const shouldAttachListener =
      selectedEmployee &&
      !showForm &&
      !(
        // Check if *any* modal is open here
        (
          showConfirmationModal ||
          showTransferModal ||
          showSalaryRevisionModal ||
          showSalaryHistoryModal ||
          showTransferHistoryModal ||
          showResignationModal ||
          showViewModal ||
          showImportModal ||
          showExportModal ||
          showAllDataExportConfirmPopup ||
          showPhotoModal ||
          showStructuresModal ||
          showImportConfirmationModal ||
          showImportResultsModal ||
          showProfileCompletionModal ||
          showImportSummaryModal
        ) // Make sure all modals are listed
      );

    if (shouldAttachListener) {
      // Add the listener ONLY if the conditions are met
      document.addEventListener("mousedown", handleClickOutside);
    }

    // --- Cleanup Function ---
    // This runs BEFORE the effect runs again OR when the component unmounts.
    // It ALWAYS removes the listener that might have been attached previously.
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    // --- Dependency Array ---
    // List ALL state variables that affect the listener's logic or attachment.
    selectedEmployee,
    showForm,
    // Add ALL modal state variables:
    showConfirmationModal,
    showSalaryRevisionModal,
    showSalaryHistoryModal,
    showTransferModal,
    showTransferHistoryModal,
    showResignationModal,
    showViewModal,
    showImportModal,
    showExportModal,
    showAllDataExportConfirmPopup,
    showPhotoModal,
    showStructuresModal,
    showImportConfirmationModal,
    showImportResultsModal,
    showProfileCompletionModal,
    showImportSummaryModal, // Make sure all modals are listed
    // Add any other modal states...
  ]);

  // Helper function to calculate dropdown position based on viewport
  const calculateDropdownPosition = (buttonRect) => {
    const estimatedMenuHeight = 280; // Estimated height of the dropdown menu (header + 5 items)
    const gap = 4; // Gap between button and menu (only used when opening downward)
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    // Calculate available space above and below the button
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    // Only open upward if there's actually not enough space below (more precise check)
    const wouldOverflowBottom = spaceBelow < estimatedMenuHeight + gap;
    
    // Determine if menu should open upward
    // Open upward only if: would overflow bottom AND there's enough space above
    // This ensures we only open upward when necessary, not just because it's in bottom half
    const shouldOpenUpward = wouldOverflowBottom && spaceAbove >= estimatedMenuHeight;
    
    let top, left;
    
    if (shouldOpenUpward) {
      // Position above the button - align menu bottom with button top (no gap)
      top = buttonRect.top + scrollY - estimatedMenuHeight;
      // Ensure menu doesn't go above viewport top
      if (top < scrollY) {
        top = scrollY + 10; // 10px padding from top as fallback
      }
    } else {
      // Position below the button (default) - with small gap
      top = buttonRect.bottom + scrollY + gap;
      // Ensure menu doesn't go below viewport (with some padding)
      const maxTop = scrollY + viewportHeight - estimatedMenuHeight - 10;
      if (top > maxTop) {
        // If we can't fit below, try above if there's space
        if (spaceAbove >= estimatedMenuHeight) {
          top = buttonRect.top + scrollY - estimatedMenuHeight; // No gap when opening upward
          if (top < scrollY) {
            top = scrollY + 10;
          }
        } else {
          // Last resort: position at bottom of viewport
          top = scrollY + viewportHeight - estimatedMenuHeight - 10;
        }
      }
    }
    
    // Align left edge, but ensure menu doesn't overflow right edge of viewport
    left = buttonRect.left + scrollX;
    const menuWidth = 180; // minWidth from the menu style
    if (left + menuWidth > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - menuWidth - 10; // 10px padding from edge
    }
    // Ensure menu doesn't go off left edge
    if (left < scrollX + 10) {
      left = scrollX + 10;
    }
    
    return { top, left };
  };

  // Update dropdown position on scroll
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (openActionDropdown && actionButtonRefs.current[openActionDropdown]) {
        const button = actionButtonRefs.current[openActionDropdown];
        const rect = button.getBoundingClientRect();
        setDropdownPosition(calculateDropdownPosition(rect));
      }
    };

    if (openActionDropdown) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);

      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [openActionDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionDropdown && !event.target.closest(".action-dropdown")) {
        setOpenActionDropdown(null);
      }
    };

    if (openActionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openActionDropdown]);

  // Load saved pattern type from API
  useEffect(() => {
    const loadPatternType = async () => {
      try {
        const data = await employeeCodeStructureService.getCurrentStructure();
        const structure = data.data;

        if (structure) {
          setPatternType(structure.patternType || "manual");
          // Load manual effective date if it's a manual pattern
          if (structure.patternType === "manual" && structure.effectiveDate) {
            setManualEffectiveDate(structure.effectiveDate);
          }
        } else {
          setPatternType("manual"); // Default to manual if no structure found
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // No structure found - default to manual for new companies
          setPatternType("manual");
        } else {
          console.error("Error loading pattern type:", error);
          setPatternType("manual"); // Default to manual on error
        }
      }
    };

    loadPatternType();
  }, []);

  // Close dropdowns when clicking outside

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setStatusDropdownOpen(false);

        setDepartmentDropdownOpen(false);

        setEmploymentTypeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debug: Log when departments are loaded

  useEffect(() => {
    if (departmentsList.length > 0) {
      console.log("Departments loaded:", departmentsList);
    }
  }, [departmentsList]);

  // Client-side fallback filtering: when a search term is present, filter
  // the currently loaded employees across multiple fields (name, email,
  // empCode, mobile/phone, PAN, Aadhar, and other nested string values).
  const matchesSearch = (emp, term) => {
    if (!term) return true;
    const t = term.toLowerCase();

    // Common direct fields
    const directChecks = [
      emp.empCode,
      emp.email,
      emp.panNumber,
      emp.panNo,
      emp.aadharNumber,
      emp.aadhaar,
      emp.aadhar,
    ];

    for (const v of directChecks) {
      if (v && String(v).toLowerCase().includes(t)) return true;
    }

    // user object (name, phone, email)
    if (emp.user) {
      const name = `${emp.user.name || ""} ${emp.user.lastName || ""}`.trim();
      if (name.toLowerCase().includes(t)) return true;
      if (emp.user.email && emp.user.email.toLowerCase().includes(t))
        return true;
      if (emp.user.phone && emp.user.phone.toLowerCase().includes(t))
        return true;
      if (emp.user.mobile && emp.user.mobile.toLowerCase().includes(t))
        return true;
    }

    // Some APIs put phone/email at top-level under different keys
    if (emp.phone && String(emp.phone).toLowerCase().includes(t)) return true;
    if (emp.mobile && String(emp.mobile).toLowerCase().includes(t)) return true;

    // Generic recursive search for likely identification fields (PAN/Aadhar)
    const searchObject = (obj, depth = 0) => {
      if (!obj || depth > 3) return false;
      if (typeof obj === "string" || typeof obj === "number") {
        return String(obj).toLowerCase().includes(t);
      }
      if (Array.isArray(obj)) {
        for (const item of obj) {
          if (searchObject(item, depth + 1)) return true;
        }
        return false;
      }
      if (typeof obj === "object") {
        for (const key of Object.keys(obj)) {
          // prioritize keys that likely contain IDs/identifiers
          if (/pan|aadhar|aadhaar|id|number|no/i.test(key)) {
            const val = obj[key];
            if (
              (typeof val === "string" || typeof val === "number") &&
              String(val).toLowerCase().includes(t)
            )
              return true;
          }
        }
        // fallback: scan some nested values
        for (const key of Object.keys(obj)) {
          if (searchObject(obj[key], depth + 1)) return true;
        }
      }
      return false;
    };

    return searchObject(emp, 0);
  };

  // Check if any non-default filters are currently active
  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearchTerm ||
      selectedStatuses.length > 0 ||
      selectedDepartments.length > 0 ||
      selectedEmploymentTypes.length > 0 ||
      Object.keys(columnFilters).some((key) => {
        const value = columnFilters[key];
        return value && value !== "" && value !== "all";
      }) ||
      dateOfJoiningFilter ||
      dateOfLeavingFilter ||
      Object.keys(advancedFiltersParams).some((key) => {
        const value = advancedFiltersParams[key];
        return value && value !== "" && value !== "all";
      })
    );
  }, [
    debouncedSearchTerm,
    selectedStatuses,
    selectedDepartments,
    selectedEmploymentTypes,
    columnFilters,
    dateOfJoiningFilter,
    dateOfLeavingFilter,
    advancedFiltersParams,
  ]);

  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    if (!debouncedSearchTerm) return employees;
    return employees.filter((emp) => matchesSearch(emp, debouncedSearchTerm));
  }, [employees, debouncedSearchTerm]);

  // Helper functions for sorting - MUST be defined before sortedEmployees
  // Helper to map field names to their nested object equivalents
  const getNestedFieldName = (fieldName) => {
    const fieldMap = {
      'branch': 'branch',
      'department': 'department', 
      'designation': 'designation',
      'subDepartment': 'subDepartment',
      'grade': 'grade',
      'category': 'category',
      'reportingManager': 'reportingManager'
    };
    return fieldMap[fieldName];
  };

  // Local getNameByIdFromList function (shadows the imported one to add getNestedFieldName support)
  function getNameByIdFromList(id, list, employeeData = null, fieldName = null) {
    // First check if employee data contains the nested object with the name
    if (employeeData && fieldName) {
      const nestedField = getNestedFieldName(fieldName);
      if (nestedField && employeeData[nestedField] && employeeData[nestedField].name) {
        return employeeData[nestedField].name.trim();
      }
    }
    
    // Fallback to list lookup
    if (!list || !Array.isArray(list)) return "-";
    const item = list.find((obj) => obj.id === id);
    return item && item.name ? item.name.trim() : "-";
  }

  // Fixed table size - always show exactly 10 rows for consistent table size

  const tableRowCount = 10;

  const employeeTableScrollRef = React.useRef(null);
  const employeeTableScrollTimeoutRef = React.useRef(null);
  const [employeeTableScrolling, setEmployeeTableScrolling] = useState(false);

  const employeeTableScrollHeightPx = useMemo(
    () => (tableRowCount + 1) * rowHeight + tableRowCount + 16,
    [rowHeight]
  );

  const handleEmployeeTableScroll = useCallback(() => {
    setEmployeeTableScrolling(true);
    if (employeeTableScrollTimeoutRef.current) {
      clearTimeout(employeeTableScrollTimeoutRef.current);
    }
    employeeTableScrollTimeoutRef.current = setTimeout(() => {
      setEmployeeTableScrolling(false);
    }, 900);
  }, []);

  useEffect(
    () => () => {
      if (employeeTableScrollTimeoutRef.current) {
        clearTimeout(employeeTableScrollTimeoutRef.current);
      }
    },
    []
  );

  const displayCount =
    itemsPerPage === "all" ? filteredEmployees.length : itemsPerPage;

  // Sort filtered employees

  const sortedEmployees = [...(filteredEmployees || [])].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;

    switch (sortField) {
      case "empCode":
        aValue = a.empCode || "";

        bValue = b.empCode || "";

        break;

      case "name":
        aValue = `${a.user.name} ${a.user.lastName || ""}`;

        bValue = `${b.user.name} ${b.user.lastName || ""}`;

        break;

      case "designation":
        aValue = getNameByIdFromList(a.designationId, designationsList, a, 'designation') || "";

        bValue = getNameByIdFromList(b.designationId, designationsList, b, 'designation') || "";

        break;

      case "department":
        aValue = getNameByIdFromList(a.departmentId, departmentsList, a, 'department') || "";

        bValue = getNameByIdFromList(b.departmentId, departmentsList, b, 'department') || "";

        break;

      case "branch":
        aValue = getNameByIdFromList(a.branchId, branchesList, a, 'branch') || "";

        bValue = getNameByIdFromList(b.branchId, branchesList, b, 'branch') || "";

        break;

      case "dateOfJoin":
        aValue = a.dateOfJoin || a.joiningDate || a.joinDate || "";

        bValue = b.dateOfJoin || b.joiningDate || b.joinDate || "";

        break;

      case "dateOfRelieving":
        aValue = getEmployeeRelievingDate(a) || "";

        bValue = getEmployeeRelievingDate(b) || "";

        break;

      case "mobile":
        aValue = a.user?.phone || "";

        bValue = b.user?.phone || "";

        break;

      case "email":
        aValue = a.email || "";

        bValue = b.email || "";

        break;

      case "employmentType":
        aValue = a.employmentType || "";

        bValue = b.employmentType || "";

        break;

      case "status":
        aValue = a.employmentStatus || "";

        bValue = b.employmentStatus || "";

        break;

      default:
        return 0;
    }

    if (sortField === "dateOfJoin" || sortField === "dateOfRelieving") {
      const aNum = parseEmployeeListDate(aValue);
      const bNum = parseEmployeeListDate(bValue);
      return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
    }

    if (sortDirection === "asc") {
      return String(aValue).localeCompare(String(bValue));
    }
    return String(bValue).localeCompare(String(aValue));
  });

  // Always show exactly 10 rows in table for consistent size, with internal scroll for more data

  const currentEmployees = Array.from({ length: tableRowCount }, (_, index) => {
    return index < sortedEmployees.length ? sortedEmployees[index] : null;
  });

  // For display purposes - actual employees to show based on selection

  const displayEmployees = sortedEmployees;

  // Handle checkbox changes for multi-select

  const handleStatusChange = useCallback((status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  }, []);

  const handleDepartmentChange = useCallback((departmentId) => {
    setSelectedDepartments((prev) =>
      prev.includes(departmentId)
        ? prev.filter((d) => d !== departmentId)
        : [...prev, departmentId]
    );
  }, []);

  const handleEmploymentTypeChange = useCallback((type) => {
    setSelectedEmploymentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }, []);

  // Handle dropdown open/close - only one can be open at a time

  const handleDropdownToggle = useCallback(
    (dropdownType) => {
      if (dropdownType === "status") {
        setStatusDropdownOpen(!statusDropdownOpen);

        setDepartmentDropdownOpen(false);

        setEmploymentTypeDropdownOpen(false);
      } else if (dropdownType === "department") {
        setDepartmentDropdownOpen(!departmentDropdownOpen);

        setStatusDropdownOpen(false);

        setEmploymentTypeDropdownOpen(false);
      } else if (dropdownType === "employmentType") {
        setEmploymentTypeDropdownOpen(!employmentTypeDropdownOpen);

        setStatusDropdownOpen(false);

        setDepartmentDropdownOpen(false);
      }
    },
    [statusDropdownOpen, departmentDropdownOpen, employmentTypeDropdownOpen]
  );

  // Get display text for dropdowns

  const getStatusDisplayText = useCallback(() => {
    if (selectedStatuses.length === 0) return "All";
    if (selectedStatuses.length === 1) return selectedStatuses[0];
    return `${selectedStatuses.length} selected`;
  }, [selectedStatuses]);

  const getDepartmentDisplayText = useCallback(() => {
    if (selectedDepartments.length === 0) return "All";
    if (selectedDepartments.length === 1) {
      const dept = departmentsList.find((d) => d.id === selectedDepartments[0]);
      return dept ? dept.name : "Unknown";
    }
    return `${selectedDepartments.length} selected`;
  }, [selectedDepartments, departmentsList]);

  const getEmploymentTypeDisplayText = useCallback(() => {
    if (selectedEmploymentTypes.length === 0) return "All";
    if (selectedEmploymentTypes.length === 1) return selectedEmploymentTypes[0];
    return `${selectedEmploymentTypes.length} selected`;
  }, [selectedEmploymentTypes]);

  const employeeListFilterBtnClass = darkMode
    ? "appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer min-w-[7.5rem]"
    : "appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium bg-white border-gray-300 text-gray-900 focus:border-redhalo-red transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer min-w-[7.5rem]";

  const employeeListFilterMenuClass = darkMode
    ? "absolute z-[9999] mt-1 min-w-[12rem] max-h-64 overflow-y-auto rounded-none shadow-lg border right-0 bg-[#2C2C2C] border-[#3C3C3C]"
    : "absolute z-[9999] mt-1 min-w-[12rem] max-h-64 overflow-y-auto rounded-none shadow-lg border right-0 bg-white border-gray-200";

  const employeeListFilterClearRowClass = darkMode
    ? "w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[#3C3C3C] disabled:opacity-40 disabled:cursor-default"
    : "w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b hover:bg-gray-100 text-red-600 hover:text-red-700 border-gray-200 disabled:opacity-40 disabled:cursor-default";

  const employeeListFilterOptionClass = darkMode
    ? "flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-[#3C3C3C] text-[#E0E0E0]"
    : "flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 text-gray-800";

  const employeeListFilterCheckboxClass = darkMode
    ? "w-4 h-4 mr-2 rounded-none border-2 bg-[#2C2C2C] border-[#3C3C3C] text-redhalo-red focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-0 cursor-pointer"
    : "w-4 h-4 mr-2 rounded-none border-2 bg-white border-gray-300 text-redhalo-navy focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-0 cursor-pointer";

  const handleSubmit = async (updatedEmployeeData) => {
    try {
      // Normalize dates in the payload first
      const payload = normalizeDatesForPayload(updatedEmployeeData);

      if (selectedEmployee && selectedEmployee.id) {
        // Update existing employee
        await employeeService.updateEmployee(selectedEmployee.id, payload);

        setEmployees((prevEmployees) =>
          prevEmployees.map((emp) =>
            emp.id === selectedEmployee.id ? { ...emp, ...payload } : emp
          )
        );
      } else {
        // Create new employee
        const response = await employeeService.createEmployee(payload);
        // API returns created employee under response.data (your code did similar)
        if (response && response.data) {
          setEmployees((prevEmployees) => [...prevEmployees, response.data]);
        } else if (response && response.employee) {
          setEmployees((prevEmployees) => [
            ...prevEmployees,
            response.employee,
          ]);
        }
      }

      setSelectedEmployee(null);
      setShowForm(false);
      setError(null);
      
      // Refresh counts after create/update
      fetchEmployeeCounts();
    } catch (err) {
      console.error("handleSubmit error:", err);
      setError(err.message || "Failed to save employee");
    }
  };

  const handleDelete = async (id) => {
    try {
      await employeeService.deleteEmployee(id);

      setEmployees((prevEmployees) =>
        prevEmployees.filter((emp) => emp.id !== id)
      );

      setSelectedEmployee(null);

      setShowForm(false);

      setError(null);
      
      // Refresh counts after delete
      fetchEmployeeCounts();
    } catch (err) {
      setError(err.message || "Failed to delete employee");
    }
  };

  const withTitleCaseName = useCallback((emp) => {
    if (!emp?.user) return emp;
    return {
      ...emp,
      user: { ...emp.user, name: toTitleCase(emp.user.name || "") },
    };
  }, []);

  const handleView = useCallback(async (employee) => {
    try {
      setLoading(true);
      const response = await employeeService.getEmployee(employee.id);
      const data = response?.data ?? response;
      const full = typeof data?.id !== "undefined" ? data : employee;
      setSelectedEmployee(withTitleCaseName(full));
      setShowForm(true);
    } catch (e) {
      console.error("Error fetching employee for view:", e);
      setSelectedEmployee(withTitleCaseName(employee));
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, [withTitleCaseName]);

  const handleEdit = useCallback(async (employee) => {
    try {
      setLoading(true);

      const response = await employeeService.getEmployee(employee.id);
      const data = response?.data ?? response;
      const fullEmployee = typeof data?.id !== "undefined" ? data : employee;

      if (response?.success !== false) {
        setSelectedEmployee(withTitleCaseName(fullEmployee));
        setShowForm(true);
      } else {
        setSelectedEmployee(withTitleCaseName(employee));
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setSelectedEmployee(withTitleCaseName(employee));
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, [withTitleCaseName]);

  const handleOpenAddEmployeeForm = useCallback(() => {
    setActiveEmployeeTab("employees");
    setEmployeesMenuOpen(true);
    setImportMenuOpen(false);
    setReportsMenuOpen(false);
    setExportMenuOpen(false);
    setSelectedEmployee(null);
    setIsRejoin(false);
    setRejoinSelectedEmployeeId(null);
    setRejoinPrefillData(null);
    setRejoinEmployeeList([]);
    setShowForm(true);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "";
    }
  };

  // Helper function to trim all string values in an object recursively
  const trimObjectValues = (obj) => {
    if (typeof obj === "string") {
      return obj.trim();
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => trimObjectValues(item));
    }
    if (obj !== null && typeof obj === "object") {
      const trimmed = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          trimmed[key] = trimObjectValues(obj[key]);
        }
      }
      return trimmed;
    }
    return obj;
  };

  // Enhanced import function with comprehensive validation and error reporting
  // Replace your current handleImport function with this
  // In Employee.jsx
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Read and parse the Excel file to trim whitespace
      const fileReader = new FileReader();
      fileReader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];

          // Parse with header normalization
          let parsedData = XLSX.utils.sheet_to_json(worksheet);

          console.log("===== IMPORT DEBUG LOGS =====");
          console.log("1. ORIGINAL PARSED DATA FROM EXCEL:");
          console.log("Total rows:", parsedData.length);
          console.log("First row (raw):", parsedData[0]);
          console.log("Column headers:", Object.keys(parsedData[0] || {}));

          // Trim all string values in the data
          parsedData = parsedData.map((row) => trimObjectValues(row));

          // Comprehensive field sanitization - clean all fields before validation
          // This prevents issues like "70368 64318" (mobile), "M. Radhika" (name), etc. being imported incorrectly
          parsedData = parsedData.map((row) => {
            const sanitizedRow = { ...row };
            
            // Sanitize Mobile Number - remove spaces, dashes, and other non-digit characters
            const mobileKey = Object.keys(sanitizedRow).find(
              (key) => key === "Mobile Number*" || key === "Mobile Number"
            );
            if (mobileKey && sanitizedRow[mobileKey]) {
              sanitizedRow[mobileKey] = String(sanitizedRow[mobileKey]).replace(/\D/g, "");
            }

            // Sanitize Employee Code - trim whitespace
            const empCodeKey = Object.keys(sanitizedRow).find(
              (key) => key === "Employee Code*" || key === "Employee Code"
            );
            if (empCodeKey && sanitizedRow[empCodeKey]) {
              sanitizedRow[empCodeKey] = String(sanitizedRow[empCodeKey]).trim();
            }

            // Sanitize Name - trim whitespace (validation will catch invalid characters)
            const nameKey = Object.keys(sanitizedRow).find(
              (key) => key === "Name*" || key === "Name"
            );
            if (nameKey && sanitizedRow[nameKey]) {
              sanitizedRow[nameKey] = String(sanitizedRow[nameKey]).trim();
            }

            // Sanitize Aadhaar Number - remove spaces
            const aadhaarKey = Object.keys(sanitizedRow).find(
              (key) => key === "Aadhaar Number*" || key === "Aadhaar Number"
            );
            if (aadhaarKey && sanitizedRow[aadhaarKey]) {
              sanitizedRow[aadhaarKey] = String(sanitizedRow[aadhaarKey]).replace(/\s/g, "");
            }

            // Helper to check if value is "Not Available" (case-insensitive)
            // ✅ Match backend logic exactly for consistency
            const isNotAvailable = (val) => {
              if (!val) return false;
              const normalized = String(val).trim().toLowerCase();
              return (
                normalized === "not available" ||
                normalized === "notavailable" ||
                normalized === "n/a" ||
                normalized === "na"
              );
            };

            // Sanitize PAN Number — canonical sentinels PANNOTAVBL / PANAPPLIED
            const panKey = Object.keys(sanitizedRow).find(
              (key) => key === "PAN Number*" || key === "PAN Number"
            );
            if (panKey && sanitizedRow[panKey]) {
              const panRaw = String(sanitizedRow[panKey]).trim();
              const panCompact = panRaw.replace(/\s+/g, "").toUpperCase();
              const isPanAppliedVal =
                /^pan\s*applied$/i.test(panRaw) || panCompact === "PANAPPLIED";
              if (isPanAppliedVal) {
                sanitizedRow[panKey] = "PANAPPLIED";
              } else if (isNotAvailable(panRaw) || panCompact === "PANNOTAVBL") {
                sanitizedRow[panKey] = "PANNOTAVBL";
              } else {
                sanitizedRow[panKey] = panRaw.replace(/\s/g, "").toUpperCase();
              }
            }

            // Sanitize Personal Email - handle "Not Available" before converting to lowercase
            const personalEmailKey = Object.keys(sanitizedRow).find(
              (key) => key === "Personal Email*" || key === "Personal Email"
            );
            if (personalEmailKey && sanitizedRow[personalEmailKey]) {
              const personalEmailRaw = String(sanitizedRow[personalEmailKey]).trim();
              if (isNotAvailable(personalEmailRaw)) {
                sanitizedRow[personalEmailKey] = "not available";
              } else {
                sanitizedRow[personalEmailKey] = personalEmailRaw.toLowerCase();
              }
            }

            // Sanitize Official Email - handle "Not Available" before converting to lowercase
            const officialEmailKey = Object.keys(sanitizedRow).find(
              (key) => key === "Official Email*" || key === "Official Email"
            );
            if (officialEmailKey && sanitizedRow[officialEmailKey]) {
              const officialEmailRaw = String(sanitizedRow[officialEmailKey]).trim();
              if (isNotAvailable(officialEmailRaw)) {
                sanitizedRow[officialEmailKey] = "not available";
              } else {
                sanitizedRow[officialEmailKey] = officialEmailRaw.toLowerCase();
              }
            }

            // Sanitize Gender - capitalize first letter
            const genderKey = Object.keys(sanitizedRow).find(
              (key) => key === "Gender*" || key === "Gender"
            );
            if (genderKey && sanitizedRow[genderKey]) {
              const gender = String(sanitizedRow[genderKey]).trim();
              sanitizedRow[genderKey] = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
            }

            return sanitizedRow;
          });

          // AUTO-GENERATE Employee Code if missing
          // Get the count of rows that need auto-generated codes
          const rowsNeedingCodes = parsedData.filter((row) => {
            const empCodeKey = Object.keys(row).find(
              (key) => key === "Employee Code*" || key === "Employee Code"
            );
            return (
              empCodeKey && (!row[empCodeKey] || row[empCodeKey].trim() === "")
            );
          }).length;

          // Generate all needed codes at once
          let generatedCodes = [];
          if (rowsNeedingCodes > 0) {
            try {
              console.log(
                `[Auto-generate] Need to generate ${rowsNeedingCodes} employee codes`
              );
              for (let i = 0; i < rowsNeedingCodes; i++) {
                const response =
                  await employeeCodeStructureService.getNextEmployeeCode();
                if (response.data && response.data.nextCode) {
                  generatedCodes.push(response.data.nextCode);
                  console.log(
                    `[Generated code ${i + 1}/${rowsNeedingCodes}]:`,
                    response.data.nextCode
                  );
                }
              }
            } catch (error) {
              console.error("[Auto-generate] Error generating codes:", error);
              toast.error(
                "Failed to auto-generate employee codes. Please check Employee Code Structure settings."
              );
              setImporting(false);
              return;
            }
          }

          // Apply generated codes to rows that need them
          let codeIndex = 0;
          parsedData = parsedData.map((row, index) => {
            const empCodeKey = Object.keys(row).find(
              (key) => key === "Employee Code*" || key === "Employee Code"
            );

            if (
              empCodeKey &&
              (!row[empCodeKey] || row[empCodeKey].trim() === "")
            ) {
              if (codeIndex < generatedCodes.length) {
                row[empCodeKey] = generatedCodes[codeIndex];
                console.log(
                  `[Applied code to row ${index + 2}]:`,
                  row[empCodeKey]
                );
                codeIndex++;
              }
            }
            return row;
          });

          console.log("2. TRIMMED DATA:");
          console.log("First row (after trim):", parsedData[0]);
          parsedData.forEach((row, index) => {
            const empCodeKey = Object.keys(row).find(
              (key) => key === "Employee Code*" || key === "Employee Code"
            );
            if (row[empCodeKey] || row["Branch"] || row["Designation"]) {
              console.log(`Row ${index + 2}:`, {
                "Employee Code": `"${row[empCodeKey] || ""}"`,
                Branch: `"${row["Branch"]}"`,
                Designation: `"${row["Designation"]}"`,
                Department: `"${row["Department"]}"`,
                "Sub Department": `"${row["Sub Department"]}"`,
                Grade: `"${row["Grade"]}"`,
                Category: `"${row["Category"]}"`,
              });
            }
          });

          // Frontend validation using comprehensive validator (catches errors before backend)
          toast.loading("Validating employee data...");
          
          try {
            // Create a temporary file for frontend validation
            const tempWorksheet = XLSX.utils.json_to_sheet(parsedData);
            const tempWorkbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(tempWorkbook, tempWorksheet, worksheetName);
            const tempBuffer = XLSX.write(tempWorkbook, { bookType: "xlsx", type: "array" });
            const tempFile = new File([tempBuffer], file.name, {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            // Run frontend validation
            const frontendValidation = await validateEmployeeImportFile(tempFile);
            
            if (!frontendValidation.success) {
              toast.dismiss();
              toast.error(frontendValidation.message || "Validation failed");
              return;
            }

            // Use frontend validation results if there are invalid records
            if (frontendValidation.data.invalidRecords.length > 0) {
              toast.dismiss();
              setImportSummary({
                valid: frontendValidation.data.validRecords || [],
                invalid: frontendValidation.data.invalidRecords || [],
                validCount: frontendValidation.data.validCount || 0,
                invalidCount: frontendValidation.data.invalidCount || 0,
              });
              setShowImportModal(false);
              setShowImportSummaryModal(true);
              toast.error(
                `Found ${frontendValidation.data.invalidCount} invalid records. Please fix them and try again.`
              );
              e.target.value = null;
              return;
            }

            // If all records are valid in frontend validation, proceed with backend validation
            // (backend may have additional business logic checks)
            console.log("Frontend validation passed. Proceeding with backend validation...");
          } catch (validationError) {
            console.error("Frontend validation error:", validationError);
            toast.dismiss();
            toast.error("Error during validation: " + (validationError.message || "Unknown error"));
            e.target.value = null;
            return;
          }

          // Create FormData with trimmed and validated data
          const formData = new FormData();

          // Convert the trimmed data back to Excel format for backend validation
          const trimmedWorksheet = XLSX.utils.json_to_sheet(parsedData);
          const trimmedWorkbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(
            trimmedWorkbook,
            trimmedWorksheet,
            worksheetName
          );

          // Write to blob and append to FormData
          const excelBuffer = XLSX.write(trimmedWorkbook, {
            bookType: "xlsx",
            type: "array",
          });
          const trimmedFile = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          formData.append("file", trimmedFile, file.name);

          console.log("3. SENDING TO BACKEND:");
          console.log("File name:", file.name);
          console.log("FormData entries:");
          for (let [key, value] of formData.entries()) {
            console.log(
              `  ${key}:`,
              value instanceof File ? `File: ${value.name}` : value
            );
          }

          const response = await employeeService.validateEmployeeImport(
            formData
          );
          toast.dismiss();

          console.log("4. BACKEND RESPONSE:");
          console.log("Full response:", response);
          console.log(
            "Valid Records:",
            response.data?.validRecords?.length || 0
          );
          console.log(
            "Invalid Records:",
            response.data?.invalidRecords?.length || 0
          );

          if (
            response.data?.invalidRecords &&
            response.data.invalidRecords.length > 0
          ) {
            console.log("FIRST INVALID RECORD DETAILS:");
            const firstInvalid = response.data.invalidRecords[0];
            console.log("Row:", firstInvalid);
            console.log("Errors:", firstInvalid.errors);
          }
          console.log("============================");

          // Check if backend returned a stub response
          if (
            response.message === "Validation successful (Stub)" ||
            response.message?.includes("Stub")
          ) {
            toast.error(
              "Backend validation is not fully implemented. The server returned a stub response. Please contact your administrator to implement the validation endpoint."
            );
            return;
          }

          // Expected response: { validRecords: [...], invalidRecords: [...] }
          // Also handle response.data being the array itself
          const validRecords =
            response.data?.validRecords ||
            response.validRecords ||
            response.data ||
            [];
          const invalidRecords =
            response.data?.invalidRecords || response.invalidRecords || [];

          if (Array.isArray(validRecords) && validRecords.length > 0) {
            setImportSummary({
              valid: validRecords,
              invalid: invalidRecords,
              validCount: validRecords.length,
              invalidCount: invalidRecords.length,
            });
            setShowImportModal(false);
            setShowImportSummaryModal(true);
            toast.success(
              `Found ${validRecords.length} valid records to import`
            );
          } else if (
            Array.isArray(invalidRecords) &&
            invalidRecords.length > 0
          ) {
            toast.error(
              `Found ${invalidRecords.length} invalid records. Please fix them and try again.`
            );
            setImportSummary({
              valid: [],
              invalid: invalidRecords,
              validCount: 0,
              invalidCount: invalidRecords.length,
            });
            setShowImportModal(false);
            setShowImportSummaryModal(true);
          } else {
            toast.error(
              response.message ||
                "Validation failed: No valid records found or unexpected response format."
            );
            console.error("Validation response error:", response);
          }
          e.target.value = null;
        } catch (error) {
          toast.dismiss();
          console.error("Import validation error:", error);
          toast.error(error.message || "Failed to validate employee file");
        }
      };

      fileReader.readAsArrayBuffer(file);
    } catch (error) {
      toast.dismiss();
      console.error("Import file reading error:", error);
      toast.error("Failed to read the selected file");
    }
  };

  // State for import confirmation and results
  //const [showImportConfirmationModal, setShowImportConfirmationModal] =
  //useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  //const [showImportResultsModal, setShowImportResultsModal] = useState(false);

  // Debug useEffect removed to prevent React rendering issues
  // Add this new function inside your Employee component
  // Employee.jsx
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Save the selected file to state
    setSelectedImportFile(file);

    // Show the confirmation modal
    setShowImportConfirmationModal(true);

    // Immediately hide the main "Import Employee Data" modal
    setShowImportModal(false);
  };
  const handleConfirmImport = async () => {
    if (!importSummary?.valid?.length && !importSummary?.invalid?.length) {
      toast.error("No records to process.");
      return;
    }

    try {
      setImporting(true);
      setShowImportSummaryModal(false); // Close the summary modal

      let successfulCount = 0;
      let finalSuccessfulRecords = [];
      let pushFailed = [];

      // Batch import with progress and ETA
      // Comprehensive field sanitization before import (defense in depth)
      const validRecords = (Array.isArray(importSummary.valid)
        ? importSummary.valid
        : []
      ).map((record) => {
        const sanitizedRecord = { ...record };
        
        // Sanitize Mobile Number - remove spaces, dashes, and other non-digit characters
        const mobileKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Mobile Number*" || key === "Mobile Number"
        );
        if (mobileKey && sanitizedRecord[mobileKey]) {
          sanitizedRecord[mobileKey] = String(sanitizedRecord[mobileKey]).replace(/\D/g, "");
        }

        // Sanitize Employee Code - trim whitespace
        const empCodeKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Employee Code*" || key === "Employee Code"
        );
        if (empCodeKey && sanitizedRecord[empCodeKey]) {
          sanitizedRecord[empCodeKey] = String(sanitizedRecord[empCodeKey]).trim();
        }

        // Sanitize Name - trim whitespace
        const nameKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Name*" || key === "Name"
        );
        if (nameKey && sanitizedRecord[nameKey]) {
          sanitizedRecord[nameKey] = String(sanitizedRecord[nameKey]).trim();
        }

        // Sanitize Aadhaar Number - remove spaces
        const aadhaarKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Aadhaar Number*" || key === "Aadhaar Number"
        );
        if (aadhaarKey && sanitizedRecord[aadhaarKey]) {
          sanitizedRecord[aadhaarKey] = String(sanitizedRecord[aadhaarKey]).replace(/\s/g, "");
        }

        // Sanitize PAN Number - remove spaces and convert to uppercase
        const panKey = Object.keys(sanitizedRecord).find(
          (key) => key === "PAN Number*" || key === "PAN Number"
        );
        if (panKey && sanitizedRecord[panKey]) {
          sanitizedRecord[panKey] = String(sanitizedRecord[panKey]).replace(/\s/g, "").toUpperCase();
        }

        // Sanitize Personal Email - trim and lowercase
        const personalEmailKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Personal Email*" || key === "Personal Email"
        );
        if (personalEmailKey && sanitizedRecord[personalEmailKey]) {
          sanitizedRecord[personalEmailKey] = String(sanitizedRecord[personalEmailKey]).trim().toLowerCase();
        }

        // Sanitize Official Email - trim and lowercase
        const officialEmailKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Official Email*" || key === "Official Email"
        );
        if (officialEmailKey && sanitizedRecord[officialEmailKey]) {
          sanitizedRecord[officialEmailKey] = String(sanitizedRecord[officialEmailKey]).trim().toLowerCase();
        }

        // Sanitize Gender - capitalize first letter
        const genderKey = Object.keys(sanitizedRecord).find(
          (key) => key === "Gender*" || key === "Gender"
        );
        if (genderKey && sanitizedRecord[genderKey]) {
          const gender = String(sanitizedRecord[genderKey]).trim();
          sanitizedRecord[genderKey] = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
        }

        return sanitizedRecord;
      });
      const total = validRecords.length;
      const batchSize = 100; // smaller batches to surface progress earlier
      const batches = [];
      for (let i = 0; i < total; i += batchSize) {
        batches.push(validRecords.slice(i, i + batchSize));
      }

      const startTs = Date.now();
      setImportProgress({ total, processed: 0, startTs, eta: "--:--" });

      if (total > 0) {
        // helper to update progress incrementally based on decided records
        const updateProgress = () => {
          let decided = successfulCount + pushFailed.length; // records that are either saved or marked failed
          if (decided === 0) decided = 1; // ensure early visible movement
          const elapsedSec = Math.max(
            1,
            Math.floor((Date.now() - startTs) / 1000)
          );
          const rate = decided / elapsedSec;
          const remaining = Math.max(0, total - decided);
          const etaSec = rate > 0 ? Math.ceil(remaining / rate) : 0;
          const mm = String(Math.floor(etaSec / 60)).padStart(2, "0");
          const ss = String(etaSec % 60).padStart(2, "0");
          setImportProgress({
            total,
            processed: decided,
            startTs,
            eta: `${mm}:${ss}`,
            successful: successfulCount,
            failed: pushFailed.length,
          });
        };

        const importChunk = async (chunk) => {
          let resp = null;
          try {
            resp = await employeeService.importConfirmedEmployees(chunk);
          } catch (e) {
            const serverMsg = e?.response?.data?.message || e?.message;
            resp = { success: false, message: serverMsg };
          }
          if (resp && resp.success) {
            const count = Array.isArray(resp.data)
              ? resp.data.length
              : chunk.length;
            successfulCount += count;
            if (Array.isArray(resp.data)) {
              // Debug log to see what department data we're receiving
              console.log("[FRONTEND] Import response data:", resp.data);
              if (resp.data.length > 0) {
                console.log("[FRONTEND] First employee data:", resp.data[0]);
                console.log("[FRONTEND] Department properties:", {
                  department: resp.data[0].department,
                  departmentName: resp.data[0].departmentName,
                  departmentObj: resp.data[0].departmentObj,
                  Department: resp.data[0].Department,
                });
              }
              finalSuccessfulRecords = finalSuccessfulRecords.concat(resp.data);
            }
            // If backend returns failedRecords in this mode, collect them
            if (resp.failedRecords && Array.isArray(resp.failedRecords)) {
              pushFailed = pushFailed.concat(resp.failedRecords);
            }
            updateProgress();
            return;
          }
          // split recursively to isolate bad records
          if (chunk.length <= 1) {
            pushFailed = pushFailed.concat(
              chunk.map((r) => ({
                row: r.__row || null,
                error: (resp && resp.message) || "Import failed",
                errorType: "server_error",
                fieldErrors: {},
                record: r,
              }))
            );
            updateProgress();
            return;
          }
          const mid = Math.floor(chunk.length / 2);
          await importChunk(chunk.slice(0, mid));
          await importChunk(chunk.slice(mid));
        };

        for (let i = 0; i < batches.length; i++) {
          await importChunk(batches[i]);

          // Also update progress at batch boundary to reflect any pending UI
          updateProgress();

          if ((i + 1) % 5 === 0 || i === batches.length - 1) {
            const decidedNow = successfulCount + pushFailed.length;
            toast.info(`Processed ${decidedNow} of ${total} records...`, {
              autoClose: 1000,
            });
          }
          await new Promise((r) => setTimeout(r, 80));
        }
      } else {
        toast.info(
          "No valid records to import. Showing failed records report."
        );
      }

      // Prepare data for the detailed results modal
      setImportResults({
        success: successfulCount > 0,
        summary: {
          total: importSummary.validCount + importSummary.invalidCount,
          successful: successfulCount,
          failed:
            importSummary.invalidCount +
            (importSummary.validCount - successfulCount),
        },
        successfulRecords: finalSuccessfulRecords,
        // *** THE FIX: Pass the invalid records from the summary step ***
        failedRecords: [...(importSummary.invalid || []), ...pushFailed],
      });

      setShowImportResultsModal(true); // Show the detailed results modal

      if (successfulCount > 0) {
        fetchEmployees(); // Refresh the main employee list if anything was imported
        fetchEmployeeCounts(); // Refresh counts after import
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.message || "An unexpected error occurred during import."
      );
      // For confirmImport (validation step), fall back to showing validation results if available
      if (importSummary) {
        setImportResults({
          success: false,
          summary: {
            total: importSummary.validCount + importSummary.invalidCount,
            successful: importSummary.validCount,
            failed: importSummary.invalidCount,
          },
          successfulRecords: (importSummary.valid || []).map((r) => ({
            empCode: r["Employee Code*"],
            user: { name: r["Name*"] },
            email: r["Official Email*"] || r["Personal Email*"] || "",
            department: r["Department*"],
          })),
          failedRecords: importSummary.invalid || [],
        });
        setShowImportResultsModal(true);
      }
    } finally {
      setImporting(false);
      setImportSummary(null); // Clean up the initial summary data
    }
  };

  // Employee.jsx

  const handleShowDetailedReportWithoutImport = () => {
    if (!importSummary) return;

    // Close the first simple summary modal
    setShowImportSummaryModal(false);

    // Prepare the data for the detailed report modal using the validation results
    setImportResults({
      success: false, // False because no import was actually performed
      summary: {
        total: importSummary.validCount + importSummary.invalidCount,
        successful: importSummary.validCount, // Show the records that *would* have been successful
        failed: importSummary.invalidCount,
      },
      // Map the valid records to the structure the results table expects
      successfulRecords: importSummary.valid.map((record) => ({
        empCode: record["Employee Code*"],
        user: { name: record["Name*"] },
        email: record["Official Email*"],
        department: record["Department*"], // Use the name directly
      })),
      // The invalid records are already in the correct format
      failedRecords: importSummary.invalid,
    });

    // Show the detailed report modal
    setShowImportResultsModal(true);
  };
  // Confirm import function
  // This is the correct function for the "Confirm Import" button
  // Employee.jsx - Replace the confirmImport function entirely with this

  // Employee.jsx - Replace the confirmImport function entirely with this

  // Employee.jsx - Replace the confirmImport function entirely with this

  const confirmImport = async () => {
    if (!selectedImportFile) {
      toast.error("No file selected for import.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedImportFile);

    // Always close the confirmation modal first
    setShowImportConfirmationModal(false);
    setImporting(true);
    const loadingToastId = toast.loading("Validating file, please wait...");

    console.log("[confirmImport] Starting validation..."); // Log 1
    console.log("[confirmImport] Selected file:", selectedImportFile.name);

    try {
      // Call the VALIDATION API
      const response = await employeeService.validateEmployeeImport(formData);
      toast.dismiss(loadingToastId);
      console.log("[confirmImport] Validation API Response:", response); // Log 2

      const data = response?.data;
      const validCount = data?.validCount ?? 0;
      const invalidCount = data?.invalidCount ?? 0;
      const totalRecords = data?.totalRecords ?? validCount + invalidCount;
      const validRecords = data?.validRecords || [];
      const invalidRecords = data?.invalidRecords || [];

      console.log(
        `[confirmImport] Counts - Valid: ${validCount}, Invalid: ${invalidCount}, Total: ${totalRecords}`
      ); // Log 3

      // LOG DETAILED INVALID RECORDS
      if (invalidRecords && invalidRecords.length > 0) {
        console.log("========== INVALID RECORDS DETAILS ==========");
        console.log("Total invalid records:", invalidRecords.length);
        invalidRecords.forEach((record, index) => {
          console.log(`\n--- Invalid Record #${index + 1} ---`);
          console.log("Row number:", record.row || "N/A");

          // Log the actual record data from Excel
          if (record.record) {
            console.log("RECORD DATA FROM EXCEL:");
            console.log(
              "  Employee Code:",
              record.record["Employee Code"] ||
                record.record.employeeCode ||
                "❌ MISSING/NULL - THIS IS REQUIRED!"
            );
            console.log(
              "  Name:",
              record.record.Name || record.record.name || "N/A"
            );
            console.log(
              "  Email:",
              record.record["Official Email"] || record.record.email || "N/A"
            );
            console.log("  Branch:", record.record.Branch || "N/A");
            console.log("  Designation:", record.record.Designation || "N/A");
            console.log("  Department:", record.record.Department || "N/A");
            console.log(
              "  Sub Department:",
              record.record["Sub Department"] || "N/A"
            );
            console.log("  Grade:", record.record.Grade || "N/A");
            console.log("  Category:", record.record.Category || "N/A");
          }

          // Log field errors
          if (record.fieldErrors) {
            console.log("\nFIELD VALIDATION ERRORS:");
            Object.entries(record.fieldErrors).forEach(([field, error]) => {
              console.log(`  ❌ ${field}: ${error}`);
            });
          }

          // Log general error
          if (record.error) {
            console.log("\nGeneral Error:", record.error);
          }
        });
        console.log("==========================================");

        // DIAGNOSE THE ROOT CAUSE
        console.log("\n🔍 ROOT CAUSE ANALYSIS:");

        // Check if errors mention "not a valid" for dropdown fields
        const dropdownFields = [
          "Branch",
          "Designation",
          "Department",
          "Sub Department",
          "Grade",
          "Category",
        ];
        let hasDropdownErrors = false;
        let hasEmptyOptionsError = false;

        if (invalidRecords[0] && invalidRecords[0].fieldErrors) {
          Object.entries(invalidRecords[0].fieldErrors).forEach(
            ([field, error]) => {
              if (
                dropdownFields.includes(field) &&
                error.includes("is not a valid")
              ) {
                hasDropdownErrors = true;
                if (error.includes('Available options: "')) {
                  hasEmptyOptionsError = true;
                }
              }
            }
          );
        }

        if (hasEmptyOptionsError) {
          console.log(
            "\n⚠️  DETECTED: Master data is MISSING or EMPTY in the system!"
          );
          console.log("The backend validation shows:");
          console.log("  'is not a valid Branch. Available options: ' (EMPTY)");
          console.log(
            "\nThis means NO Branches exist in your system for this company."
          );
          console.log("\n✅ FIX STEPS:");
          console.log("1. Go to Admin → Masters");
          console.log("2. Add the following master data:");
          console.log("   - Branches: Bengaluru");
          console.log(
            "   - Designations: Recruiter (note: check spelling in your Excel)"
          );
          console.log("   - Departments: Accounts & Finance");
          console.log("   - Sub Departments: Goa");
          console.log("   - Grades: A");
          console.log("   - Categories: Unskilled");
          console.log("3. Return to import and try again");
        } else if (hasDropdownErrors) {
          console.log(
            "\n⚠️  DETECTED: One or more master data values are not in the system."
          );
          console.log(
            "Check the values in your Excel against what's in Admin → Masters"
          );
        } else {
          console.log(
            "1. ❌ Employee Code is NULL or MISSING - This field is REQUIRED!"
          );
          console.log(
            "2. Fill in all required fields marked with * in your Excel template"
          );
          console.log(
            "3. Verify all values match the system's master data exactly"
          );
        }
        console.log("==========================================\n");
      }
      // --- DECISION LOGIC ---
      if (validCount === 0 && invalidCount > 0) {
        // *** CASE: ZERO valid, INVALID exist -> Show Results Modal DIRECTLY ***
        console.log(
          "[confirmImport] Condition Met: Zero valid, invalid exist. Preparing Results Modal..."
        ); // Log 4
        toast("Validation complete. No valid records found.");
        const resultsData = {
          success: false, // Indicate no import happened yet
          summary: {
            total: totalRecords,
            successful: 0,
            failed: invalidCount,
          },
          successfulRecords: [],
          failedRecords: invalidRecords,
        };
        console.log("[confirmImport] Setting Import Results:", resultsData); // Log 5
        setImportResults(resultsData);
        console.log("[confirmImport] Showing Import Results Modal"); // Log 6
        setShowImportResultsModal(true); // <-- This should show the results modal
      } else if (validCount > 0) {
        // *** CASE: VALID records exist -> Show Summary Modal (Yes/No) ***
        console.log(
          "[confirmImport] Condition Met: Valid records exist. Showing Summary Modal."
        ); // Log 7
        setImportSummary({
          valid: validRecords,
          invalid: invalidRecords,
          validCount: validCount,
          invalidCount: invalidCount,
        });
        setShowImportSummaryModal(true); // <-- This shows the summary modal
      } else {
        // *** CASE: ZERO valid and ZERO invalid (or other unexpected) -> Show Error Toast ***
        console.log(
          "[confirmImport] Condition Met: Zero valid/invalid or unexpected data. Showing Error Toast."
        ); // Log 8
        toast.error(
          response?.message ||
            data?.message ||
            "No valid records found in the file, or the file was empty."
        );
      }
      // --- END OF DECISION LOGIC ---
    } catch (error) {
      // --- CATCH BLOCK ---
      toast.dismiss(loadingToastId);
      console.error(
        "[confirmImport] Validation API call failed (Catch Block):",
        error.response || error
      ); // Log 9
      console.error(
        "[confirmImport] Backend Error Response Data:",
        error.response?.data
      ); // Log 10

      // Try to extract results data even from error response
      let errorDetails = null;
      const responseData = error.response?.data;
      if (responseData) {
        if (
          responseData.data &&
          (responseData.data.invalidRecords !== undefined ||
            responseData.data.failedRecords !== undefined)
        ) {
          errorDetails = responseData.data;
          if (responseData.data.failedRecords)
            errorDetails.invalidRecords = responseData.data.failedRecords;
        } else if (
          responseData.invalidRecords !== undefined ||
          responseData.failedRecords !== undefined
        ) {
          errorDetails = responseData;
          if (responseData.failedRecords)
            errorDetails.invalidRecords = responseData.failedRecords;
        }
      }

      if (
        errorDetails &&
        errorDetails.invalidRecords &&
        errorDetails.invalidRecords.length > 0
      ) {
        // *** CASE: API Error BUT we found error details -> Show Results Modal DIRECTLY ***
        console.log(
          "[confirmImport] Catch Block: Found details in error. Preparing Results Modal..."
        ); // Log 11
        toast("Validation failed. Showing error report.");
        const invalidRecs = errorDetails.invalidRecords || [];
        const validRecs = errorDetails.validRecords || [];
        const validCnt = errorDetails.validCount ?? validRecs.length;
        const invalidCnt = errorDetails.invalidCount ?? invalidRecs.length;
        const totalRecs = errorDetails.totalRecords || validCnt + invalidCnt;

        const resultsData = {
          success: false,
          summary: {
            total: totalRecs,
            successful: validCnt,
            failed: invalidCnt,
          },
          successfulRecords: validRecs,
          failedRecords: invalidRecs,
        };
        console.log(
          "[confirmImport] Setting Import Results from Catch:",
          resultsData
        ); // Log 12
        setImportResults(resultsData);
        console.log("[confirmImport] Showing Import Results Modal from Catch"); // Log 13
        setShowImportResultsModal(true); // <-- This should show the results modal
      } else {
        // *** CASE: API Error and NO details found -> Show Generic Error Toast ***
        console.log(
          "[confirmImport] Catch Block: No details found. Showing Generic Error Toast."
        ); // Log 14
        toast.error(responseData?.message || "Failed to validate the file.");
      }
      // --- END OF CATCH BLOCK ---
    } finally {
      setImporting(false);
      setSelectedImportFile(null); // Clean up
      console.log("[confirmImport] Function finished."); // Log 15
    }
  };

  const [exporting, setExporting] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    processed: 0,
    startTs: 0,
    eta: "--:--",
  });

  //const [showImportModal, setShowImportModal] = useState(false);

  //const [showExportModal, setShowExportModal] = useState(false);

  //const [showPhotoModal, setShowPhotoModal] = useState(false);

  const [selectedPhotoEmployee, setSelectedPhotoEmployee] = useState(null);

  // Toggle this when you want to disable fetching photo URLs for list/grid display
  // Modal will still fetch on demand when clicked
  const ENABLE_EMPLOYEE_PHOTO_URL_FETCH = false;

  const getEmployeePhotoSrc = useCallback(
    (employee) => {
      const rawPhoto = employee?.user?.photo || employee?.photo || "";
      if (typeof rawPhoto !== "string" || !rawPhoto) return "";
      if (rawPhoto.startsWith("data:")) return rawPhoto;
      if (rawPhoto.startsWith("http://") || rawPhoto.startsWith("https://"))
        return rawPhoto;
      if (!ENABLE_EMPLOYEE_PHOTO_URL_FETCH) return "";
      return employeePhotoUrlCache.get(employee?.id) || "";
    },
    [employeePhotoUrlCache]
  );

  // Separate function for modal photo src that always allows fetching
  const getModalPhotoSrc = useCallback(
    (employee) => {
      const rawPhoto = employee?.user?.photo || employee?.photo || "";
      if (typeof rawPhoto !== "string" || !rawPhoto) return "";
      if (rawPhoto.startsWith("data:")) return rawPhoto;
      if (rawPhoto.startsWith("http://") || rawPhoto.startsWith("https://"))
        return rawPhoto;
      return employeePhotoUrlCache.get(employee?.id) || "";
    },
    [employeePhotoUrlCache]
  );

  useEffect(() => {
    if (!ENABLE_EMPLOYEE_PHOTO_URL_FETCH) return;
    const list = Array.isArray(displayEmployees) ? displayEmployees : [];
    const toFetch = [];

    for (const emp of list) {
      const rawPhoto = emp?.user?.photo || emp?.photo;
      if (!emp?.id) continue;
      if (typeof rawPhoto !== "string" || !rawPhoto) continue;
      if (
        rawPhoto.startsWith("data:") ||
        rawPhoto.startsWith("http://") ||
        rawPhoto.startsWith("https://")
      ) {
        continue;
      }
      if (employeePhotoUrlCache.get(emp.id)) continue;
      toFetch.push(emp.id);
    }

    if (toFetch.length === 0) return;

    let cancelled = false;
    Promise.all(
      toFetch.map((id) =>
        employeeService
          .getEmployeePhotoUrl(id)
          .then((resp) => ({
            id,
            url: resp?.url || resp?.data?.url || "",
          }))
          .catch(() => ({ id, url: "" }))
      )
    ).then((results) => {
      if (cancelled) return;
      setEmployeePhotoUrlCache((prev) => {
        const next = new Map(prev);
        results.forEach((r) => {
          if (r?.id && r?.url) next.set(r.id, r.url);
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [displayEmployees, employeePhotoUrlCache]);

  const [confirmationData, setConfirmationData] = useState({
    isConfirmed: "",

    confirmationDate: "",

    extendProbation: false,

    extendedProbationDate: "",
  });

  //const [showSalaryRevisionModal, setShowSalaryRevisionModal] = useState(false);

  const [salaryRevisionData, setSalaryRevisionData] = useState({
    effectiveDate: "",

    arrearsEffectiveDate: "",

    ctcAmount: "",

    reason: "",

    changeDesignation: false,

    newDesignationId: "",

    designationSearch: "",

    copyEffectiveDate: false,
  });

  // const [showSalaryHistoryModal, setShowSalaryHistoryModal] = useState(false);

  const [salaryHistory, setSalaryHistory] = useState([]);
  const [loadingSalaryHistory, setLoadingSalaryHistory] = useState(false);
  const [latestSalaryEffectiveDate, setLatestSalaryEffectiveDate] =
    useState(null);

  const [transferData, setTransferData] = useState({
    effectiveDate: getTodayDateYYYYMMDD(),
    newBranchId: "",
    newDepartmentId: "",
    newSubDepartmentId: "",
    newDesignationId: "",
    newGradeId: "",
    newCategoryId: "",
    newReportingManagerId: "",
    reason: "",
    remarks: "",
  });

  //const [showTransferHistoryModal, setShowTransferHistoryModal] =
  //useState(false);

  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingTransferHistory, setLoadingTransferHistory] = useState(false);

  //const [showResignationModal, setShowResignationModal] = useState(false);

  // const [showProfileCompletionModal, setShowProfileCompletionModal] =
  // useState(false);

  const [profileCompletionData, setProfileCompletionData] = useState(null);

  const [resignationData, setResignationData] = useState({
    resignationDate: "",
    relievingDate: "",
    reason: "",
    noticePeriodDays: 0,
    noticePeriodWaiverDays: 0,
    noticePeriodToDeduct: 0,
    isRetroResignation: false,
    proofDocument: null,
  });

  // Function to check if any form/modal is open (for reference only)
  // const isAnyFormOpen = () => {
  //   return (
  //     showForm ||
  //     showConfirmationModal ||
  //     showSalaryRevisionModal ||
  //     showSalaryHistoryModal ||
  //     showTransferModal ||
  //     showTransferHistoryModal ||
  //     showResignationModal ||
  //     showViewModal ||
  //     showImportModal ||
  //     showExportModal ||
  //     showPhotoModal ||
  //     showStructuresModal
  //   );
  //};

  const handleExport = async () => {
    if (!canExportEmployeeRecords) {
      toast.error("You do not have permission to export employee records.");
      return;
    }
    try {
      setExporting(true);
      setError(null);

      // Export from backend only — same filters as table, order strictly by DOJ descending (same as list API)
      const exportParams = {
        page: 1,
        limit: 50000,
        search: debouncedSearchTerm,
        sortBy: "dateOfJoin",
        sortOrder: "desc",
        employmentStatus: selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
        department: selectedDepartments.length > 0 ? selectedDepartments.join(",") : undefined,
        employmentType: selectedEmploymentTypes.length > 0 ? selectedEmploymentTypes.join(",") : undefined,
        dateOfJoinFrom: columnFilters.dateOfJoinFrom,
        dateOfJoinTo: columnFilters.dateOfJoinTo,
        dateOfRelievingFrom: columnFilters.dateOfRelievingFrom,
        dateOfRelievingTo: columnFilters.dateOfRelievingTo,
        joiningDate: dateOfJoiningFilter,
        leavingDate: dateOfLeavingFilter,
        ...advancedFiltersParams,
      };

      const response = await employeeService.exportEmployees(exportParams);
      if (!response || !response.data || response.status !== 200) {
        toast.error("Export failed. No file received.");
        setExporting(false);
        return;
      }
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildModuleExcelFilename("Employees_Export");
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Employee data exported successfully!");
      setShowExportModal(false);
    } catch (err) {
      setError(err?.message || "Failed to export employees");
      toast.error(err?.response?.data?.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Helper function to format dates to DD-MM-YYYY
  const formatDateForExport = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "";
    }
  };

  // Helper function to get resignation reason text
  const getResignationReasonText = (reasonCode) => {
    if (!reasonCode) return "";
    // Map reason codes to actual text
    const reasonMap = {
      "Personal Reasons": "Personal Reasons",
      "Better Opportunity": "Better Opportunity",
      "Career Growth": "Career Growth",
      "Health Issues": "Health Issues",
      "Family Commitments": "Family Commitments",
      Relocation: "Relocation",
      "Work Environment": "Work Environment",
      "Salary Issues": "Salary Issues",
      Other: "Other",
    };
    return reasonMap[reasonCode] || reasonCode;
  };

  // Section-wise export functions

  // Download error report for failed import records
  // Replace your existing function with this corrected version
  const downloadErrorReport = (failedRecords) => {
    if (!failedRecords || failedRecords.length === 0) {
      toast.error("No failed records to export.");
      return;
    }

    // This creates the report with ONLY the columns you asked for.
    const errorData = failedRecords.map((record, idx) => {
      // Get the employee code from record (could be from record.record or generated)
      const empCode =
        record.record?.["Employee Code*"] ||
        record.record?.["Employee Code"] ||
        record.record?.empCode ||
        "N/A";

      // Get employee name from record
      const empName =
        record.record?.["Name*"] ||
        record.record?.Name ||
        record.record?.name ||
        "Unknown";

      // Get official email from record
      const officialEmail =
        record.record?.["Official Email*"] ||
        record.record?.["Official Email"] ||
        record.record?.email ||
        "N/A";

      // Get mobile number from record
      const mobileNumber =
        record.record?.["Mobile Number*"] ||
        record.record?.["Mobile Number"] ||
        record.record?.mobile ||
        "N/A";

      // Get PAN number from record
      const panNumber =
        record.record?.["PAN Number*"] ||
        record.record?.["PAN Number"] ||
        record.record?.pan ||
        "N/A";

      // Format field errors - if fieldErrors object exists, join all errors
      let errorMessage = "Unknown validation error";
      if (record.fieldErrors && typeof record.fieldErrors === "object") {
        const fieldErrorMessages = Object.entries(record.fieldErrors)
          .map(([field, error]) => `${field}: ${error}`)
          .join("\n");
        errorMessage = fieldErrorMessages || "Validation failed";
      } else if (Array.isArray(record.errors)) {
        errorMessage = record.errors.join("; ");
      } else if (record.error) {
        errorMessage = record.error;
      }

      return {
        "Sl No": idx + 1,
        Row: record.row || "N/A",
        "Employee Code": empCode,
        "Employee Name": empName,
        "Official Email": officialEmail,
        "Mobile Number": mobileNumber,
        "PAN Number": panNumber,
        "Error Type": record.errorType || "Validation",
        "Error Message": errorMessage,
        Suggestions: "",
      };
    });

    // Generate the Excel file from this specific data.
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(errorData);

    // Auto-fit columns for better readability.
    const colWidths = Object.keys(errorData[0] || {}).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...errorData.map((row) => String(row[key] || "").length)
      );
      if (key === "Error Message" || key === "Suggestions") {
        return { wch: Math.min(Math.max(maxLength, 25), 70) };
      }
      return { wch: Math.min(Math.max(maxLength, 12), 40) };
    });
    worksheet["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors To Fix");

    // Generate and download the file.
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = buildModuleExcelFilename("Import_Error_Report");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Error report downloaded successfully.");
  };

  const handleSectionExport = async (section, selectedIds = []) => {
    try {
      setExporting(true);

      setError(null);

      // Same filters and order as table (DOJ desc) when exporting org so Excel order matches
      const exportParams = {
        page: 1,
        limit: 50000,
        search: debouncedSearchTerm,
        sortBy: "dateOfJoin",
        sortOrder: "desc",
        employmentStatus: selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
        department: selectedDepartments.length > 0 ? selectedDepartments.join(",") : undefined,
        employmentType: selectedEmploymentTypes.length > 0 ? selectedEmploymentTypes.join(",") : undefined,
        dateOfJoinFrom: columnFilters.dateOfJoinFrom,
        dateOfJoinTo: columnFilters.dateOfJoinTo,
        dateOfRelievingFrom: columnFilters.dateOfRelievingFrom,
        dateOfRelievingTo: columnFilters.dateOfRelievingTo,
        joiningDate: dateOfJoiningFilter,
        leavingDate: dateOfLeavingFilter,
        ...advancedFiltersParams,
      };

      const sectionsNeedProfile = [
        "personal",
        "family",
        "qualification",
        "educational",
        "professional",
      ];

      const allEmployeesResponse = await employeeService.getEmployees({
        ...exportParams,
        ...(sectionsNeedProfile.includes(section) ? { expandProfile: true } : {}),
      });
      const allEmployees = Array.isArray(allEmployeesResponse.data)
        ? allEmployeesResponse.data
        : Array.isArray(allEmployeesResponse.employees)
        ? allEmployeesResponse.employees
        : [];
      const exportEmployees =
        Array.isArray(selectedIds) && selectedIds.length > 0
          ? allEmployees.filter((emp) => selectedIds.includes(emp.id))
          : allEmployees;

      if (exportEmployees.length === 0) {
        toast.error("No employees found to export");
        setExporting(false);
        return;
      }

      /**
       * @param {Array<object>} exportRows
       * @param {string} sheetName
       * @param {{ mergeGroups?: Array<{ startRow: number, rowCount: number }>, mergeLeadingCols?: number } | null} mergeMeta - vertical merges for grouped section exports
       */
      const buildSheet = (exportRows, sheetName, mergeMeta = null) => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        if (
          mergeMeta &&
          mergeMeta.mergeGroups &&
          mergeMeta.mergeGroups.length > 0 &&
          mergeMeta.mergeLeadingCols
        ) {
          applyGroupedExportMerges(
            worksheet,
            mergeMeta.mergeGroups,
            mergeMeta.mergeLeadingCols,
            1
          );
        }
        const colWidths = Object.keys(exportRows[0] || {}).map((key) => {
          const maxLength = Math.max(
            key.length,
            ...exportRows.map((row) => String(row[key] || "").length)
          );
          return { wch: Math.min(Math.max(maxLength, 10), 50) };
        });
        worksheet["!cols"] = colWidths;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        return buffer;
      };

      // Org details: client-side export with all fields (incl. Confirmation Date, Date of Resignation, Relieving, Reason) in table order
      if (section === "org") {
        const masters = {
          branches: branchesList,
          designations: designationsList,
          departments: departmentsList,
          subDepartments: subDepartmentsList,
          grades: gradesList,
          categories: categoriesList,
          employees: exportEmployees,
          resignationReasons: resignationReasonsList,
          terminationReasons: terminationReasonsList,
        };
        const exportRows = getOrgDetailsExportRows(exportEmployees, masters).map(
          (row, idx) => ({
            "Sl No": idx + 1,
            ...row,
          })
        );
        const buffer = buildSheet(exportRows, "Organization Details");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Org_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Organization Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "personal") {
        const exportRows = getPersonalDetailsExportRows(exportEmployees);
        const buffer = buildSheet(exportRows, "Personal Details");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Personal_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Personal Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "bank") {
        const exportRows = getBankDetailsExportRows(exportEmployees);
        const buffer = buildSheet(exportRows, "Bank Details");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Bank_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Bank Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "family") {
        const { rows: exportRows, mergeGroups, mergeLeadingCols } =
          getFamilyDetailsExportRows(exportEmployees);
        if (exportRows.length === 0) {
          toast.error("No data to export");
          setExporting(false);
          return;
        }
        const buffer = buildSheet(exportRows, "Family Details", {
          mergeGroups,
          mergeLeadingCols,
        });
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Family_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Family Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "qualification" || section === "educational") {
        const { rows: exportRows, mergeGroups, mergeLeadingCols } =
          getQualificationDetailsExportRows(exportEmployees);
        if (exportRows.length === 0) {
          toast.error("No data to export");
          setExporting(false);
          return;
        }
        const buffer = buildSheet(exportRows, "Education Details", {
          mergeGroups,
          mergeLeadingCols,
        });
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Education_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Education Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "professional") {
        const { rows: exportRows, mergeGroups, mergeLeadingCols } =
          getProfessionalDetailsExportRows(exportEmployees);
        if (exportRows.length === 0) {
          toast.error("No data to export");
          setExporting(false);
          return;
        }
        const buffer = buildSheet(exportRows, "Professional Details", {
          mergeGroups,
          mergeLeadingCols,
        });
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Professional_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Professional Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      if (section === "statutory") {
        const exportRows = getStatutoryDetailsExportRows(exportEmployees);
        const buffer = buildSheet(exportRows, "Statutory Details");
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename("Employees_Statutory_Export");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Statutory Details data exported successfully!");
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      // Legacy / fallback: server-side export
      let response;
      switch (section) {
        case "experience":
          response = await employeeService.exportExperienceDetails();
          break;
        default:
          response = await employeeService.exportEmployees();
          break;
      }

      if (response && response.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildModuleExcelFilename(`Employees_${section}_Export`);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(
          `${section.charAt(0).toUpperCase() + section.slice(1)} data exported successfully!`
        );
        setShowExportModal(false);
      }
    } catch (err) {
      setError(err.message || `Failed to export ${section} data`);

      toast.error(`Export failed. Please try again.`);
    } finally {
      setExporting(false);
    }
  };

  // Download template function

  const handleDownloadTemplate = (section) => {
    if (!canCreateEmployeeRecords) {
      toast.error("You do not have permission to download import templates.");
      return;
    }
    try {
      // Create comprehensive template data based on section

      let templateData = [];

      let fileName = "";

      let instructionsData = [];

      switch (section) {
        case "org":
          // Comprehensive Organization Details Template with exact same fields as export

          templateData = [
            // Header row with exact same fields as export (34 fields)

            [
              "Employee Code*",
              "Name*",
              "Gender*",
              "Date of Birth*",
              "Mobile Number*",
              "Personal Email*",
              "Official Email*",
              "Blood Group",
              "Aadhaar Number*",
              "PAN Number*",
              "Branch*",
              "Designation*",
              "Department*",
              "Sub Department*",
              "Grade*",
              "Category*",
              "Reporting Manager",
              "Date of Join*",
              "Employment Type*",
              "Employment Status*",
              "CTC (Cost to Company)*",
              "Confirmation Date",
              "Date of Resignation",
              "Date of Relieved",
              "Resignation/Termination Reason",
              "PF Applicable",
              "Ceiling PF",
              "EPS Applicable",
              "ESI Applicable",
              "PT Applicable",
              "Gratuity Applicable",
              "Enable ESS",
              "Send Invite",
            ],

            // Sample data row 1

            [
              "EMP001",
              "John Doe",
              "Male",
              "1990-05-15",
              "9876543210",
              "john.doe@personal.com",
              "john.doe@company.com",
              "",
              "123456789012",
              "ABCDE1234F",
              "Head Office",
              "Software Engineer",
              "IT",
              "Development",
              "Grade A",
              "Regular",
              "Jane Smith",
              "2024-01-15",
              "Full-time",
              "Probation",
              "500000",
              "",
              "",
              "",
              "",
              "Yes",
              "Yes",
              "No",
              "No",
              "Yes",
              "No",
              "No",
              "Yes",
              "No",
            ],

            // Sample data row 2

            [
              "EMP002",
              "Jane Smith",
              "Female",
              "1988-12-20",
              "9876543211",
              "jane.smith@personal.com",
              "jane.smith@company.com",
              "A+",
              "987654321098",
              "FGHIJ5678K",
              "Mumbai Branch",
              "Senior Developer",
              "IT",
              "Development",
              "Grade B",
              "Regular",
              "John Doe",
              "2023-06-01",
              "Full-time",
              "Confirmed",
              "750000",
              "2023-12-01",
              "",
              "",
              "",
              "Yes",
              "Yes",
              "No",
              "No",
              "Yes",
              "No",
              "No",
              "Yes",
              "No",
            ],
          ];

          // Instructions for Organization Details

          instructionsData = [
            ["IMPORT INSTRUCTIONS - ORGANIZATION DETAILS"],

            [""],

            ["FIELD REQUIREMENTS:"],

            ["* = Required fields (must be filled)"],

            [""],

            ["FIELD DESCRIPTIONS:"],

            ["1. Employee Code*: Unique identifier (e.g., EMP001)"],

            ["2. Name*: Full name of employee"],

            ["3. Gender*: Male, Female, Other"],

            ["4. Date of Birth*: Format YYYY-MM-DD (age 18-70 years)"],

            ["5. Mobile Number*: 10-digit Indian mobile number"],

            ["6. Personal Email*: Personal email address"],

            ["7. Official Email*: Company email address"],

            [
              "8. Blood Group: A+, A-, B+, AB+, AB-, O+, O-, NA (Optional - leave blank if not available)",
            ],

            ["9. Aadhaar Number*: 12-digit Aadhaar number"],

            ["10. PAN Number*: Use ABCDE1234F, PANNOTAVBL, or PANAPPLIED"],

            ["11. Branch*: Branch name (e.g., Head Office, Mumbai Branch)"],

            ["12. Designation*: Job title (e.g., Software Engineer)"],

            ["13. Department*: Department name (e.g., IT, HR)"],

            ["14. Sub Department*: Sub-department name (e.g., Development)"],

            ["15. Grade*: Grade level (e.g., Grade A, Grade B)"],

            ["16. Category*: Employee category (e.g., Regular, Contract)"],

            ["17. Reporting Manager: Name of reporting manager (optional)"],

            ["18. Date of Join*: Format YYYY-MM-DD"],

            ["19. Employment Type*: Full-time, Part-time, Contract, Intern"],

            [
              "20. Employment Status*: Probation, Confirmed, Resigned, Relieved, Terminated",
            ],

            ["21. CTC (Cost to Company)*: Annual salary amount"],

            ["22. Confirmation Date: Required if status is Confirmed"],

            [
              "23. Date of Resignation: Required if status is Resigned/Relieved",
            ],

            [
              "24. Date of Relieved: Required if status is Resigned/Relieved/Terminated",
            ],

            [
              "25. Resignation/Termination Reason: Required if status is Resigned/Terminated",
            ],

            ["26. PF Applicable: Yes or No"],

            ["27. Ceiling PF: Yes or No"],

            ["28. EPS Applicable: Yes or No"],

            ["29. ESI Applicable: Yes or No"],

            ["30. PT Applicable: Yes or No"],

            ["31. Gratuity Applicable: Yes or No"],

            ["32. Enable ESS: Yes or No (enable Employee Self Service for this employee)"],

            ["33. Send Invite: Yes or No (only applies when Enable ESS is Yes)"],

            [""],

            ["IMPORTANT NOTES:"],

            ["- Remove sample data rows before importing"],

            ["- Use exact names for Branch, Designation, Department, etc."],

            ["- Conditional fields are required based on Employment Status"],

            ['- All Yes/No fields should contain exactly "Yes" or "No"'],

            ["- Dates must be in YYYY-MM-DD format"],

            ["- Employee codes must be unique"],

            ["- Email addresses must be valid and unique"],

            [""],

            ["SUPPORTED FILE FORMATS:"],

            ["- Excel (.xlsx)"],

            ["- CSV (.csv)"],

            [""],

            ["For assistance, contact HR Department"],
          ];

          fileName = buildModuleExcelFilename(
            "Comprehensive_Employee_Import_Template"
          );

          break;

        case "qualification":
        case "educational":
          // Comprehensive Qualification Details Template with exact same fields as export
          templateData = [
            // Header row with exact same fields as export (9 fields)
            [
              "Employee Code*",
              "Name*",
              "Less than Primary Education*",
              "Qualification Number*",
              "Degree*",
              "Year of Completion*",
              "Name of Institution*",
              "Percentage/Grade/Class*",
              "Specialization*",
            ],
            // Sample data row 1
            [
              "EMP001",
              "John Doe",
              "No",
              "1",
              "Graduation / Bachelor's Degree",
              "2020",
              "University of Technology",
              "85%",
              "Computer Science",
            ],
            [
              "EMP001",
              "John Doe",
              "No",
              "2",
              "12th / Higher Secondary",
              "2016",
              "ABC College",
              "92%",
              "Science",
            ],
            // Sample data row 2
            [
              "EMP002",
              "Jane Smith",
              "No",
              "1",
              "Post Graduation / Master's Degree",
              "2022",
              "Institute of Management",
              "78%",
              "Business Administration",
            ],
            [
              "EMP002",
              "Jane Smith",
              "No",
              "2",
              "Graduation / Bachelor's Degree",
              "2020",
              "State University",
              "88%",
              "Engineering",
            ],
            // Sample data row 3 (Less than Primary)
            ["EMP003", "Mike Johnson", "Yes", "", "", "", "", "", ""],
          ];

          // Instructions for Qualification Details
          instructionsData = [
            ["IMPORT INSTRUCTIONS - QUALIFICATION DETAILS"],
            [""],
            ["FIELD REQUIREMENTS:"],
            ["* = Required fields (must be filled)"],
            [""],
            ["FIELD DESCRIPTIONS:"],
            ["1. Employee Code*: Unique identifier (e.g., EMP001)"],
            ["2. Name*: Full name of employee"],
            ["3. Less than Primary Education*: Yes or No"],
            [
              "4. Qualification Number*: Sequential number for multiple qualifications (1, 2, 3...)",
            ],
            ["5. Degree*: Educational degree from dropdown options"],
            ["6. Year of Completion*: 4-digit year (e.g., 2020)"],
            ["7. Name of Institution*: Educational institution name"],
            [
              "8. Percentage/Grade/Class*: Academic performance (e.g., 85%, A+, First Class)",
            ],
            [
              "9. Specialization*: Field of study (e.g., Computer Science, Engineering)",
            ],
            [""],
            ["DEGREE OPTIONS:"],
            ["- 10th / Matriculation"],
            ["- 12th / Higher Secondary"],
            ["- Diploma"],
            ["- Graduation / Bachelor's Degree"],
            ["- Post Graduation / Master's Degree"],
            ["- Doctorate / Ph.D."],
            ["- Professional Certification"],
            ["- Others"],
            [""],
            ["IMPORTANT NOTES:"],
            [
              '- If "Less than Primary Education" is "Yes", leave qualification fields empty',
            ],
            [
              '- If "Less than Primary Education" is "No", at least one qualification is required',
            ],
            ["- Each qualification should be in a separate row"],
            [
              "- Employee Code and Name should be same for multiple qualifications of same employee",
            ],
            [
              "- Qualification Number should be sequential (1, 2, 3...) for each employee",
            ],
            ["- Year must be exactly 4 digits (e.g., 2020, 2023)"],
            ["- Remove sample data rows before importing"],
            [""],
            ["SUPPORTED FILE FORMATS:"],
            ["- Excel (.xlsx)"],
            ["- CSV (.csv)"],
            [""],
            ["For assistance, contact HR Department"],
          ];

          fileName = buildModuleExcelFilename(
            "Qualification_Details_Import_Template"
          );

          break;

        case "statutory":
          // Comprehensive Statutory Details Template with exact same fields as export
          templateData = [
            // Header row with exact same fields as export (10 fields)
            [
              "Employee Code*",
              "Name*",
              "Has UAN*",
              "PF UAN Number*",
              "EPS Eligible Previous Company*",
              "Has ESI IP*",
              "ESI IP Number*",
              "ESI State*",
              "ESI District*",
              "ESI Dispensary*",
            ],
            // Sample data row 1
            [
              "EMP001",
              "John Doe",
              "Yes",
              "123456789012",
              "Yes",
              "Yes",
              "1234567890",
              "Karnataka",
              "Bangalore Urban",
              "Bangalore",
            ],
            // Sample data row 2
            [
              "EMP002",
              "Jane Smith",
              "Yes",
              "987654321098",
              "No",
              "No",
              "",
              "Maharashtra",
              "Mumbai",
              "Mumbai",
            ],
            // Sample data row 3
            [
              "EMP003",
              "Mike Johnson",
              "Yes",
              "112233445566",
              "Yes",
              "Yes",
              "9988776655",
              "Tamil Nadu",
              "Chennai",
              "Chennai",
            ],
          ];

          // Instructions for Statutory Details
          instructionsData = [
            ["IMPORT INSTRUCTIONS - STATUTORY DETAILS"],
            [""],
            ["FIELD REQUIREMENTS:"],
            ["* = Required fields (must be filled)"],
            [""],
            ["FIELD DESCRIPTIONS:"],
            ["1. Employee Code*: Unique identifier (e.g., EMP001)"],
            ["2. Name*: Full name of employee"],
            ["3. Has UAN*: Yes or No (UAN is mandatory)"],
            [
              "4. PF UAN Number*: 12-digit UAN number (required if Has UAN = Yes)",
            ],
            ["5. EPS Eligible Previous Company*: Yes or No"],
            ["6. Has ESI IP*: Yes or No (only if ESI is applicable)"],
            [
              "7. ESI IP Number*: 10-digit IP number (required if Has ESI IP = Yes)",
            ],
            ["8. ESI State*: State name (required if ESI is applicable)"],
            ["9. ESI District*: District name (required if ESI is applicable)"],
            [
              "10. ESI Dispensary*: Town/city name (required if ESI is applicable)",
            ],
            [""],
            ["VALIDATION RULES:"],
            ["- UAN Number must be exactly 12 digits"],
            ["- ESI IP Number must be exactly 10 digits"],
            ["- ESI Dispensary must contain only alphabets and spaces"],
            ['- All Yes/No fields should contain exactly "Yes" or "No"'],
            [
              "- ESI fields are only required if ESI is applicable for the employee",
            ],
            [""],
            ["IMPORTANT NOTES:"],
            ["- UAN number is mandatory for all employees"],
            ["- PF UAN Number is required when Has UAN = Yes"],
            ["- ESI IP Number is required when Has ESI IP = Yes"],
            [
              "- ESI State, District, and Dispensary are always required if ESI is applicable",
            ],
            ["- Remove sample data rows before importing"],
            [""],
            ["SUPPORTED FILE FORMATS:"],
            ["- Excel (.xlsx)"],
            ["- CSV (.csv)"],
            [""],
            ["For assistance, contact HR Department"],
          ];

          fileName = buildModuleExcelFilename(
            "Statutory_Details_Import_Template"
          );

          break;

        default:
          templateData = [
            [
              "Employee Code*",
              "First Name*",
              "Last Name*",
              "Email*",
              "Phone*",
              "Department*",
              "Designation*",
              "Date of Join*",
            ],

            [
              "EMP001",
              "John",
              "Doe",
              "john.doe@company.com",
              "9876543210",
              "IT",
              "Software Engineer",
              "2024-01-15",
            ],

            [
              "EMP002",
              "Jane",
              "Smith",
              "jane.smith@company.com",
              "9876543211",
              "IT",
              "Senior Developer",
              "2023-06-01",
            ],
          ];

          fileName = buildModuleExcelFilename("Employee_Data_Template");
      }

      // Create Excel file with multiple sheets

      if (
        section === "org" ||
        section === "qualification" ||
        section === "educational" ||
        section === "statutory"
      ) {
        // For comprehensive template, create Excel with multiple sheets

        const workbook = XLSX.utils.book_new();

        // Add main data sheet

        const templateDataWithSerial =
          Array.isArray(templateData) && templateData.length > 0
            ? [
                ["Sl No", ...(templateData[0] || [])],
                ...templateData
                  .slice(1)
                  .map((row, idx) => [idx + 1, ...(Array.isArray(row) ? row : [])]),
              ]
            : templateData;
        const worksheet = XLSX.utils.aoa_to_sheet(templateDataWithSerial);

        const dataSheetName =
          section === "org"
            ? "Employee Data"
            : section === "qualification" || section === "educational"
            ? "Qualification Data"
            : section === "statutory"
            ? "Statutory Data"
            : "Employee Data";

        XLSX.utils.book_append_sheet(workbook, worksheet, dataSheetName);

        // Add instructions sheet

        const instructionsWorksheet = XLSX.utils.aoa_to_sheet(instructionsData);

        XLSX.utils.book_append_sheet(
          workbook,
          instructionsWorksheet,
          "Instructions"
        );

        // Add validation sheet with dropdown options

        const validationData = [
          ["FIELD", "VALIDATION TYPE", "OPTIONS/REQUIREMENTS"],

          ["Gender", "Dropdown", "Male, Female, Other"],

          ["Blood Group", "Dropdown", "A+, A-, B+, B-, AB+, AB-, O+, O-, NA"],

          [
            "Employment Type",
            "Dropdown",
            "Full-time, Part-time, Contract, Intern",
          ],

          [
            "Employment Status",
            "Dropdown",
            "Probation, Confirmed, Resigned, Relieved, Terminated",
          ],

          ["PF Applicable", "Dropdown", "Yes, No"],

          ["Ceiling PF", "Dropdown", "Yes, No"],

          ["EPS Applicable", "Dropdown", "Yes, No"],

          ["ESI Applicable", "Dropdown", "Yes, No"],

          ["PT Applicable", "Dropdown", "Yes, No"],

          ["Gratuity Applicable", "Dropdown", "Yes, No"],

          ["Enable ESS", "Dropdown", "Yes, No"],
          ["Send Invite", "Dropdown", "Yes, No"],

          ["Branch", "Text", "Use exact branch names from system"],

          ["Designation", "Text", "Use exact designation names from system"],

          ["Department", "Text", "Use exact department names from system"],

          [
            "Sub Department",
            "Text",
            "Use exact sub-department names from system",
          ],

          ["Grade", "Text", "Use exact grade names from system"],

          ["Category", "Text", "Use exact category names from system"],

          ["Reporting Manager", "Text", "Use exact employee names from system"],
        ];

        const validationWorksheet = XLSX.utils.aoa_to_sheet(validationData);

        XLSX.utils.book_append_sheet(
          workbook,
          validationWorksheet,
          "Field Validations"
        );

        // Write the file

        XLSX.writeFile(workbook, fileName);
      } else {
        // For other sections, create CSV content

        const csvContent = templateData.map((row) => row.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");

        a.href = url;

        a.download = fileName;

        document.body.appendChild(a);

        a.click();

        window.URL.revokeObjectURL(url);

        document.body.removeChild(a);
      }

      toast.success(
        `${
          section.charAt(0).toUpperCase() + section.slice(1)
        } template downloaded successfully!`
      );
    } catch (err) {
      console.error("Template download error:", err);

      toast.error("Failed to download template. Please try again.");
    }
  };

  // Section-wise import functions

  const handleSectionImport = async (section, file) => {
    if (!file) return;

    // Show confirmation modal before import
    setShowImportConfirmationModal(true);
    setSelectedImportFile(file);
    setSelectedImportSection(section);

    // Reset file input
    const fileInput = document.querySelector(
      `input[type="file"][data-section="${section}"]`
    );
    if (fileInput) fileInput.value = "";
  };

  // State for section-specific import
  const [selectedImportSection, setSelectedImportSection] = useState(null);

  // Confirm section import function
  const confirmSectionImport = async () => {
    if (!selectedImportFile || !selectedImportSection) return;

    try {
      setImporting(true);
      setError(null);
      setShowImportConfirmationModal(false);

      const formData = new FormData();
      formData.append("file", selectedImportFile);
      formData.append("section", selectedImportSection);

      let response;

      // Call appropriate API based on section
      switch (selectedImportSection) {
        case "org":
          response = await employeeService.importEmployees(formData);
          break;
        case "personal":
          response = await employeeService.importPersonalDetails(formData);
          break;
        case "family":
          response = await employeeService.importFamilyDetails(formData);
          break;
        case "professional":
          response = await employeeService.importProfessionalDetails(formData);
          break;
        case "bank":
          response = await employeeService.importBankDetails(formData);
          break;
        case "qualification":
          response = await employeeService.importQualificationDetails(formData);
          break;
        case "experience":
          response = await employeeService.importExperienceDetails(formData);
          break;
        default:
          response = await employeeService.importEmployees(formData);
      }

      // Handle response with detailed results
      if (response.success) {
        const { data, summary } = response;

        // Update employees list if it's a full employee import
        if (selectedImportSection === "org" && data && data.length > 0) {
          setEmployees((prevEmployees) => [...prevEmployees, ...data]);
        }

        // Set import results for display
        setImportResults({
          success: true,
          section: selectedImportSection,
          summary: summary || {
            total: 0,
            successful: data?.length || 0,
            failed: 0,
            errors: [],
          },
          successfulRecords: data || [],
          failedRecords: summary?.failedRecords || [],
          errors: summary?.errors || [],
        });

        setShowImportResultsModal(true);

        toast.success(
          `${
            selectedImportSection.charAt(0).toUpperCase() +
            selectedImportSection.slice(1)
          } import completed! ${
            summary?.successful || data?.length || 0
          } records imported successfully.`
        );
      } else {
        throw new Error(
          response.message || `${selectedImportSection} import failed`
        );
      }
    } catch (err) {
      console.error(`${selectedImportSection} import error:`, err);

      // Handle detailed error response
      if (err.response?.data) {
        const errorData = err.response.data;
        setImportResults({
          success: false,
          section: selectedImportSection,
          summary: {
            total: errorData.total || 0,
            successful: errorData.successful || 0,
            failed: errorData.failed || 0,
            errors: errorData.errors || [],
          },
          successfulRecords: errorData.successfulRecords || [],
          failedRecords: errorData.failedRecords || [],
          errors: errorData.errors || [],
        });
        setShowImportResultsModal(true);
      } else {
        setError(
          err.message || `Failed to import ${selectedImportSection} details`
        );
        toast.error(
          err.message || `Failed to import ${selectedImportSection} details`
        );
      }
    } finally {
      setImporting(false);
      setSelectedImportFile(null);
      setSelectedImportSection(null);
    }
  };

  // ✅ Handle employee import validation
  // const handleEmployeeImport = async (file) => {
  //   const formData = new FormData();
  //   formData.append("file", file);

  //   try {
  //     toast.loading("Validating file...");
  //     const result = await employeeService.validateEmployeeImport(formData);
  //     toast.dismiss();

  //     if (result.success) {
  //       setImportSummary(result);
  //       setShowImportModal(true);
  //     } else {
  //       toast.error("Validation failed. Please check your file.");
  //     }
  //   } catch (error) {
  //     toast.dismiss();
  //     toast.error("File validation error!");
  //   }
  // };

  // // ✅ Import only valid records when user clicks Yes
  // const handleConfirmImport = async () => {
  //   try {
  //     toast.loading("Importing valid records...");
  //     const formData = new FormData();
  //     const validBlob = new Blob(
  //       [JSON.stringify(importSummary.validRecords)],
  //       { type: "application/json" }
  //     );
  //     formData.append("validRecords", validBlob);

  //     const result = await employeeService.importEmployees(formData);
  //     toast.dismiss();
  //     toast.success(`Successfully imported ${result.count || importSummary.validCount} employees`);
  //     setShowImportModal(false);
  //     fetchEmployees();
  //   } catch (error) {
  //     toast.dismiss();
  //     toast.error("Import failed!");
  //   }
  // };
  const fetchResignationReasons = useCallback(async () => {
    try {
      // Using the service defined in your api.js
      const response = await reasonsService.getAllResignationReasons();
      if (response.success) {
        setResignationReasonsList(response.data || []);
        console.log("Fetched Resignation Reasons:", response.data); // Optional debug log
      } else {
        toast.error("Failed to load resignation reasons");
      }
    } catch (error) {
      console.error("Error fetching resignation reasons:", error);
      toast.error("Error fetching resignation reasons");
    }
  }, []);
  const fetchDepartments = useCallback(async () => {
    try {
      setDepartmentsLoading(true);

      const response = await departmentService.getDepartments();

      if (response.success) {
        setDepartments(response.data);
      } else {
        toast.error(response.message || "Failed to fetch departments");
      }
    } catch (error) {
      console.error("Error fetching departments:", error);

      toast.error("Error fetching departments");
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  const fetchDesignations = useCallback(async () => {
    try {
      const response = await designationService.getDesignations();

      if (response.success) {
        setDesignations(response.data);
      } else {
        toast.error(response.message || "Error fetching designations");
      }
    } catch (error) {
      console.error("Error fetching designations:", error);

      toast.error("Error fetching designations");
    }
  }, []);

  const fetchSubDepartments = useCallback(async () => {
    try {
      const response = await subDepartmentService.getAllSubDepartments();

      if (response.success) {
        setSubDepartments(response.data);
      } else {
        toast.error(response.message || "Error fetching sub-departments");
      }
    } catch (error) {
      console.error("Error fetching sub-departments:", error);

      toast.error("Error fetching sub-departments");
    }
  }, []);

  const fetchGrades = useCallback(async () => {
    try {
      const response = await gradesService.getGrades();

      if (response.success) {
        setGrades(response.data);
      } else {
        toast.error(response.message || "Error fetching grades");
      }
    } catch (error) {
      console.error("Error fetching grades:", error);

      toast.error("Error fetching grades");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryService.getAllCategories();

      if (response.success) {
        setCategories(response.data);
      } else {
        toast.error(response.message || "Error fetching categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);

      toast.error("Error fetching categories");
    }
  }, []);

  // Function to manually refresh master data cache
  const refreshMasterDataCache = useCallback(async () => {
    localStorage.removeItem("employee_master_data");
    localStorage.removeItem("employee_master_data_timestamp");

    const startTime = performance.now();
    try {
      const [
        departmentsRes,
        branchesRes,
        designationsRes,
        subDepartmentsRes,
        gradesRes,
        categoriesRes,
        reasonsRes,
        terminationReasonsRes,
      ] = await Promise.all([
        departmentService.getDepartments(),
        branchService.getBranches(),
        designationService.getDesignations(),
        subDepartmentService.getAllSubDepartments(),
        gradesService.getGrades(),
        categoryService.getAllCategories(),
        reasonsService.getAllResignationReasons(),
        reasonsService.getAllTerminationReasons(),
      ]);

      if (departmentsRes.success) setDepartments(departmentsRes.data);
      if (branchesRes.success) setBranches(branchesRes.data);
      if (designationsRes.success) setDesignations(designationsRes.data);
      if (subDepartmentsRes.success) setSubDepartments(subDepartmentsRes.data);
      if (gradesRes.success) setGrades(gradesRes.data);
      if (categoriesRes.success) setCategories(categoriesRes.data);
      if (reasonsRes.success) setResignationReasonsList(reasonsRes.data);
      if (terminationReasonsRes.success)
        setTerminationReasonsList(terminationReasonsRes.data);

      const cacheData = {
        departments: departmentsRes.data || [],
        branches: branchesRes.data || [],
        designations: designationsRes.data || [],
        subDepartments: subDepartmentsRes.data || [],
        grades: gradesRes.data || [],
        categories: categoriesRes.data || [],
        resignationReasons: reasonsRes.data || [],
        terminationReasons: terminationReasonsRes.data || [],
      };
      localStorage.setItem("employee_master_data", JSON.stringify(cacheData));
      localStorage.setItem(
        "employee_master_data_timestamp",
        Date.now().toString()
      );

      const fetchTime = ((performance.now() - startTime) / 1000).toFixed(2);
      toast.success(`Master data refreshed in ${fetchTime}s`);
    } catch (error) {
      console.error("Error refreshing master data:", error);
      toast.error("Failed to refresh master data");
    }
  }, []);

  // Function to calculate notice period
  const calculateNoticePeriod = (resignationDate, relievingDate) => {
    if (!resignationDate || !relievingDate) return 0;

    const resignation = new Date(resignationDate);
    const relieving = new Date(relievingDate);

    // Calculate difference in days
    const timeDiff = relieving.getTime() - resignation.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return Math.max(0, daysDiff);
  };

  // Function to handle resignation data changes
  const handleResignationDataChange = (field, value) => {
    setResignationData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-calculate notice period when both dates are set
      if (field === "resignationDate" || field === "relievingDate") {
        if (newData.resignationDate && newData.relievingDate) {
          newData.noticePeriodDays = calculateNoticePeriod(
            newData.resignationDate,
            newData.relievingDate
          );
        }
      }

      return newData;
    });
  };

  const refreshDepartments = async () => {
    await fetchDepartments();
  };

  const fetchBranches = async () => {
    try {
      setLoading(true);

      const response = await branchService.getBranches();

      if (response.success) {
        setBranches(response.data);
      } else {
        toast.error(response.message || "Failed to fetch branches");
      }
    } catch (error) {
      toast.error("Error fetching branches");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = () => {
    console.log("Invite functionality to be implemented");
  };

  const hasEmployeeActionAccess = useCallback(
    (actionType) => {
      switch (actionType) {
        case "edit":
          return canEditEmployeeRecords;
        case "confirmation":
          return canRunConfirmation;
        case "transfer":
          return canRunTransfer;
        case "resignation":
          return canRunResignation;
        case "salaryRevision":
          return canEditEmployeeRecords;
        case "letters":
        case "fullAndFinal":
          return canEditEmployeeRecords;
        default:
          return true;
      }
    },
    [
      canEditEmployeeRecords,
      canRunConfirmation,
      canRunTransfer,
      canRunResignation,
    ]
  );

  // Handle employee actions

  const handleAction = (actionType, employee) => {
    if (!hasEmployeeActionAccess(actionType)) {
      toast.error("You do not have permission for this employee action.");
      return;
    }
    console.log(`Action: ${actionType} for employee:`, employee);

    // Ensure selectedEmployee is set for modal rendering
    setSelectedEmployee(employee);

    // TODO: Implement specific action logic

    switch (actionType) {
      case "letters":
        toast.success(`Letters action initiated for ${employee.user?.name}`);

        break;

      case "confirmation":
        setShowConfirmationModal(true);

        setConfirmationData({
          isConfirmed: "",

          confirmationDate: "",

          extendProbation: false,

          extendedProbationDate: "",
        });

        break;

      case "salaryRevision":
        setShowSalaryRevisionModal(true);

        setSalaryRevisionData({
          effectiveDate: "",

          arrearsEffectiveDate: "",

          ctcAmount: "",

          reason: "",

          changeDesignation: false,

          newDesignationId: "",

          designationSearch: "",

          copyEffectiveDate: false,
        });

        // Fetch latest salary effective date
        fetchLatestSalaryEffectiveDate(employee.id);

        break;

      case "transfer":
        setShowTransferModal(true);

        setTransferData({
          effectiveDate: getTodayDateYYYYMMDD(),

          newBranchId: "",

          newDepartmentId: "",

          newSubDepartmentId: "",

          newDesignationId: "",

          newGradeId: "",

          newCategoryId: "",
          newReportingManagerId: "",

          reason: "",

          remarks: "",
        });

        break;

      case "resignation": {
        setShowResignationModal(true);

        // Set default resignation date to today
        const today = new Date().toISOString().split("T")[0];

        setResignationData({
          resignationDate: today,

          relievingDate: "",

          reason: "",

          noticePeriodDays: 0,

          noticePeriodWaiverDays: 0,

          noticePeriodToDeduct: 0,

          isRetroResignation: false,

          proofDocument: null,
        });

        break;
      }

      case "edit":
        handleEdit(employee);
        break;

      case "fullAndFinal":
        toast.success(
          `Full & Final settlement action initiated for ${employee.user?.name}`
        );

        break;

      default:
        toast.error("Unknown action type");
    }
  };

  useEffect(() => {
    const findTargetEmployee = (payload) => {
      const employeeId = Number(payload?.employeeId);
      const userId = Number(payload?.userId);
      return (
        employees.find((emp) => emp?.id === employeeId) ||
        employees.find((emp) => Number(emp?.user?.id) === userId) ||
        null
      );
    };

    const runQuickAction = (payload) => {
      const action = payload?.actionLabel;
      if (!action) return;
      const employee = findTargetEmployee(payload);
      if (!employee) return;

      if (action === "Resignation") {
        handleAction("resignation", employee);
        return;
      }
      if (action === "Investment Declaration") {
        toast("Investment Declaration form is not available in Employee actions yet.");
        return;
      }
      if (action === "MRF") {
        toast("MRF form is not available in Employee actions yet.");
      }
    };

    const handleQuickActionEvent = (event) => {
      runQuickAction(event?.detail || {});
    };

    const consumeStoredQuickAction = () => {
      try {
        const raw = localStorage.getItem("adminQuickActionRequest");
        if (!raw) return;
        const payload = JSON.parse(raw);
        if (Date.now() - Number(payload?.requestedAt || 0) > 3 * 60 * 1000) {
          localStorage.removeItem("adminQuickActionRequest");
          return;
        }
        const employeeActions = new Set([
          "Resignation",
          "Investment Declaration",
          "MRF",
        ]);
        if (employeeActions.has(payload?.actionLabel)) {
          runQuickAction(payload);
        }
        localStorage.removeItem("adminQuickActionRequest");
      } catch {
        localStorage.removeItem("adminQuickActionRequest");
      }
    };

    window.addEventListener("admin:quick-action", handleQuickActionEvent);
    consumeStoredQuickAction();
    return () => {
      window.removeEventListener("admin:quick-action", handleQuickActionEvent);
    };
  }, [employees]);

  // Handle manual pattern save
  const saveManualPattern = async () => {
    try {
      // Validate that effective date is provided
      if (!manualEffectiveDate) {
        toast.error("Please select an effective date for manual pattern");
        return;
      }

      // Validate that effective date is today or in the future
      const selectedDate = new Date(manualEffectiveDate);
      const today = new Date(getTodayDate());
      if (selectedDate < today) {
        toast.error(
          "Effective date cannot be in the past. It must be today or in the future."
        );
        return;
      }

      const payload = {
        patternType: "manual",
        effectiveDate: manualEffectiveDate,
      };

      await employeeCodeStructureService.saveStructure(payload);
      toast.success("Manual pattern saved successfully!");
      // Reload to update the UI
      window.location.reload();
    } catch (error) {
      console.error("Error saving manual pattern:", error);
      // Check if it's a consecutive duplicate error
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes(
          "same configuration consecutively"
        )
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to save manual pattern");
      }
    }
  };

  // Handle photo click to show passport size card

  const handlePhotoClick = (employee) => {
    setSelectedPhotoEmployee(employee);
    setShowPhotoModal(true);

    // Always fetch a fresh signed URL for the modal so new uploads are reflected immediately
    try {
      const rawPhoto = employee?.user?.photo || employee?.photo;
      const hasDirectUrl =
        typeof rawPhoto === "string" &&
        (rawPhoto.startsWith("data:") || rawPhoto.startsWith("http://") || rawPhoto.startsWith("https://"));

      if (!hasDirectUrl && employee?.id) {
        employeeService
          .getEmployeePhotoUrl(employee.id)
          .then((resp) => {
            const url = resp?.url || resp?.data?.url;
            if (!url) return;
            setEmployeePhotoUrlCache((prev) => {
              const next = new Map(prev);
              // Overwrite only this employee's entry so the modal uses the latest URL
              next.set(employee.id, url);
              return next;
            });
          })
          .catch((err) => {
            console.error("Failed to fetch photo URL for modal:", err);
          });
      }
    } catch (err) {
      console.error("Error in handlePhotoClick:", err);
    }

    // Auto-hide after 30 seconds
    setTimeout(() => {
      setShowPhotoModal(false);
      setSelectedPhotoEmployee(null);
    }, 30000);
  };

  // Handle ID card download

  const handleDownloadIDCard = (employee) => {
    try {
      // Create a canvas for the ID card

      const canvas = document.createElement("canvas");

      const ctx = canvas.getContext("2d");

      canvas.width = 400;

      canvas.height = 250;

      // Set background

      ctx.fillStyle = "#f8fafc";

      ctx.fillRect(0, 0, 400, 250);

      // Add border

      ctx.strokeStyle = "#1e40af";

      ctx.lineWidth = 3;

      ctx.strokeRect(5, 5, 390, 240);

      // Company header

      ctx.fillStyle = "#1e40af";

      ctx.fillRect(0, 0, 400, 40);

      ctx.fillStyle = "#ffffff";

      ctx.font = "bold 18px Arial";

      ctx.textAlign = "center";

      ctx.fillText("COMPANY NAME", 200, 25);

      // Employee photo placeholder

      ctx.fillStyle = "#e2e8f0";

      ctx.fillRect(20, 60, 80, 100);

      ctx.fillStyle = "#64748b";

      ctx.font = "bold 24px Arial";

      ctx.textAlign = "center";

      ctx.fillText(
        employee.user?.name?.charAt(0)?.toUpperCase() || "E",
        60,
        115
      );

      // Employee details

      ctx.fillStyle = "#1e293b";

      ctx.font = "bold 14px Arial";

      ctx.textAlign = "left";

      ctx.fillText("Emp Code:", 120, 70);

      ctx.fillText("Name:", 120, 90);

      ctx.fillText("Designation:", 120, 110);

      ctx.fillText("Department:", 120, 130);

      ctx.fillText("Blood Group:", 120, 150);

      ctx.fillText("Address:", 120, 170);

      // Employee values

      ctx.font = "12px Arial";

      ctx.fillStyle = "#475569";

      ctx.fillText(employee.empCode || "N/A", 220, 70);

      ctx.fillText(
        `${employee.user?.name || "N/A"} ${employee.user?.lastName || ""}`,
        220,
        90
      );

      ctx.fillText(
        getNameByIdFromList(employee.designationId, designationsList, employee, 'designation') || "N/A",
        220,
        110
      );

      ctx.fillText(
        getNameByIdFromList(employee.departmentId, departmentsList, employee, 'department') || "N/A",
        220,
        130
      );

      ctx.fillText(employee.bloodGroup || "N/A", 220, 150);

      ctx.fillText(employee.address || "N/A", 220, 170);

      // Company details at bottom

      ctx.fillStyle = "#1e40af";

      ctx.fillRect(0, 210, 400, 40);

      ctx.fillStyle = "#ffffff";

      ctx.font = "12px Arial";

      ctx.textAlign = "center";

      ctx.fillText(
        "Company Address: 123 Business Street, City, State, Country",
        200,
        225
      );

      ctx.fillText(
        "Phone: +1-234-567-8900 | Email: info@company.com",
        200,
        240
      );

      // Convert to blob and download

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");

        a.href = url;

        a.download = `ID_Card_${employee.empCode}_${employee.user?.name}.png`;

        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);

        URL.revokeObjectURL(url);

        toast.success("ID Card downloaded successfully!");
      }, "image/png");
    } catch (error) {
      console.error("Error generating ID card:", error);

      toast.error("Failed to download ID card");
    }
  };

  // Handle sorting

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const employeeTableThSortClass = (field) => {
    const base = "employee-table-th-sortable";
    return sortField === field ? `${base} employee-table-th-sorted` : base;
  };

  // Handle column filtering

  const handleColumnFilter = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,

      [field]: value,
    }));
  };

  // Handle date range filtering

  const handleDateRangeFilter = (field, type, value) => {
    setColumnFilters((prev) => ({
      ...prev,

      [field + (type === "from" ? "From" : "To")]: value,
    }));
  };

  // Enhanced date fetching for different employee statuses

  const getEmployeeRelievingDate = (employee) => {
    if (!employee) return null;
    console.log("EMPLOYEE EMPLOYMENT STATUS:", employee.employmentStatus);
    // For terminated employees, check specific fields first
    if (employee.employmentStatus === "Terminated") {
      return (
        employee.terminationDate ||
        employee.lastWorkingDay ||
        employee.dateOfLeaving ||
        employee.relievingDate
      );
    }

    // For resigned employees

    if (employee.employmentStatus === "Resigned") {
      return (
        employee.resignationDate ||
        employee.dateOfLeaving ||
        employee.relievingDate ||
        employee.lastWorkingDay
      );
    }

    // For relieved employees

    if (employee.employmentStatus === "Relieved") {
      return (
        employee.relievedDate ||
        employee.dateOfLeaving ||
        employee.lastWorkingDay
      );
    }

    // Default fallback

    return (
      employee.dateOfLeaving ||
      employee.relievingDate ||
      employee.leaveDate ||
      employee.resignationDate ||
      employee.terminationDate ||
      employee.exitDate ||
      employee.lastWorkingDay 
    );
  };

  // Toggle column filter visibility

  const toggleColumnFilter = (field) => {
    setShowColumnFilters((prev) => ({
      ...prev,

      [field]: !prev[field],
    }));
  };

  // Only block initial render with a full-page spinner.
  // During subsequent refreshes (search/filter/pagination), keep the page mounted
  // so inputs stay focused; table can refresh without unmounting the header.
  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-redhalo-red"></div>
      </div>
    );
  }

  // Generate preview codes for historical configuration
  const generateHistoryPreviewCodes = (entry) => {
    const codes = [];

    // Handle new API data structure that supports both automated and manual patterns
    if (entry.patternType === "automated") {
      // Generate preview codes for automated pattern
      for (let i = 1; i <= 5; i++) {
        const number = (entry.startingNumber + i - 1)
          .toString()
          .padStart(entry.numberOfDigits, "0");
        codes.push(`${entry.prefix}${number}`);
      }
    } else if (entry.patternType === "manual") {
      // For manual pattern, show descriptive codes
      codes.push("Manual Pattern");
      codes.push("Custom Codes");
      codes.push("User Defined");
      codes.push("HR Assigned");
      codes.push("No Auto-Gen");
    } else {
      // Fallback for old data structure (legacy support)
      if (entry.organizationLevel === "company" && entry.companyConfig) {
        for (let i = 1; i <= 5; i++) {
          const number = (entry.companyConfig.startingNumber + i - 1)
            .toString()
            .padStart(entry.companyConfig.digits, "0");
          codes.push(`${entry.companyConfig.prefix}${number}`);
        }
      } else {
        codes.push("Legacy Pattern");
        codes.push("Old Configuration");
        codes.push("Historical Entry");
      }
    }

    return codes;
  };
  // console.log("--- Rendering Employee.jsx ---");
  // console.log(
  //   "State before render - showConfirmationModal:",
  //   showConfirmationModal
  // );
  // console.log("State before render - showTransferModal:", showTransferModal);
  // console.log(
  //   "State before render - showSalaryRevisionModal:",
  //   showSalaryRevisionModal
  // );
  // console.log(
  //   "State before render - showResignationModal:",
  //   showResignationModal
  // );
  // console.log(
  //   "State before render - selectedEmployee:",
  //   selectedEmployee ? selectedEmployee.empCode : null
  // ); // Log empCode or null
  // console.log("State before render - showForm:", showForm);
  // --->>> END LOGS <<<---
  return (
    <div className="flex-1 h-[calc(100dvh-5rem)] overflow-hidden">
      <div
        className={`flex h-full flex-col ${
          darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : activeEmployeeTab === "reports" ? "bg-white text-gray-900" : "bg-redhalo-gray/30 text-gray-900"
        }`}
      >
        {error && (
          <div className="p-4 mb-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p>{error?.message || String(error)}</p>
          </div>
        )}

        <div className="flex flex-1">
        {/* Sub Sidebar — sticky below main nav, in-flow (no overlap of main content) */}
        <aside
          className="sticky top-0 z-20 flex h-full min-h-0 w-56 shrink-0 flex-col overflow-hidden bg-white shadow-sm dark:bg-[#121212]"
        >
          <div className="rh-subnav-aside-head">
            <h4
              className="invisible text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
              aria-hidden="true"
            >
              Employee
            </h4>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto border-t border-gray-200 p-2 dark:border-[#3C3C4C]">
            {canViewEmployeeList && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const onEmployees = activeEmployeeTab === "employees";
                    setActiveEmployeeTab("employees");
                    setEmployeesMenuOpen((prev) => (onEmployees ? !prev : true));
                    setReportsMenuOpen(false);
                    setExportMenuOpen(false);
                    setImportMenuOpen(false);
                    if (!onEmployees) {
                      setShowForm(false);
                      setSelectedEmployee(null);
                    }
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    activeEmployeeTab === "employees" &&
                    !(showForm && !selectedEmployee && !isRejoin)
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Employee
                </button>
                {employeesMenuOpen && (
                  <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                    <Can module="employee" screen="employee_list" action="create">
                      <button
                        type="button"
                        onClick={handleOpenAddEmployeeForm}
                        className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                          activeEmployeeTab === "employees" &&
                          showForm &&
                          !selectedEmployee &&
                          !isRejoin
                            ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                            : darkMode
                            ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                            : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        Add Employee
                      </button>
                    </Can>
                  </div>
                )}
              </>
            )}
            <Can module="employee" screen="employee_list" action="create">
              <>
                <button
                  type="button"
                  onClick={() => {
                    setImportMenuOpen((prev) => !prev);
                    setEmployeesMenuOpen(false);
                    setReportsMenuOpen(false);
                    setExportMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    importMenuOpen || showImportModal
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Import
                </button>
                {importMenuOpen && (
                  <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveEmployeeTab("employees");
                        setImportMenuOpen(true);
                        setEmployeesMenuOpen(false);
                        setReportsMenuOpen(false);
                        setExportMenuOpen(false);
                        setShowImportModal(true);
                      }}
                      className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                        showImportModal
                          ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                          : darkMode
                          ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                          : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Org Details
                    </button>
                  </div>
                )}
              </>
            </Can>
            {canViewEmployeeReports && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setActiveEmployeeTab("reports");
                    setEmployeesMenuOpen(false);
                    setImportMenuOpen(false);
                    setReportsMenuOpen((prev) =>
                      activeEmployeeTab === "reports" ? !prev : true
                    );
                    setExportMenuOpen(false);
                    if (String(activeEmployeeReportsSubTab).startsWith("export-")) {
                      setActiveEmployeeReportsSubTab("employees");
                    }
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    activeEmployeeTab === "reports" &&
                    !String(activeEmployeeReportsSubTab).startsWith("export-")
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Reports
                </button>
                {reportsMenuOpen && (
                  <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                    {[
                      { id: "employees", label: "Employee Report" },
                      { id: "age-report", label: "Age Report" },
                      { id: "service-report", label: "Service Report" },
                      { id: "salary-report", label: "Salary Report" },
                      { id: "confirmation", label: "Confirmation Report" },
                      { id: "transfers", label: "Transfers Report" },
                      { id: "salary-revision", label: "Salary Revision Report" },
                      { id: "resignations", label: "Resignations Report" },
                    ].map((report) => (
                      <button
                        key={report.id}
                        type="button"
                        onClick={() => {
                          setActiveEmployeeTab("reports");
                          setImportMenuOpen(false);
                          setEmployeeReportsSubTabTouched(true);
                          setActiveEmployeeReportsSubTab(report.id);
                          setExportMenuOpen(false);
                        }}
                        className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                          activeEmployeeTab === "reports" &&
                          employeeReportsSubTabTouched &&
                          activeEmployeeReportsSubTab === report.id
                            ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                            : darkMode
                            ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                            : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        {report.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {canExportEmployeeRecords && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setActiveEmployeeTab("reports");
                    setEmployeesMenuOpen(false);
                    setImportMenuOpen(false);
                    setExportMenuOpen((prev) => !prev);
                    setReportsMenuOpen(false);
                    setEmployeeReportsSubTabTouched(true);
                    setActiveEmployeeReportsSubTab(`export-${activeEmployeeExportSubTab}`);
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    activeEmployeeTab === "reports" &&
                    String(activeEmployeeReportsSubTab).startsWith("export-")
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Export
                </button>
                {exportMenuOpen && (
                  <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                    {employeeExportSections.map((section) => (
                      <button
                        key={`export-${section.key}`}
                        type="button"
                        onClick={() => {
                          setActiveEmployeeTab("reports");
                          setImportMenuOpen(false);
                          setEmployeeReportsSubTabTouched(true);
                          setActiveEmployeeReportsSubTab(`export-${section.key}`);
                          setActiveEmployeeExportSubTab(section.key);
                          setSelectedExportEmployeeIds([]);
                        }}
                        className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                          activeEmployeeTab === "reports" &&
                          activeEmployeeReportsSubTab === `export-${section.key}`
                            ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                            : darkMode
                            ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                            : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        {section.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {canOpenEmployeeSettings && (
              <button
                type="button"
                onClick={() => {
                  setShowStructuresModal(true);
                  setEmployeesMenuOpen(false);
                  setImportMenuOpen(false);
                  setReportsMenuOpen(false);
                  setExportMenuOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  showStructuresModal
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Settings
              </button>
            )}
          </div>

        </aside>
        {/* Tab Content — flex sibling; aside width reserved in layout (no pl- offset needed) */}
        <div className="flex min-w-0 flex-1 flex-col">
        {!canViewEmployeeList && !canViewEmployeeReports && (
          <div className="flex h-full items-center justify-center p-6">
            <div
              className={`max-w-md rounded-xl border p-6 text-center ${
                darkMode
                  ? "border-[#3C3C4C] bg-[#1f1f1f] text-gray-300"
                  : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              <h4 className="mb-1 text-sm font-semibold uppercase tracking-wide">
                No employee access
              </h4>
              <p className="text-xs">
                You do not currently have permission to view Employee sections.
                Please contact your administrator.
              </p>
            </div>
          </div>
        )}
        {activeEmployeeTab === "employees" && canViewEmployeeList && (
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white p-4 dark:bg-[#121212]">
          <div className="flex min-h-0 w-full flex-1 flex-col">
            <style>{`
              @keyframes blink-border {
                0%, 100% {
                  border-color: rgb(255 54 54);
                  box-shadow: 0 0 0 0 rgba(255, 54, 54, 0.35);
                }
                50% {
                  border-color: rgb(216 74 70);
                  box-shadow: 0 0 0 4px rgba(255, 54, 54, 0);
                }
              }
            `}</style>
            <div className="mb-3 flex shrink-0 justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowEmployeeInsights((v) => !v);
                  setShowEmployeeDashboard(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
                  darkMode
                    ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
                    : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
                }`}
                title={
                  showEmployeeInsights
                    ? "Back to employee list"
                    : "Flip to insights view"
                }
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {showEmployeeInsights ? "Back" : "Flip Insights"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEmployeeDashboard((v) => !v);
                  setShowEmployeeInsights(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
                  darkMode
                    ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
                    : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
                }`}
                title={
                  showEmployeeDashboard
                    ? "Back to employee list"
                    : "Flip to workforce dashboard"
                }
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                {showEmployeeDashboard ? "Back" : "Flip Dashboard"}
              </button>
            </div>
            {showEmployeeInsights && (
              <ActionInsightsPanel
                darkMode={darkMode}
                title="Employee Action Insights"
                items={employeeInsightItems}
                isFlipped
                showFlipButton={false}
                onFlip={() => setShowEmployeeInsights(false)}
                overlayMode="panel"
                onRowAction={handleEmployeeInsightRowAction}
              />
            )}
            {showEmployeeDashboard && (
              <EmployeeDashboardPanel
                darkMode={darkMode}
                isFlipped
                showFlipButton={false}
                onFlip={() => setShowEmployeeDashboard(false)}
                employees={dashboardEmployees}
                loading={dashboardLoading}
              />
            )}
            {!showEmployeeInsights && !showEmployeeDashboard ? (
            <>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3 bg-white dark:bg-[#121212]">
              <div
                className={`relative z-[100] shrink-0 overflow-visible px-3 pt-2.5 pb-3 ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C] rounded-t-2xl"
                    : "bg-white border-[#165b5d]/30 rounded-t-2xl"
                } border shadow-sm`}
              >
                <div className="mt-2.5 flex flex-wrap items-center gap-2.5 overflow-visible">
                  <div className="relative w-full min-w-[240px] max-w-md shrink-0">
                    <div className="relative">
                      <Search
                        className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${
                          darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                        } w-3.5 h-3.5`}
                      />

                      <input
                        ref={searchInputRef}
                        type="text"
                        name="employee_filter_query"
                        id="admin-employee-list-filter"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        data-lpignore="true"
                        placeholder="Search by employee code, name, or mobile number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          // Trigger immediate search on Enter
                          if (e.key === "Enter") {
                            setDebouncedSearchTerm(
                              typeof searchTerm === "string"
                                ? searchTerm.trim()
                                : searchTerm
                            );
                          }
                        }}
                        className={`w-full rounded-full border py-2 pl-8 pr-8 text-xs transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-redhalo-red/30 ${
                          darkMode
                            ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0] focus:border-redhalo-red"
                            : "border-[#f3d6bf] bg-white text-gray-800 focus:border-[#ef6f2e] hover:border-[#ef6f2e]"
                        } shadow-sm`}
                      />

                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            setDebouncedSearchTerm("");
                          }}
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                            darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-700"
                          }`}
                          aria-label="Clear search"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="ml-auto flex flex-nowrap items-center gap-2.5 shrink-0 overflow-visible">
                    <div
                      className={`relative dropdown-container ${
                        statusDropdownOpen ? "z-[9999]" : "z-[1]"
                      }`}
                    >
                        <button
                          type="button"
                          onClick={() => handleDropdownToggle("status")}
                          className={employeeListFilterBtnClass}
                        >
                          {getStatusDisplayText()}

                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-gray-400">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </button>

                        {statusDropdownOpen && (
                          <div
                            className={employeeListFilterMenuClass}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              {selectedStatuses.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedStatuses([])}
                                  className={`w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b ${
                                    darkMode
                                      ? "hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[#3C3C3C]"
                                      : "hover:bg-gray-100 text-red-600 hover:text-red-700 border-gray-200"
                                  }`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Clear
                                </button>
                              )}

                              {statusOptions.map((status) => (
                                <label
                                  key={status}
                                  className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                                    darkMode
                                      ? "hover:bg-[#3C3C3C] text-[#E0E0E0]"
                                      : "hover:bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedStatuses.includes(status)}
                                    onChange={() => handleStatusChange(status)}
                                    className={`w-4 h-4 mr-2 rounded-md border-2 ${
                                      darkMode
                                        ? "bg-[#2C2C2C] border-[#3C3C3C] text-redhalo-red"
                                        : "bg-white border-gray-300 text-redhalo-navy"
                                    } focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-0 cursor-pointer`}
                                  />
                                  <span className="flex-1 cursor-pointer">
                                    {status}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>

                    <div
                      className={`relative dropdown-container ${
                        departmentDropdownOpen ? "z-[9999]" : "z-[1]"
                      }`}
                    >
                        <button
                          type="button"
                          onClick={() => handleDropdownToggle("department")}
                          className={employeeListFilterBtnClass}
                        >
                          {getDepartmentDisplayText()}

                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-gray-400">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </button>

                        {departmentDropdownOpen && (
                          <div
                            className={employeeListFilterMenuClass}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              <button
                                type="button"
                                onClick={() => setSelectedDepartments([])}
                                disabled={selectedDepartments.length === 0}
                                className={employeeListFilterClearRowClass}
                              >
                                <X className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                                Clear
                              </button>

                              {departmentsLoading ? (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  Loading departments...
                                </div>
                              ) : departmentsList.length === 0 ? (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No departments found
                                </div>
                              ) : (
                                departmentsList.map((dept) => (
                                  <label
                                    key={dept.id}
                                    className={employeeListFilterOptionClass}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedDepartments.includes(
                                        dept.id
                                      )}
                                      onChange={() =>
                                        handleDepartmentChange(dept.id)
                                      }
                                      className={employeeListFilterCheckboxClass}
                                    />
                                    <span className="flex-1 cursor-pointer">
                                      {dept.name}
                                    </span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                    {departmentsList.length === 0 && !departmentsLoading && (
                      <button
                        type="button"
                        onClick={refreshDepartments}
                        className={`px-2 py-1 text-sm rounded-none border ${
                          darkMode
                            ? "bg-redhalo-navy text-redhalo-mint border-[#3C3C3C] hover:bg-redhalo-navy-light"
                            : "rh-btn-primary border-gray-300"
                        } transition-colors duration-200`}
                        title="Refresh departments"
                      >
                        ↻
                      </button>
                    )}

                    <div
                      className={`relative dropdown-container ${
                        employmentTypeDropdownOpen ? "z-[9999]" : "z-[1]"
                      }`}
                    >
                        <button
                          type="button"
                          onClick={() => handleDropdownToggle("employmentType")}
                          className={employeeListFilterBtnClass}
                        >
                          {getEmploymentTypeDisplayText()}

                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-gray-400">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </button>

                        {employmentTypeDropdownOpen && (
                          <div
                            className={employeeListFilterMenuClass}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              <button
                                type="button"
                                onClick={() => setSelectedEmploymentTypes([])}
                                disabled={selectedEmploymentTypes.length === 0}
                                className={employeeListFilterClearRowClass}
                              >
                                <X className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                                Clear
                              </button>

                              {employmentTypeOptions.map((type) => (
                                <label
                                  key={type}
                                  className={employeeListFilterOptionClass}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedEmploymentTypes.includes(type)}
                                    onChange={() => handleEmploymentTypeChange(type)}
                                    className={employeeListFilterCheckboxClass}
                                  />
                                  <span className="flex-1 cursor-pointer">{type}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setViewMode(viewMode === "list" ? "grid" : "list")
                      }
                      className={`rounded-md border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                        darkMode
                          ? "border-[#4C4C4C] bg-[#2C2C2C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                          : "border-gray-300 bg-white text-gray-800 hover:bg-redhalo-mint/40"
                      }`}
                      title={`Switch to ${
                        viewMode === "list" ? "Grid" : "List"
                      } View`}
                    >
                      {viewMode === "list" ? (
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>

                    <div className="flex items-center space-x-2.5">
                      <div className="flex items-center space-x-1.5">
                        <label
                          className={`text-xs font-medium ${
                            darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                          }`}
                        >
                          Show:
                        </label>
                        <select
                          value={itemsPerPage}
                          onChange={(e) =>
                            setItemsPerPage(
                              e.target.value === "all"
                                ? "all"
                                : parseInt(e.target.value)
                            )
                          }
                          className={`px-2 py-1.5 border rounded-md text-xs font-medium ${
                            darkMode
                              ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                              : "bg-white/95 border-[#f3d6bf] text-gray-900 focus:border-[#ef6f2e]"
                          } transition-all duration-200 focus:outline-none shadow-sm`}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value="all">All</option>
                        </select>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowFilterPopup(true);
                          }}
                          className={`flex items-center justify-center rh-btn-primary text-sm px-3 py-1.5 transition-all ${
                            hasActiveFilters
                              ? "ring-2 ring-redhalo-red ring-offset-1 dark:ring-offset-[#1C1C1C]"
                              : ""
                          }`}
                          style={
                            hasActiveFilters
                              ? { animation: "blink-border 1.5s ease-in-out infinite" }
                              : undefined
                          }
                          title="Filters"
                          aria-label="Filters"
                        >
                          <Filter className="w-4 h-4" aria-hidden />
                        </button>

                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`mt-0.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl ${
                  darkMode ? "border border-[#3C3C3C]" : "border border-[#165b5d]/30"
                }`}
              >
                  <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    {/* List View */}
                    {viewMode === "list" && (
                      <div className="flex min-h-0 flex-1 flex-col">
                        {/* Table container with max height for exactly 10 employee rows visible at any time
                            - Header row (sticky) + 10 employee rows = 11 rows total max height
                            - When more than 10 employees exist, table body scrolls internally
                            - Page scrolling is prevented, only table scrolls */}
                        <div
                          ref={employeeTableScrollRef}
                          className={`employee-table-scroll-area overflow-y-auto overflow-x-auto relative flex-1 min-h-0 ${
                            darkMode ? "employee-table-scroll-area-dark" : ""
                          } ${employeeTableScrolling ? "is-scrolling" : ""}`}
                          style={{
                            maxHeight: `${employeeTableScrollHeightPx}px`,
                          }}
                          onScroll={handleEmployeeTableScroll}
                        >
                          <table
                            className={`border-collapse text-sm leading-tight table-fixed employee-table ${
                              darkMode ? "employee-table-dark" : ""
                            } ${
                              darkMode
                                ? "bg-[#2C2C2C] text-[#E0E0E0]"
                                : "bg-[#fafafa] text-gray-800"
                            }`}
                            style={{ minWidth: `${employeeTableMinWidth}px`, width: "100%" }}
                          >
                            <thead
                            className={`sticky top-0 z-40 border-b ${
                                darkMode
                                  ? "border-[#4C4C4C] bg-[#3a3a3a]"
                                : "border-[#d7dee8] bg-[#e6eaef]"
                              }`}
                            >
                              <tr style={{ height: `${rowHeight}px` }}>
                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableFrozenClass("actions", employeeTableStickyLeft)}`}
                                  style={{
                                    width: `${columnWidths.actions}px`,
                                    ...employeeTableFrozenStyle("actions", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: true,
                                    }),
                                  }}
                                >
                                  Actions
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.actions;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("actions", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableFrozenClass("photo", employeeTableStickyLeft)}`}
                                  style={{
                                    width: `${columnWidths.photo}px`,
                                    ...employeeTableFrozenStyle("photo", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: true,
                                    }),
                                  }}
                                >
                                  Photo
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.photo;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("photo", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>
                                {/* //hello */}
                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableFrozenClass("slNo", employeeTableStickyLeft)}`}
                                  style={{
                                    width: `${columnWidths.slNo}px`,
                                    ...employeeTableFrozenStyle("slNo", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: true,
                                    }),
                                  }}
                                >
                                  Sl No
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.slNo;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("slNo", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>
                                {/* //hello end */}
                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableThSortClass("empCode")} ${employeeTableFrozenClass("empCode", employeeTableStickyLeft)}`}
                                  style={{
                                    width: `${columnWidths.empCode}px`,
                                    ...employeeTableFrozenStyle("empCode", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: true,
                                    }),
                                  }}
                                  onClick={() => handleSort("empCode")}
                                  title="Sort by Emp Code (click to toggle ascending / descending)"
                                >
                                  Emp Code
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.empCode;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("empCode", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableThSortClass("name")} ${employeeTableFrozenClass("name", employeeTableStickyLeft, true)}`}
                                  style={{
                                    width: `${columnWidths.name}px`,
                                    ...employeeTableFrozenStyle("name", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: true,
                                    }),
                                  }}
                                  onClick={() => handleSort("name")}
                                  title="Sort by Name (click to toggle ascending / descending)"
                                >
                                  Name
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.name;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("name", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative ${employeeTableThSortClass("designation")}`}
                                  style={{
                                    width: `${columnWidths.designation}px`,
                                  }}
                                  onClick={() => handleSort("designation")}
                                  title="Sort by Designation (click to toggle ascending / descending)"
                                >
                                  Designation
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const startX = e.clientX;
                                      const startWidth = columnWidths.designation;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize("designation", newWidth);
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("department")}`}
                                  style={{
                                    width: `${columnWidths.department}px`,
                                  }}
                                  onClick={() => handleSort("department")}
                                  title="Sort by Department (click to toggle ascending / descending)"
                                >
                                  Department
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("branch")}`}
                                  style={{
                                    width: `${columnWidths.branch}px`,
                                  }}
                                  onClick={() => handleSort("branch")}
                                  title="Sort by Branch (click to toggle ascending / descending)"
                                >
                                  Branch
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("dateOfJoin")}`}
                                  style={{ width: `${columnWidths.doj}px` }}
                                  onClick={() => handleSort("dateOfJoin")}
                                  title="Sort by Date of Join (click to toggle ascending / descending)"
                                >
                                  DOJ
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("dateOfRelieving")}`}
                                  style={{ width: `${columnWidths.dol}px` }}
                                  onClick={() => handleSort("dateOfRelieving")}
                                  title="Sort by Date of Leaving (click to toggle ascending / descending)"
                                >
                                  DOL
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("mobile")}`}
                                  style={{
                                    width: `${columnWidths.mobile}px`,
                                  }}
                                  onClick={() => handleSort("mobile")}
                                  title="Sort by Mobile (click to toggle ascending / descending)"
                                >
                                  Mobile
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("email")}`}
                                  style={{
                                    width: `${columnWidths.email}px`,
                                  }}
                                  onClick={() => handleSort("email")}
                                  title="Sort by Email (click to toggle ascending / descending)"
                                >
                                  Email
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("employmentType")}`}
                                  style={{
                                    width: `${columnWidths.employmentType}px`,
                                  }}
                                  onClick={() => handleSort("employmentType")}
                                  title="Sort by Employment Type (click to toggle ascending / descending)"
                                >
                                  Employment Type
                                </th>

                                <th
                                  className={`px-2 py-2 text-center font-bold text-xs uppercase tracking-wide ${employeeTableThSortClass("status")}`}
                                  style={{
                                    width: `${columnWidths.status}px`,
                                  }}
                                  onClick={() => handleSort("status")}
                                  title="Sort by Status (click to toggle ascending / descending)"
                                >
                                  Status
                                </th>

                                {/* <th
                                  className="px-2 py-2 text-center font-bold text-xs uppercase tracking-wide relative"
                                  style={{
                                    width: `${columnWidths.profileCompletion}px`,
                                  }}
                                >
                                  Profile %
                                  <div
                                    className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-redhalo-mint/500 opacity-0 hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      const startX = e.clientX;
                                      const startWidth =
                                        columnWidths.profileCompletion;
                                      const handleMouseMove = (e) => {
                                        const newWidth =
                                          startWidth + (e.clientX - startX);
                                        handleColumnResize(
                                          "profileCompletion",
                                          newWidth
                                        );
                                      };
                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          "mouseup",
                                          handleMouseUp
                                        );
                                      };
                                      document.addEventListener(
                                        "mousemove",
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </th> */}
                              </tr>
                            </thead>
                            <tbody
                              className={`${
                                darkMode
                                  ? hasActiveFilters
                                    ? "divide-redhalo-navy"
                                    : "divide-[#4C4C4C]"
                                  : hasActiveFilters
                                    ? "divide-gray-300"
                                    : "divide-gray-200"
                              }`}
                            >
                              {displayEmployees.map((employee, index) => (
                                <tr
                                  key={employee.id}
                                  className={`${
                                    darkMode
                                      ? hasActiveFilters
                                        ? "border-redhalo-navy"
                                        : "border-[#4C4C4C]"
                                      : hasActiveFilters
                                        ? "border-gray-300"
                                        : "border-[#e4e8ee]"
                                  } employee-table-row border-b ${
                                    darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]"
                                  } cursor-pointer transition-colors duration-150`}
                                  style={{ height: `${rowHeight}px` }}
                                  onDoubleClick={() => {
                                    if (!canEditEmployeeRecords) {
                                      toast.error(
                                        "You do not have permission to edit employees."
                                      );
                                      return;
                                    }
                                    handleEdit(employee);
                                  }}
                                >
                                  <td
                                    className={`px-2 py-2 text-center font-normal align-middle ${employeeTableFrozenClass("actions", employeeTableStickyLeft)}`}
                                    style={employeeTableFrozenStyle("actions", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: false,
                                    })}
                                  >
                                    <div className="relative action-dropdown">
                                      {canOpenAnyRowAction ? (
                                      <button
                                        ref={(el) => {
                                          if (el)
                                            actionButtonRefs.current[
                                              employee.id
                                            ] = el;
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const button = e.currentTarget;
                                          const rect =
                                            button.getBoundingClientRect();
                                          if (
                                            openActionDropdown === employee.id
                                          ) {
                                            setOpenActionDropdown(null);
                                          } else {
                                            setOpenActionDropdown(employee.id);
                                            setSelectedEmployee(employee);
                                            setDropdownPosition(calculateDropdownPosition(rect));
                                          }
                                        }}
                                        className={`w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center ${
                                          darkMode
                                            ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                                            : "bg-gray-100 hover:bg-gray-200"
                                        }`}
                                        title="Employee Actions"
                                      >
                                        <MoreVertical className="w-4 h-4" />
                                      </button>
                                      ) : (
                                        <span
                                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-[10px] ${
                                            darkMode
                                              ? "border-[#3C3C3C] bg-[#2C2C2C] text-gray-500"
                                              : "border-gray-300 bg-gray-100 text-gray-400"
                                          }`}
                                          title="View-only access"
                                        >
                                          -
                                        </span>
                                      )}
                                    </div>
                                  </td>

                                  <td
                                    className={`px-2 py-2 text-center font-normal align-middle ${employeeTableFrozenClass("photo", employeeTableStickyLeft)}`}
                                    style={employeeTableFrozenStyle("photo", employeeTableStickyLeft, {
                                      darkMode,
                                      isHeader: false,
                                    })}
                                  >
                                    <div
                                      className="w-8 h-8 rounded-full overflow-hidden mx-auto cursor-pointer hover:scale-110 transition-transform duration-200"
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        handlePhotoClick(employee);
                                      }}
                                      title="Click to view photo"
                                    >
                                      {(() => {
                                        const photoSrc = getEmployeePhotoSrc(employee);
                                        if (photoSrc) {
                                          return (
                                            <img
                                              src={photoSrc}
                                              alt={`${
                                                employee.user?.name ||
                                                "Employee Avatar"
                                              }`}
                                              className="w-full h-full object-cover"
                                            />
                                          );
                                        }

                                        return (
                                          <div
                                            className={`w-full h-full ${
                                              darkMode
                                                ? "bg-redhalo-navy text-redhalo-mint"
                                                : "bg-gray-300 text-gray-600"
                                            } flex items-center justify-center text-sm font-normal`}
                                          >
                                            {employee.user?.name
                                              ?.charAt(0)
                                              ?.toUpperCase() || "E"}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </td>

                                  <EmployeeTableTextCell
                                    align="center"
                                    title={String(index + 1)}
                                    columnKey="slNo"
                                    stickyLeftMap={employeeTableStickyLeft}
                                    darkMode={darkMode}
                                  >
                                    {index + 1}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    title={employee.empCode || ""}
                                    columnKey="empCode"
                                    stickyLeftMap={employeeTableStickyLeft}
                                    darkMode={darkMode}
                                  >
                                    {employee.empCode}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="left"
                                    title={toTitleCase(
                                      `${employee.user?.name || ""} ${employee.user?.lastName || ""}`.trim()
                                    )}
                                    columnKey="name"
                                    stickyLeftMap={employeeTableStickyLeft}
                                    darkMode={darkMode}
                                  >
                                    {toTitleCase(
                                      `${employee.user?.name || ""} ${employee.user?.lastName || ""}`.trim()
                                    )}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="left"
                                    columnKey="designation"
                                    truncate
                                    darkMode={darkMode}
                                    title={
                                      employee.designation?.name ||
                                      getNameByIdFromList(
                                        employee.designationId,
                                        designationsList,
                                        employee,
                                        "designation"
                                      ) ||
                                      ""
                                    }
                                  >
                                    {employee.designation?.name ||
                                      getNameByIdFromList(
                                        employee.designationId,
                                        designationsList,
                                        employee,
                                        "designation"
                                      )}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="left"
                                    truncate
                                    title={
                                      employee.department?.name ||
                                      getNameByIdFromList(
                                        employee.departmentId,
                                        departmentsList,
                                        employee,
                                        "department"
                                      ) ||
                                      ""
                                    }
                                  >
                                    {employee.department?.name ||
                                      getNameByIdFromList(
                                        employee.departmentId,
                                        departmentsList,
                                        employee,
                                        "department"
                                      )}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="left"
                                    truncate
                                    title={
                                      getNameByIdFromList(
                                        employee.branchId,
                                        branchesList,
                                        employee,
                                        "branch"
                                      ) || ""
                                    }
                                  >
                                    {getNameByIdFromList(
                                      employee.branchId,
                                      branchesList,
                                      employee,
                                      "branch"
                                    )}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    columnKey="doj"
                                    darkMode={darkMode}
                                    title={formatDate(
                                      employee.dateOfJoin ||
                                        employee.joiningDate ||
                                        employee.joinDate
                                    )}
                                  >
                                    {formatDate(
                                      employee.dateOfJoin ||
                                        employee.joiningDate ||
                                        employee.joinDate
                                    )}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    columnKey="dol"
                                    darkMode={darkMode}
                                    title={
                                      formatDate(getEmployeeRelievingDate(employee)) || "—"
                                    }
                                  >
                                    {formatDate(getEmployeeRelievingDate(employee)) || "—"}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    columnKey="mobile"
                                    darkMode={darkMode}
                                    title={employee.user?.phone || "N/A"}
                                  >
                                    {employee.user?.phone || "N/A"}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="left"
                                    columnKey="email"
                                    darkMode={darkMode}
                                    title={
                                      typeof employee.email === "object" &&
                                      employee.email !== null
                                        ? employee.email.name
                                        : employee.email || "N/A"
                                    }
                                  >
                                    {typeof employee.email === "object" &&
                                    employee.email !== null
                                      ? employee.email.name
                                      : employee.email || "N/A"}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    truncate
                                    title={employee.employmentType || ""}
                                  >
                                    {employee.employmentType}
                                  </EmployeeTableTextCell>

                                  <EmployeeTableTextCell
                                    align="center"
                                    truncate
                                    title={employee.employmentStatus || ""}
                                  >
                                    <span
                                      className={`inline-flex rounded-md px-2 py-1 text-xs font-normal whitespace-nowrap ${
                                        employee.employmentStatus === "Probation"
                                          ? "bg-amber-100 text-amber-900 ring-1 ring-amber-200/80"
                                          : employee.employmentStatus === "Confirmed"
                                          ? "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/80"
                                          : employee.employmentStatus === "Resigned"
                                          ? "bg-rose-100 text-rose-900 ring-1 ring-rose-200/80"
                                          : employee.employmentStatus === "Relieved"
                                          ? "bg-redhalo-mint/90 text-redhalo-navy ring-1 ring-redhalo-mint/50"
                                          : "bg-gray-100 text-gray-800 ring-1 ring-gray-200/80 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600"
                                      }`}
                                    >
                                      {employee.employmentStatus}
                                    </span>
                                  </EmployeeTableTextCell>

                                  {/* <td
                                    className="px-2 py-2 text-center font-normal align-middle"
                                    style={{
                                      width: `${columnWidths.profileCompletion}px`,
                                    }}
                                  >
                                    <div className="flex items-center justify-center">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const completionData =
                                            calculateProfileCompletion(
                                              employee
                                            );
                                          setProfileCompletionData({
                                            employee: employee,
                                            percentage:
                                              completionData.percentage,
                                            pendingSections:
                                              completionData.pendingSections,
                                          });
                                          setShowProfileCompletionModal(true);
                                        }}
                                        className={`${
                                          calculateProfileCompletion(employee)
                                            .percentage === 100
                                            ? "w-9 h-9"
                                            : "w-10 h-10"
                                        } rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 hover:scale-105 cursor-pointer ${
                                          calculateProfileCompletion(employee)
                                            .percentage >= 80
                                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                            : calculateProfileCompletion(
                                                employee
                                              ).percentage >= 60
                                            ? "bg-amber-500 text-white hover:bg-amber-600"
                                            : "bg-redhalo-red text-white hover:opacity-90"
                                        }`}
                                        title="Click to view profile completion details"
                                      >
                                        {
                                          calculateProfileCompletion(employee)
                                            .percentage
                                        }
                                        %
                                      </button>
                                    </div>
                                  </td> */}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* End of scrollable table container */}

                        {/* Summary bar — pinned to bottom of list card */}
                        <div className="mt-auto shrink-0 z-40">
                          <div
                            className={`py-2 px-3 ${
                              darkMode
                                ? "bg-gradient-to-r from-[#252525] via-[#2C2C2C] to-[#333]"
                                : "bg-gradient-to-r from-redhalo-mint/30 via-white to-white"
                            } border-t ${
                              darkMode ? "border-[#4C4C4C]" : "border-gray-200"
                            } shadow-lg`}
                          >
                            <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                              <div
                                className={`text-sm font-medium ${
                                  darkMode ? "text-[#A6A9C8]" : "text-gray-700"
                                }`}
                              >
                                Showing{" "}
                                <span
                                  className={`font-bold ${
                                    darkMode
                                      ? "text-redhalo-mint"
                                      : "text-redhalo-navy"
                                  }`}
                                >
                                  {displayEmployees.length}
                                </span>{" "}
                                of{" "}
                                <span
                                  className={`font-bold ${
                                    darkMode
                                      ? "text-redhalo-mint"
                                      : "text-redhalo-navy"
                                  }`}
                                >
                                  {totalEmployees}
                                </span>{" "}
                                entries
                              </div>

                              <div className="flex items-center space-x-2">
                                {/* Pagination Buttons */}
                                {/* <Pagination
                                  currentPage={page}
                                  totalPages={
                                    Math.ceil(
                                      totalEmployees /
                                        (itemsPerPage === "all"
                                          ? totalEmployees
                                          : itemsPerPage)
                                    ) || 1
                                  }
                                  onPageChange={setPage}
                                  totalItems={totalEmployees}
                                  itemsPerPage={itemsPerPage}
                                  darkMode={darkMode}
                                /> */}

                                
                                
                                {/* Filtered Indicator - MUST match the same logic used for blue divider lines */}
                                {hasActiveFilters && (
                                  <div
                                    className={`px-2.5 py-1 rounded-md text-sm font-semibold ${
                                      darkMode
                                        ? "bg-red-600 text-white"
                                        : "bg-red-500 text-white"
                                    }`}
                                  >
                                    Filtered
                                  </div>
                                )}


                                {/* Total Employees */}
                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-redhalo-navy text-redhalo-mint"
                                      : "rh-accent-soft"
                                  }`}
                                >
                                  Total: {statusCounts.total}
                                </div>


                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-amber-800 text-amber-100"
                                      : "bg-amber-100 text-amber-900 ring-1 ring-amber-200/80"
                                  }`}
                                >
                                  Probation: {statusCounts.probation}
                                </div>

                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-emerald-800 text-emerald-100"
                                      : "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/80"
                                  }`}
                                >
                                  Confirmed: {statusCounts.confirmed}
                                </div>

                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-rose-800 text-rose-100"
                                      : "bg-rose-100 text-rose-900 ring-1 ring-rose-200/80"
                                  }`}
                                >
                                  Resigned: {statusCounts.resigned}
                                </div>

                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-redhalo-navy text-redhalo-mint"
                                      : "rh-accent-soft ring-1 ring-redhalo-mint/50"
                                  }`}
                                >
                                  Relieved: {statusCounts.relieved}
                                </div>

                                <div
                                  className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                                    darkMode
                                      ? "bg-gray-600 text-white"
                                      : "bg-gray-100 text-gray-800 ring-1 ring-gray-200/80"
                                  }`}
                                >
                                  Terminated: {statusCounts.terminated}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Grid View */}
                    {viewMode === "grid" && (
                      <div
                        className="overflow-y-auto"
                        style={{ height: `calc(92vh - 290px)` }}
                      >
                        <div className="grid grid-cols-4 gap-3 p-3">
                          {displayEmployees.map((employee, index) => (
                            <div
                              key={employee.id}
                              className={`cursor-pointer rounded-lg border p-3 shadow-sm transition-all duration-200 hover:shadow-md ${
                                darkMode
                                  ? "border-[#4C4C4C] bg-[#2C2C2C] hover:bg-[#2e2e2e]"
                                  : "border-gray-200 bg-white hover:bg-redhalo-mint/40"
                              }`}
                              onClick={() => {
                                setSelectedEmployee(employee);
                              }}
                              onDoubleClick={() => {
                                if (!canEditEmployeeRecords) {
                                  toast.error(
                                    "You do not have permission to edit employees."
                                  );
                                  return;
                                }
                                handleEdit(employee);
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                {/* Left Section - Photo */}
                                <div className="flex-shrink-0">
                                  {(() => {
                                    const photoSrc = getEmployeePhotoSrc(employee);
                                    if (photoSrc) {
                                      return (
                                        <div className="w-12 h-12 rounded-md overflow-hidden">
                                          <img
                                            src={photoSrc}
                                            alt={`${employee.user?.name || "Employee"}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        className={`w-12 h-12 rounded-md ${
                                          darkMode
                                            ? "bg-redhalo-navy text-redhalo-mint"
                                            : "bg-gray-300 text-gray-600"
                                        } flex items-center justify-center text-lg font-semibold`}
                                      >
                                        {employee.user?.name
                                          ?.charAt(0)
                                          ?.toUpperCase() || "E"}
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Right Section - Details */}
                                <div className="flex-1 min-w-0">
                                  <div className="space-y-1">
                                    <p
                                      className={`text-sm ${
                                        darkMode
                                          ? "text-white"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      <span className="font-medium">
                                        Emp Code:
                                      </span>{" "}
                                      {employee.empCode}
                                    </p>
                                    <p
                                      className={`text-sm ${
                                        darkMode
                                          ? "text-gray-300"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      <span className="font-medium">Name:</span>{" "}
                                      {`${employee.user.name} ${
                                        employee.user.lastName || ""
                                      }`}
                                    </p>
                                    <p
                                      className={`text-sm ${
                                        darkMode
                                          ? "text-gray-400"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      <span className="font-medium">
                                        Designation:
                                      </span>{" "}
                                      {getNameByIdFromList(
                                        employee.designationId,
                                        designationsList,
                                        employee,
                                        'designation'
                                      )}
                                    </p>
                                    {/* <div className="flex items-center justify-between mt-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const completionData =
                                            calculateProfileCompletion(
                                              employee
                                            );
                                          setProfileCompletionData({
                                            employee: employee,
                                            percentage:
                                              completionData.percentage,
                                            pendingSections:
                                              completionData.pendingSections,
                                          });
                                          setShowProfileCompletionModal(true);
                                        }}
                                        className={`${
                                          calculateProfileCompletion(employee)
                                            .percentage === 100
                                            ? "w-5 h-5"
                                            : "w-6 h-6"
                                        } rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 hover:scale-105 cursor-pointer ${
                                          calculateProfileCompletion(employee)
                                            .percentage >= 80
                                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                            : calculateProfileCompletion(
                                                employee
                                              ).percentage >= 60
                                            ? "bg-amber-500 text-white hover:bg-amber-600"
                                            : "bg-redhalo-red text-white hover:opacity-90"
                                        }`}
                                        title="Click to view profile completion details"
                                      >
                                        {
                                          calculateProfileCompletion(employee)
                                            .percentage
                                        }
                                        %
                                      </button>
                                    </div> */}
                                    <div className="mt-2">
                                      <span
                                        className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${
                                          employee.employmentStatus ===
                                          "Probation"
                                            ? "bg-amber-100 text-amber-900 ring-1 ring-amber-200/80 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-700/50"
                                            : employee.employmentStatus ===
                                              "Confirmed"
                                            ? "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/80 dark:bg-emerald-900/40 dark:text-emerald-100 dark:ring-emerald-700/50"
                                            : employee.employmentStatus ===
                                              "Resigned"
                                            ? "bg-rose-100 text-rose-900 ring-1 ring-rose-200/80 dark:bg-rose-900/40 dark:text-rose-100 dark:ring-rose-700/50"
                                            : employee.employmentStatus ===
                                              "Relieved"
                                            ? "bg-redhalo-mint/90 text-redhalo-navy ring-1 ring-redhalo-mint/50 dark:bg-redhalo-navy/80 dark:text-redhalo-mint dark:ring-redhalo-navy"
                                            : "bg-gray-100 text-gray-800 ring-1 ring-gray-200/80 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600"
                                        }`}
                                      >
                                        {employee.employmentStatus}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
            ) : null}

            {/* ) : (

              <EmployeeProfileDetails

                employee={selectedEmployee}

                onSubmit={handleSubmit}

                onCancel={() => setShowForm(false)}

                darkMode={darkMode}

                departments={departmentsList}

                designations={designationsList}

                branches={branchesList}

              />

            )} */}

            {showImportModal && (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                <div
                  className={`mx-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl p-8 shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold">
                      Import Employee Data
                    </h2>

                    <button
                      type="button"
                      onClick={() => setShowImportModal(false)}
                      className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-[#3C3C3C] dark:hover:text-gray-300"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {/* Warning Section */}
                  <div
                    className={`mb-6 rounded-md border-l-4 p-4 ${
                      darkMode
                        ? "border-amber-500 bg-amber-900/25 text-amber-100"
                        : "border-amber-400 bg-amber-50 text-amber-950"
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>

                      <div className="ml-3">
                        <h3 className="text-sm font-medium">
                          Important Import Guidelines
                        </h3>

                        <div className="mt-2 text-sm">
                          <ul className="list-disc list-inside space-y-1">
                            <li>
                              Download the template first to understand the
                              required format
                            </li>

                            <li>
                              Ensure data follows the validation rules specified
                              in the template
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-4">
                      Upload Employee Data
                    </h3>

                    <div
                      className={`rounded-md border-2 border-dashed p-8 text-center ${
                        darkMode
                          ? "border-[#4C4C4C] bg-[#1f1f1f]/80"
                          : "border-redhalo-teal/35 bg-gray-panel"
                      }`}
                    >
                      <div className="space-y-4">
                        {/* <div className="flex justify-center">
                              <button
                                onClick={() => {
                                  console.log(
                                    "Test button clicked - setting test import results"
                                  );
                                  setImportResults({
                                    success: false,
                                    summary: {
                                      total: 2,
                                      successful: 1,
                                      failed: 1,
                                      errors: [],
                                    },
                                    successfulRecords: [
                                      {
                                        empCode: "EMP001",
                                        name: "Test User",
                                        email: "test@example.com",
                                        department: "IT",
                                      },
                                    ],
                                    failedRecords: [
                                      {
                                        row: 2,
                                        name: "Failed User",
                                        employeeCode: "EMP002",
                                        email: "failed@example.com",
                                        error: "Email already exists",
                                        errorType: "duplicate_data",
                                        fieldErrors: {
                                          email: "Email already registered",
                                        },
                                        suggestions: [
                                          "Use a different email address",
                                        ],
                                      },
                                    ],
                                  });
                                  setShowImportResultsModal(true);
                                }}
                                className="px-4 py-2 rh-btn-primary rounded-md transition-colors text-sm"
                              >
                                Test Import Results Modal
                              </button>
                            </div> */}

                        <div className="flex justify-center">
                          <svg
                            className="h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>

                        <div>
                          <label
                            htmlFor="file-upload"
                            className="cursor-pointer"
                          >
                            <span
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${
                                darkMode
                                  ? "bg-redhalo-navy text-redhalo-mint hover:bg-redhalo-navy-light"
                                  : "rh-btn-primary hover:opacity-95"
                              } transition-colors duration-200`}
                            >
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              Choose File to Upload
                            </span>

                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              accept=".csv,.xlsx"
                              onChange={handleFileSelect}
                              className="sr-only"
                              disabled={importing}
                            />
                          </label>

                          <p className="mt-2 text-sm text-gray-500">
                            or drag and drop your file here
                          </p>
                        </div>

                        <p className="text-sm text-gray-500">
                          Excel (.xlsx) or CSV (.csv) files up to 10MB
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Template Download Section */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-4">
                      Download Template
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {["org"].map((section) => (
                        <div
                          key={section}
                          className={`p-4 rounded-md border ${
                            darkMode
                              ? "border-gray-600 bg-gray-800/50"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium capitalize">
                                {section} Details
                              </h4>

                              <p className="text-sm text-gray-500 mt-1">
                                Comprehensive template with all fields and
                                validations
                              </p>
                            </div>

                            <button
                              onClick={() => handleDownloadTemplate(section)}
                              className={`px-4 py-2 text-sm rounded-md ${
                                darkMode
                                  ? "bg-redhalo-navy text-redhalo-mint hover:bg-redhalo-navy-light"
                                  : "rh-btn-primary"
                              } transition-colors duration-200`}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {importing && (
                    <div className="mt-6 flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-redhalo-red"></div>

                      <span className="ml-3 text-lg">Processing import...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import Confirmation Modal */}
            {showImportConfirmationModal && selectedImportFile && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-md w-full mx-4 ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Confirm Import</h2>
                    <button
                      onClick={() => {
                        setShowImportConfirmationModal(false);
                        setSelectedImportFile(null);
                        setSelectedImportSection(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="mb-6">
                    <div
                      className={`p-4 rounded-md border-l-4 ${
                        darkMode
                          ? "bg-redhalo-navy/25 border-redhalo-red text-redhalo-mint"
                          : "bg-redhalo-mint/50 border-redhalo-teal/80 text-redhalo-navy"
                      }`}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium">
                            Ready to Import
                          </h3>
                          <div className="mt-2 text-sm">
                            <p>
                              <strong>File:</strong> {selectedImportFile.name}
                            </p>
                            <p>
                              <strong>Size:</strong>{" "}
                              {(selectedImportFile.size / 1024 / 1024).toFixed(
                                2
                              )}{" "}
                              MB
                            </p>
                            <p>
                              <strong>Type:</strong>{" "}
                              {selectedImportFile.type || "Unknown"}
                            </p>
                            {selectedImportSection && (
                              <p>
                                <strong>Section:</strong>{" "}
                                {selectedImportSection.charAt(0).toUpperCase() +
                                  selectedImportSection.slice(1)}{" "}
                                Details
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`mb-6 rounded-md border-l-4 p-4 ${
                      darkMode
                        ? "border-amber-500 bg-amber-900/25 text-amber-100"
                        : "border-amber-400 bg-amber-50 text-amber-950"
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium">Important Notes</h3>
                        <div className="mt-2 text-sm">
                          <ul className="list-disc list-inside space-y-1">
                            <li>
                              Invalid records will be skipped with detailed
                              error messages
                            </li>
                            <li>
                              You will receive a detailed report after import
                              completion
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowImportConfirmationModal(false);
                        setSelectedImportFile(null);
                        setSelectedImportSection(null);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-md ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "bg-gray-200 text-gray-900 hover:bg-gray-300"
                      } transition-colors duration-200`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={
                        selectedImportSection
                          ? confirmSectionImport
                          : confirmImport
                      }
                      disabled={importing}
                      type="button"
                      className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-200 disabled:opacity-50 ${
                        darkMode
                          ? "bg-redhalo-orange text-white hover:opacity-90"
                          : "rh-btn-primary"
                      }`}
                    >
                      {importing ? "Importing..." : "Confirm Import"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Import Progress Overlay */}
            {importing && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div
                  className={`w-full max-w-md p-6 rounded-md shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <h2 className="text-lg font-semibold mb-4">
                    Importing Employees
                  </h2>
                  <p className="text-sm mb-4">
                    Please keep this tab open until import completes.
                  </p>

                  <div className="mb-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Progress:</span>
                      <span>
                        {importProgress.processed} / {importProgress.total}{" "}
                        records
                      </span>
                    </div>

                    {importProgress.successful !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          Successful:
                        </span>
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {importProgress.successful}
                        </span>
                      </div>
                    )}

                    {importProgress.failed !== undefined &&
                      importProgress.failed > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-red-600 font-medium">
                            Failed:
                          </span>
                          <span className="text-red-600">
                            {importProgress.failed}
                          </span>
                        </div>
                      )}

                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">ETA:</span>
                      <span>{importProgress.eta}</span>
                    </div>
                  </div>

                  <div
                    className={`w-full h-4 rounded-md ${
                      darkMode ? "bg-[#3C3C3C]" : "bg-gray-200"
                    }`}
                  >
                    <div
                      className={`h-4 rounded-md ${
                        darkMode ? "bg-redhalo-navy" : "bg-redhalo-red"
                      }`}
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(
                            (importProgress.total
                              ? importProgress.processed / importProgress.total
                              : 0) * 100
                          )
                        )}%`,
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  </div>

                  <div className="mt-2 text-center text-lg font-bold">
                    {Math.min(
                      100,
                      Math.round(
                        (importProgress.total
                          ? importProgress.processed / importProgress.total
                          : 0) * 100
                      )
                    )}
                    %
                  </div>

                  <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-redhalo-red"></div>
                    <span className="ml-3 text-sm">Processing...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Import Results Modal */}
            {showImportResultsModal && importResults && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                {/* Debug log removed to prevent React rendering issues */}
                <div
                  className={`p-6 rounded-md shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div
                    className={`sticky top-0 z-20 pb-4 bg-white dark:bg-[#2C2C2C] rounded-t-lg`}
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">
                        Import Results{" "}
                        {importResults.section &&
                          `- ${
                            importResults.section.charAt(0).toUpperCase() +
                            importResults.section.slice(1)
                          } Details`}
                      </h2>

                      <div className="flex items-center space-x-3">
                        {/* Download Error Report Button */}
                        {importResults.failedRecords &&
                          importResults.failedRecords.length > 0 && (
                            <button
                              onClick={() =>
                                downloadErrorReport(importResults.failedRecords)
                              }
                              className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm flex items-center space-x-2 shadow"
                              title="Download Error Report"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <span>Download Error Report</span>
                            </button>
                          )}
                        <button
                          onClick={() => {
                            setShowImportResultsModal(false);
                            setImportResults(null);
                          }}
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <X size={24} />
                        </button>
                      </div>
                    </div>

                    <div className="border-b border-gray-200 dark:border-gray-700"></div>
                  </div>

                  <div className="space-y-6 pt-4 pb-6">
                    {/* Summary Section */}
                    <div
                      className={`rounded-md p-6 ${
                        importResults.success
                          ? darkMode
                            ? "border-emerald-500 bg-emerald-900/20 text-emerald-100"
                            : "border-emerald-400 bg-emerald-50 text-emerald-950"
                          : darkMode
                          ? "border-red-500 bg-red-950/30 text-red-200"
                          : "border-red-200 bg-[#FDE8E8] text-red-800"
                      } border-l-4 shadow-sm`}
                    >
                      <h3 className="text-lg font-medium mb-4">
                        Import Summary
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center py-2">
                          <div className="text-3xl font-extrabold">
                            {importResults.summary.total}
                          </div>
                          <div className="text-sm text-gray-600">
                            Total Records
                          </div>
                        </div>
                        <div className="text-center py-2">
                          <div className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400">
                            {importResults.summary.successful}
                          </div>
                          <div className="text-sm text-gray-600">
                            Successful
                          </div>
                        </div>
                        <div className="text-center py-2">
                          <div className="text-3xl font-extrabold text-red-600">
                            {importResults.summary.failed}
                          </div>
                          <div className="text-sm text-gray-600">Failed</div>
                        </div>
                        <div className="text-center py-2">
                          <div className="text-3xl font-extrabold text-redhalo-red">
                            {importResults.summary.total > 0
                              ? Math.round(
                                  (importResults.summary.successful /
                                    importResults.summary.total) *
                                    100
                                )
                              : 0}
                            %
                          </div>
                          <div className="text-sm text-gray-600">
                            Success Rate
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Successful Records */}
                    {importResults.successfulRecords &&
                      importResults.successfulRecords.length > 0 && (
                        <div className="mb-6">
                          <h3 className="mb-4 text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                            Successfully Imported Records
                          </h3>
                          <div
                            className={`max-h-40 overflow-y-auto overflow-x-auto rounded-lg border shadow-sm ${
                              darkMode
                                ? "border-[#3C3C3C] bg-[#1f1f1f]"
                                : "border-gray-200 bg-[#fafafa]"
                            }`}
                          >
                            <table className="w-full text-sm">
                              <thead
                                className={
                                  darkMode
                                    ? "rh-table-header-row-dark"
                                    : "rh-table-header-row"
                                }
                              >
                                <tr>
                                  <th className="px-4 py-2 text-left">
                                    Employee Code
                                  </th>
                                  <th className="px-4 py-2 text-left">Name</th>
                                  <th className="px-4 py-2 text-left">Email</th>
                                  <th className="px-4 py-2 text-left">
                                    Department
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {importResults.successfulRecords
                                  ?.slice(0, 10)
                                  .map((record, index) => (
                                    <tr
                                      key={index}
                                      className={`transition-colors ${
                                        darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]"
                                      } ${
                                        darkMode
                                          ? "hover:bg-[#2a2a2a]"
                                          : "hover:bg-redhalo-mint/40"
                                      }`}
                                    >
                                      <td className="px-4 py-2">
                                        {record.employeeCode ||
                                          record.empCode ||
                                          "N/A"}
                                      </td>
                                      <td className="px-4 py-2">
                                        {record.firstName && record.lastName
                                          ? `${record.firstName} ${record.lastName}`
                                          : record.user?.name || "N/A"}
                                      </td>
                                      <td className="px-4 py-2">
                                        {typeof record.email === "object" &&
                                        record.email !== null
                                          ? record.email.name
                                          : record.email || "N/A"}
                                      </td>

                                      <td className="px-4 py-2">
                                        {record.department?.name ||
                                          record.department ||
                                          record.departmentName ||
                                          record.departmentObj?.name ||
                                          record.Department ||
                                          "N/A"}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                            {importResults.successfulRecords?.length > 10 && (
                              <div className="p-2 text-center text-sm text-gray-500">
                                ... and{" "}
                                {importResults.successfulRecords.length - 10}{" "}
                                more records
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Failed Records */}
                    {importResults.failedRecords &&
                      importResults.failedRecords.length > 0 && (
                        <div className="mb-6">
                          <h3 className="mb-4 text-lg font-semibold text-redhalo-red dark:text-red-400">
                            Failed Records
                          </h3>
                          <div
                            className={`max-h-96 overflow-y-auto overflow-x-auto rounded-lg border shadow-sm ${
                              darkMode
                                ? "border-[#3C3C3C] bg-[#1f1f1f]"
                                : "border-gray-200 bg-[#fafafa]"
                            }`}
                          >
                            <table
                              className={`min-w-full border-collapse text-sm ${
                                darkMode ? "text-gray-200" : "text-gray-800"
                              }`}
                            >
                              <thead
                                className={`text-left ${
                                  darkMode
                                    ? "rh-table-header-row-dark"
                                    : "rh-table-header-row"
                                }`}
                              >
                                <tr>
                                  <th className="px-4 py-2 border-b border-gray-300 w-16">
                                    Row
                                  </th>
                                  <th className="px-4 py-2 border-b border-gray-300 w-32">
                                    Employee Code
                                  </th>
                                  <th className="px-4 py-2 border-b border-gray-300 w-48">
                                    Name
                                  </th>
                                  <th className="px-4 py-2 border-b border-gray-300 w-64">
                                    Email
                                  </th>
                                  <th className="px-4 py-2 border-b border-gray-300 w-56">
                                    Error Message
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {importResults.failedRecords
                                  ?.slice(0, 20)
                                  .map((record, index) => (
                                    <tr
                                      key={index}
                                      className={`align-top transition-colors ${
                                        darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]"
                                      } ${
                                        darkMode
                                          ? "hover:bg-[#2a2a2a]"
                                          : "hover:bg-redhalo-mint/40"
                                      }`}
                                    >
                                      <td className="px-4 py-2 border-b border-gray-200 text-center">
                                        {record.row || record.rowNumber}
                                      </td>
                                      <td className="px-4 py-2 border-b border-gray-200">
                                        {record.record?.["Employee Code*"] ||
                                          record.record?.["Employee Code"] ||
                                          record.employeeCode ||
                                          "N/A"}
                                      </td>
                                      <td className="px-4 py-2 border-b border-gray-200">
                                        {record.record?.["Name*"] ||
                                          record.record?.["Name"] ||
                                          record.record?.name ||
                                          record.data?.["Name*"] ||
                                          record.name ||
                                          "—"}
                                      </td>
                                      <td className="px-4 py-2 border-b border-gray-200">
                                        {record.record?.["Official Email*"] ||
                                          record.record?.["Official Email"] ||
                                          record.record?.["Personal Email*"] ||
                                          record.record?.["Personal Email"] ||
                                          record.record?.["Email"] ||
                                          record.email ||
                                          record.user?.email ||
                                          record.data?.["Official Email*"] ||
                                          record.data?.["Official Email"] ||
                                          record.data?.["Personal Email*"] ||
                                          record.data?.["Personal Email"] ||
                                          record.data?.["Email"] ||
                                          "—"}
                                      </td>
                                      <td className="px-4 py-2 border-b border-gray-200 align-top">
                                        <div className="w-56 max-h-40 overflow-y-auto pr-2">
                                          <div className="text-sm text-red-600 dark:text-red-400 leading-relaxed space-y-1">
                                            {/* Display field-level errors from backend */}
                                            {record.fieldErrors &&
                                            typeof record.fieldErrors ===
                                              "object" ? (
                                              Object.entries(
                                                record.fieldErrors
                                              ).map(([field, error], i) => (
                                                <div key={i} className="mb-2">
                                                  <span className="font-semibold">
                                                    {field}:
                                                  </span>{" "}
                                                  {error}
                                                </div>
                                              ))
                                            ) : Array.isArray(record.errors) &&
                                              record.errors.length > 0 ? (
                                              // Render each error on its own line for readability
                                              record.errors.map((err, i) => (
                                                <div key={i} className="mb-2">
                                                  {err}
                                                </div>
                                              ))
                                            ) : record.error ? (
                                              // If backend returned a single error string, split common separators to show multiple lines
                                              String(record.error)
                                                .split(/\n|;|\.|\\r\\n/)
                                                .map((err, i) =>
                                                  err.trim() ? (
                                                    <div
                                                      key={i}
                                                      className="mb-2"
                                                    >
                                                      {err.trim()}
                                                    </div>
                                                  ) : null
                                                )
                                            ) : (
                                              <div>
                                                Error details not available.
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>

                            {importResults.failedRecords?.length > 20 && (
                              <div className="p-2 text-center text-sm text-gray-500">
                                ... and{" "}
                                {importResults.failedRecords.length - 20} more
                                failed records
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Error Resolution Guide */}
                    {importResults.failedRecords &&
                      importResults.failedRecords.length > 0 && (
                        <div
                          className={`mt-6 p-4 rounded-md border-l-4 border-redhalo-red ${
                            darkMode ? "bg-redhalo-navy/15" : "bg-redhalo-mint/50"
                          }`}
                        >
                          <h3 className="text-lg font-medium mb-3 text-redhalo-navy dark:text-redhalo-mint">
                            How to Fix Import Errors
                          </h3>
                          <div className="space-y-3 text-sm text-redhalo-navy dark:text-redhalo-mint">
                            <div className="flex items-start">
                              <span className="mr-2 text-redhalo-red font-bold">
                                1.
                              </span>
                              <span>
                                Download the template again to see the correct
                                format
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="mr-2 text-redhalo-red font-bold">
                                2.
                              </span>
                              <span>
                                Fix the errors shown above in your Excel file
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="mr-2 text-redhalo-red font-bold">
                                3.
                              </span>
                              <span>
                                Ensure all master data (departments, branches,
                                designations) exist in the system
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="mr-2 text-redhalo-red font-bold">
                                4.
                              </span>
                              <span>
                                Check for duplicate emails, phone numbers, and
                                PAN numbers
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="mr-2 text-redhalo-red font-bold">
                                5.
                              </span>
                              <span>Re-upload the corrected file</span>
                            </div>
                          </div>
                        </div>
                      )}

                    <div
                      className={`sticky bottom-0 left-0 right-0 pt-4 ${
                        darkMode ? "bg-[#2C2C2C]" : "bg-white"
                      }`}
                    >
                      <div
                        className={`flex justify-end border-t pt-4 ${
                          darkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <button
                          onClick={() => {
                            setShowImportResultsModal(false);
                            setImportResults(null);
                          }}
                          className={`px-6 py-2 text-sm font-medium rounded-md ${
                            darkMode
                              ? "bg-redhalo-navy text-redhalo-mint hover:bg-redhalo-navy-light"
                              : "rh-btn-primary hover:opacity-95"
                          } transition-colors duration-200`}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showExportModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-md w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">
                      Export Employee Data
                    </h2>

                    <button
                      onClick={() => setShowExportModal(false)}
                      disabled={exporting}
                      className={`text-gray-500 hover:text-gray-700 transition-colors ${
                        exporting ? "cursor-not-allowed opacity-50" : ""
                      }`}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-2">
                    {[
                      { key: "org", label: "Organization Details" },
                      { key: "personal", label: "Personal Details" },
                      { key: "family", label: "Family Details" },
                      { key: "bank", label: "Bank Details" },
                      { key: "educational", label: "Education Details" },
                      {
                        key: "professional",
                        label: "Professional Details",
                      },
                      { key: "statutory", label: "Statutory Details" },
                    ].map((section) => (
                      <button
                        key={section.key}
                        onClick={() => handleSectionExport(section.key)}
                        disabled={exporting}
                        className={`w-full px-3 py-2 text-sm rounded-md text-left ${
                          darkMode
                            ? "bg-[#3C3C3C] hover:bg-[#4C4C4C]"
                            : "bg-gray-100 hover:bg-gray-200"
                        } transition-colors duration-200 ${
                          exporting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {section.label}
                      </button>
                    ))}

                    <button
                      onClick={() => setShowAllDataExportConfirmPopup(true)}
                      disabled={exporting}
                      className={`w-full px-3 py-2 text-sm rounded-md ${
                        darkMode
                          ? "bg-redhalo-navy text-white hover:bg-redhalo-navy-light"
                          : "rh-btn-primary"
                      } transition-colors duration-200 ${
                        exporting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      All Employee Data
                    </button>
                  </div>

                  {exporting && (
                    <div className="mt-4 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-redhalo-red"></div>

                      <span className="ml-2 text-sm">Exporting...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showAllDataExportConfirmPopup && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000]">
                <div
                  className={`p-6 rounded-xl shadow-2xl max-w-md w-full mx-4 border ${
                    darkMode
                      ? "bg-[#1E1E2E] border-[#3C3C4C]"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      darkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-600"
                    }`}>
                      <AlertTriangle size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                      }`}>
                        Export Notice
                      </h3>
                      <p className={`text-sm leading-relaxed ${
                        darkMode ? "text-[#B0B0B0]" : "text-gray-600"
                      }`}>
                        Due to column structure limitations, we are unable to export family, education, and professional details of all employees in a single file.
                      </p>
                      <p className={`text-sm mt-3 ${
                        darkMode ? "text-[#A0A0A0]" : "text-gray-500"
                      }`}>
                        Please download those sections separately using the options above.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowAllDataExportConfirmPopup(false)}
                      className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                        darkMode
                          ? "bg-[#3C3C4C] text-[#E0E0E0] hover:bg-[#4C4C5C]"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setShowAllDataExportConfirmPopup(false);
                        await handleExport();
                      }}
                      disabled={exporting}
                      className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                        darkMode
                          ? "bg-redhalo-navy text-white hover:bg-redhalo-navy-light"
                          : "rh-btn-primary hover:opacity-95"
                      } ${exporting ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      Okay, Export
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showPhotoModal && selectedPhotoEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-md w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">
                      {selectedPhotoEmployee.user?.name}'s Photo
                    </h2>

                    <button
                      onClick={() => setShowPhotoModal(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="flex justify-center">
                    {(() => {
                      const photoSrc = getModalPhotoSrc(selectedPhotoEmployee);
                      if (photoSrc) {
                        return (
                          <img
                            src={photoSrc}
                            alt={`${selectedPhotoEmployee.user?.name || "Employee"}`}
                            className="w-48 h-48 object-cover rounded-md"
                          />
                        );
                      }

                      return (
                        <div
                          className={`w-48 h-48 ${
                            darkMode
                              ? "bg-redhalo-navy text-redhalo-mint"
                              : "bg-gray-300 text-gray-600"
                          } flex items-center justify-center text-4xl font-semibold rounded-md`}
                        >
                          {selectedPhotoEmployee.user?.name
                            ?.charAt(0)
                            ?.toUpperCase() || "E"}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {showViewModal && selectedEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-4 sticky top-0 bg-inherit pb-2 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">Employee Details</h2>
                      {selectedEmployee.editLocked && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          Locked (historical record)
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-4 mt-4">
                    <div>
                      <span className="font-semibold">Emp Code:</span>{" "}
                      {selectedEmployee.empCode}
                    </div>
                    <div>
                      <span className="font-semibold">Name:</span>{" "}
                      {toTitleCase(
                        `${selectedEmployee.user?.name || ""} ${selectedEmployee.user?.lastName || ""}`.trim()
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Designation:</span>{" "}
                      {getNameByIdFromList(
                        selectedEmployee.designationId,
                        designationsList,
                        selectedEmployee,
                        'designation'
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Department:</span>{" "}
                      {getNameByIdFromList(
                        selectedEmployee.departmentId,
                        departmentsList,
                        selectedEmployee,
                        'department'
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Branch:</span>{" "}
                      {getNameByIdFromList(
                        selectedEmployee.branchId,
                        branchesList,
                        selectedEmployee,
                        'branch'
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Date of Join:</span>{" "}
                      {formatDate(
                        selectedEmployee.dateOfJoin ||
                          selectedEmployee.joiningDate ||
                          selectedEmployee.joinDate
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Date of Relieving:</span>{" "}
                      {formatDate(getEmployeeRelievingDate(selectedEmployee)) || "—"}
                    </div>
                    {selectedEmployee.employmentStatus === "Resigned" &&
                      selectedEmployee.resignationReason && (
                        <div>
                          <span className="font-semibold">Resignation Reason:</span>{" "}
                          {selectedEmployee.resignationReason}
                        </div>
                      )}
                    <div>
                      <span className="font-semibold">Mobile:</span>{" "}
                      {selectedEmployee.user?.phone || "N/A"}
                    </div>
                    <div>
                      <span className="font-semibold">Email:</span>{" "}
                      {(selectedEmployee.email || selectedEmployee.user?.email) || "N/A"}
                    </div>
                    <div>
                      <span className="font-semibold">Employment Type:</span>{" "}
                      {selectedEmployee.employmentType || "—"}
                    </div>
                    <div>
                      <span className="font-semibold">Status:</span>{" "}
                      {selectedEmployee.employmentStatus}
                    </div>
                    <div>
                      <span className="font-semibold">CTC:</span>{" "}
                      {selectedEmployee.ctc != null ? (typeof selectedEmployee.ctc === 'number' ? selectedEmployee.ctc.toLocaleString() : selectedEmployee.ctc) : "—"}
                    </div>

                    {/* Bank details */}
                    <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-600">
                      <h3 className="font-semibold mb-2">Bank Details</h3>
                      <div className="space-y-1 text-sm">
                        <div><span className="font-medium">Bank Name:</span> {selectedEmployee.bankName || "—"}</div>
                        <div><span className="font-medium">Account Number:</span> {selectedEmployee.accountNumber ? `****${String(selectedEmployee.accountNumber).slice(-4)}` : "—"}</div>
                        <div><span className="font-medium">IFSC:</span> {selectedEmployee.ifscCode || "—"}</div>
                        <div><span className="font-medium">Bank Branch:</span> {selectedEmployee.bankBranch || "—"}</div>
                      </div>
                    </div>

                    {/* Statutory details */}
                    <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-600">
                      <h3 className="font-semibold mb-2">Statutory Details</h3>
                      <div className="space-y-1 text-sm">
                        <div><span className="font-medium">PF Applicable:</span> {selectedEmployee.pfApplicable !== false ? "Yes" : "No"}</div>
                        <div><span className="font-medium">Ceiling PF:</span> {selectedEmployee.ceilingPf !== false ? "Yes" : "No"}</div>
                        <div><span className="font-medium">ESI Applicable:</span> {selectedEmployee.esiApplicable ? "Yes" : "No"}</div>
                        <div><span className="font-medium">PT Applicable:</span> {selectedEmployee.ptApplicable !== false ? "Yes" : "No"}</div>
                        <div><span className="font-medium">Gratuity Applicable:</span> {selectedEmployee.gratuityApplicable ? "Yes" : "No"}</div>
                        <div><span className="font-medium">PAN:</span> {selectedEmployee.panNumber || "—"}</div>
                        <div><span className="font-medium">Aadhaar:</span> {selectedEmployee.aadharNumber ? `****${String(selectedEmployee.aadharNumber).slice(-4)}` : "—"}</div>
                        {selectedEmployee.pfUANNumber && (
                          <div><span className="font-medium">PF UAN:</span> {selectedEmployee.pfUANNumber}</div>
                        )}
                        {selectedEmployee.esiIPNumber && (
                          <div><span className="font-medium">ESI IP:</span> {selectedEmployee.esiIPNumber}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmationModal && selectedEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-md w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold">
                      Employee Confirmation
                    </h2>
                    <button
                      onClick={() => {
                        setShowConfirmationModal(false);
                        setConfirmationData({
                          isConfirmed: "",
                          confirmationDate: "",
                          extendProbation: false,
                          extendedProbationDate: "",
                        });
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div className="text-center">
                      <p
                        className={`text-sm mb-4 ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Employee:{" "}
                        <span className="font-semibold">
                          {selectedEmployee.user?.name} (
                          {selectedEmployee.empCode})
                        </span>
                      </p>
                    </div>

                    <div>
                      <p
                        className={`text-sm font-medium mb-4 ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Is the employee confirmed as permanent?
                      </p>
                      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="confirmation"
                            value="yes"
                            checked={confirmationData.isConfirmed === "yes"}
                            onChange={(e) => {
                              e.stopPropagation();
                              setConfirmationData((prev) => ({
                                ...prev,
                                isConfirmed: e.target.value,
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-3"
                          />
                          <span
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Yes
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="confirmation"
                            value="no"
                            checked={confirmationData.isConfirmed === "no"}
                            onChange={(e) => {
                              e.stopPropagation();
                              setConfirmationData((prev) => ({
                                ...prev,
                                isConfirmed: e.target.value,
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-3"
                          />
                          <span
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            No
                          </span>
                        </label>
                      </div>
                    </div>

                    {confirmationData.isConfirmed === "yes" && (
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Date of Confirmation{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          value={confirmationData.confirmationDate || ""}
                          min={
                            selectedEmployee.dateOfJoin ||
                            selectedEmployee.joiningDate ||
                            selectedEmployee.joinDate
                          }
                          onChange={(e) =>
                            setConfirmationData((prev) => ({
                              ...prev,
                              confirmationDate: e.target.value,
                            }))
                          }
                          className={`w-full`}
                          validateOnType={true}
                        />
                      </div>
                    )}

                    {confirmationData.isConfirmed === "no" && (
                      <div>
                        <label className="flex items-center cursor-pointer mb-3">
                          <input
                            type="checkbox"
                            checked={confirmationData.extendProbation}
                            onChange={(e) =>
                              setConfirmationData((prev) => ({
                                ...prev,
                                extendProbation: e.target.checked,
                              }))
                            }
                            className="mr-3"
                          />
                          <span
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Extend the probation
                          </span>
                        </label>

                        {confirmationData.extendProbation && (
                          <div>
                            <label
                              className={`block text-sm font-medium mb-2 ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Extended Probation End Date{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                              value={
                                confirmationData.extendedProbationDate || ""
                              }
                              min={
                                selectedEmployee.dateOfJoin ||
                                selectedEmployee.joiningDate ||
                                selectedEmployee.joinDate
                              }
                              onChange={(e) =>
                                setConfirmationData((prev) => ({
                                  ...prev,
                                  extendedProbationDate: e.target.value,
                                }))
                              }
                              className={`w-full`}
                              validateOnType={true}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowConfirmationModal(false);
                        setConfirmationData({
                          isConfirmed: "",
                          confirmationDate: "",
                          extendProbation: false,
                          extendedProbationDate: "",
                        });
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } transition-colors`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (
                          confirmationData.isConfirmed === "yes" &&
                          !confirmationData.confirmationDate
                        ) {
                          toast.error("Please select confirmation date");
                          return;
                        }
                        // ✅ Validation: Confirmation date must not be before joining date
                        const joiningDate =
                          selectedEmployee.joiningDate ||
                          selectedEmployee.dateOfJoin ||
                          selectedEmployee.joinDate;
                        if (
                          joiningDate &&
                          !isSameOrAfter(
                            confirmationData.confirmationDate,
                            joiningDate
                          )
                        ) {
                          toast.error(
                            "Confirmation date cannot be before joining date."
                          );
                          return;
                        }

                        if (
                          confirmationData.isConfirmed === "no" &&
                          confirmationData.extendProbation &&
                          !confirmationData.extendedProbationDate
                        ) {
                          toast.error("Please select extended probation date");
                          return;
                        }
                        if (!confirmationData.isConfirmed) {
                          toast.error("Please select Yes or No");
                          return;
                        }

                        try {
                          // No date validation needed here - backend will handle effective dates
                          // Backend will update employee status immediately if date is today or past

                          // Save confirmation to database
                          const confirmationPayload = {
                            employeeId: selectedEmployee.id,
                            isConfirmed: confirmationData.isConfirmed === "yes",
                            confirmationDate: confirmationData.confirmationDate,
                            extendProbation: confirmationData.extendProbation,
                            extendedProbationDate:
                              confirmationData.extendProbation
                                ? confirmationData.extendedProbationDate
                                : null,
                            remarks: "",
                          };

                          const response =
                            await confirmationService.createConfirmation(
                              confirmationPayload
                            );

                          if (response.success) {
                            addEmployeeInsight({
                              action: "Confirmation",
                              employee: selectedEmployee,
                              pendingAt: "Reporting Manager",
                            });
                            toast.success(
                              "Employee confirmation saved successfully!"
                            );

                            setShowConfirmationModal(false);
                            setConfirmationData({
                              isConfirmed: "",
                              confirmationDate: "",
                              extendProbation: false,
                              extendedProbationDate: "",
                            });

                            // Refresh employee list
                            fetchEmployees();
                          } else {
                            throw new Error(
                              response.message || "Confirmation save failed"
                            );
                          }
                        } catch (error) {
                          console.error(
                            "Error updating employee confirmation:",
                            error
                          );
                          toast.error("Error updating employee confirmation");
                        }
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        darkMode
                          ? "rh-btn-primary hover:opacity-95"
                          : "rh-btn-primary"
                      } transition-colors`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Salary Revision Modal */}
            {showSalaryRevisionModal && selectedEmployee && (
              <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
                <div
                  className={`w-full max-w-4xl rounded-xl p-6 shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      Salary Revision –{" "}
                      {selectedEmployee.user?.name ||
                        selectedEmployee.name ||
                        "N/A"}{" "}
                      –{" "}
                      {selectedEmployee.empCode ||
                        selectedEmployee.code ||
                        "N/A"}
                    </h2>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSalaryRevisionModal(false);
                        setSalaryRevisionData({
                          effectiveDate: "",
                          arrearsEffectiveDate: "",
                          ctcAmount: "",
                          reason: "",
                          changeDesignation: false,
                          newDesignationId: "",
                          designationSearch: "",
                          copyEffectiveDate: false,
                        });
                      }}
                      className="text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="space-y-6">
                    {/* Current CTC Information */}
                    <div
                      className={`p-4 rounded-md border ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-300"
                      }`}
                    >
                      <h3
                        className={`text-sm font-semibold mb-3 ${
                          darkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Current Salary Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Current CTC/Salary
                          </label>
                          <div
                            className={`text-sm font-medium ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            ₹
                            {selectedEmployee.ctc
                              ? formatIndianNumber(selectedEmployee.ctc)
                              : "Not set"}
                          </div>
                          {selectedEmployee.ctc && (
                            <div
                              className={`mt-1 text-xs ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Rs. {numberToWords(selectedEmployee.ctc)}
                            </div>
                          )}
                        </div>
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Current Designation
                          </label>
                          <div
                            className={`text-sm font-medium ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.designationId,
                              designationsList
                            )}
                          </div>
                        </div>
                        {/* Current Effective Date - Hidden/Commented */}
                        {/* <div>
                            <label
                              className={`block text-sm font-medium mb-1 ${
                                darkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Current Effective Date
                            </label>
                            <div
                              className={`text-sm font-medium ${
                                darkMode ? "text-gray-200" : "text-gray-800"
                              }`}
                            >
                              {latestSalaryEffectiveDate
                                ? new Date(
                                    latestSalaryEffectiveDate
                                  ).toLocaleDateString()
                                : selectedEmployee.dateOfJoin
                                ? new Date(
                                    selectedEmployee.dateOfJoin
                                  ).toLocaleDateString() + " (Date of Join)"
                                : "Not set"}
                            </div>
                          </div> */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Last Updated
                          </label>
                          <div
                            className={`text-sm font-medium ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {selectedEmployee.updatedAt
                              ? formatDate(selectedEmployee.updatedAt)
                              : "Not available"}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Salary Revision Form - Horizontal Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Effective Date{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            value={salaryRevisionData.effectiveDate || ""}
                            min={
                              getTodayDateYYYYMMDD() ||
                              selectedEmployee.dateOfJoin ||
                              selectedEmployee.joiningDate ||
                              selectedEmployee.joinDate
                            }
                            onChange={(e) =>
                              setSalaryRevisionData((prev) => ({
                                ...prev,
                                effectiveDate: e.target.value,
                              }))
                            }
                            className={`w-full`}
                            validateOnType={true}
                          />
                        </div>
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Revised CTC/Salary per Annum{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={
                              salaryRevisionData.ctcAmount
                                ? formatIndianNumber(
                                    salaryRevisionData.ctcAmount
                                  )
                                : ""
                            }
                            onChange={(e) => {
                              // Remove commas and non-numeric characters except empty string
                              const rawValue = e.target.value.replace(/,/g, "");
                              // Only allow numeric values
                              if (rawValue === "" || /^\d+$/.test(rawValue)) {
                                setSalaryRevisionData((prev) => ({
                                  ...prev,
                                  ctcAmount: rawValue,
                                }));
                              }
                            }}
                            onBlur={(e) => {
                              // Ensure the value is stored without commas
                              const rawValue = e.target.value.replace(/,/g, "");
                              if (rawValue !== salaryRevisionData.ctcAmount) {
                                setSalaryRevisionData((prev) => ({
                                  ...prev,
                                  ctcAmount: rawValue,
                                }));
                              }
                            }}
                            placeholder="Enter CTC amount"
                            className={`w-full p-2 border rounded-md text-sm ${
                              darkMode
                                ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            }`}
                          />
                          {salaryRevisionData.ctcAmount && (() => {
                            const currentCTC = parseFloat(selectedEmployee.ctc) || 0;
                            const revisedCTC = parseFloat(salaryRevisionData.ctcAmount) || 0;
                            const percentageChange = currentCTC > 0
                              ? calculatePercentageChange(currentCTC, revisedCTC)
                              : null;
                            const words = numberToWords(salaryRevisionData.ctcAmount);
                            const isIncrease = percentageChange !== null && percentageChange > 0;
                            const isDecrease = percentageChange !== null && percentageChange < 0;
                            const absPercentage = percentageChange !== null
                              ? Math.abs(percentageChange).toFixed(2)
                              : "0.00";

                            return (
                              <div className="mt-1 flex items-center justify-between">
                                <div
                                  className={`text-xs ${
                                    darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                                  }`}
                                >
                                  Rs. {words}
                                </div>
                                {percentageChange !== null && (
                                  <div
                                    className={`text-xs font-semibold ${
                                      isIncrease
                                        ? darkMode ? "text-emerald-400" : "text-emerald-700"
                                        : isDecrease
                                        ? darkMode ? "text-red-400" : "text-redhalo-red"
                                        : darkMode ? "text-gray-300" : "text-gray-600"
                                    }`}
                                  >
                                    {isIncrease
                                      ? `${absPercentage}% Increase`
                                      : isDecrease
                                      ? `${absPercentage}% Decrease`
                                      : "No Change"}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Reason <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={salaryRevisionData.reason}
                            onChange={(e) =>
                              setSalaryRevisionData((prev) => ({
                                ...prev,
                                reason: e.target.value,
                              }))
                            }
                            className={`w-full p-2 border rounded-md text-sm ${
                              darkMode
                                ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            }`}
                          >
                            <option value="">Select Reason</option>
                            <option value="Performance">Performance</option>
                            <option value="Promotion">Promotion</option>
                            <option value="As per benchmarking">
                              As per benchmarking
                            </option>
                            <option value="Market adjustment">
                              Market adjustment
                            </option>
                            <option value="Retention">Retention</option>
                            <option value="Skill enhancement">
                              Skill enhancement
                            </option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Arrears Effective Date{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              checked={salaryRevisionData.copyEffectiveDate}
                              onChange={(e) => {
                                const copyEffective = e.target.checked;
                                setSalaryRevisionData((prev) => ({
                                  ...prev,
                                  copyEffectiveDate: copyEffective,
                                  arrearsEffectiveDate: copyEffective
                                    ? prev.effectiveDate
                                    : prev.arrearsEffectiveDate,
                                }));
                              }}
                              className="mr-2"
                            />
                            <span
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Copy from Effective Date
                            </span>
                          </div>
                          <DatePicker
                            value={salaryRevisionData.arrearsEffectiveDate}
                            min={
                              selectedEmployee.dateOfJoin ||
                              selectedEmployee.joiningDate ||
                              selectedEmployee.joinDate
                            }
                            onChange={(e) =>
                              setSalaryRevisionData((prev) => ({
                                ...prev,
                                arrearsEffectiveDate: e.target.value,
                              }))
                            }
                            className={`w-full p-2 border rounded-md text-sm ${
                              darkMode
                                ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            }`}
                          />
                        </div>

                        <div>
                          <label className="flex items-center cursor-pointer mb-3">
                            <input
                              type="checkbox"
                              checked={salaryRevisionData.changeDesignation}
                              onChange={(e) =>
                                setSalaryRevisionData((prev) => ({
                                  ...prev,
                                  changeDesignation: e.target.checked,
                                }))
                              }
                              className="mr-3"
                            />
                            <span
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Change Designation
                            </span>
                          </label>

                          {salaryRevisionData.changeDesignation && (
                            <div>
                              <label
                                className={`block text-sm font-medium mb-2 ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                New Designation{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={salaryRevisionData.designationSearch}
                                  onChange={(e) =>
                                    setSalaryRevisionData((prev) => ({
                                      ...prev,
                                      designationSearch: e.target.value,
                                    }))
                                  }
                                  placeholder="Search designations..."
                                  className={`w-full p-2 border rounded-md text-sm ${
                                    darkMode
                                      ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                      : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  }`}
                                />
                                {salaryRevisionData.designationSearch && (
                                  <div
                                    className={`absolute top-full left-0 right-0 z-10 mt-1 max-h-40 overflow-y-auto rounded-md border shadow-lg ${
                                      darkMode
                                        ? "border-[#4C4C4C] bg-[#2C2C2C]"
                                        : "border-gray-200 bg-white"
                                    }`}
                                  >
                                    {designationsList
                                      .filter((designation) =>
                                        designation.name
                                          .toLowerCase()
                                          .includes(
                                            salaryRevisionData.designationSearch.toLowerCase()
                                          )
                                      )
                                      .map((designation) => (
                                        <div
                                          key={designation.id}
                                          onClick={() =>
                                            setSalaryRevisionData((prev) => ({
                                              ...prev,
                                              newDesignationId: designation.id,
                                              designationSearch:
                                                designation.name,
                                            }))
                                          }
                                          className={`cursor-pointer p-2 text-sm transition-colors hover:bg-redhalo-mint/40 dark:hover:bg-[#2e2e2e] ${
                                            darkMode
                                              ? "text-gray-200"
                                              : "text-gray-800"
                                          }`}
                                        >
                                          {designation.name}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex justify-between items-center mt-6">
                      <button
                        onClick={async () => {
                          try {
                            setLoadingSalaryHistory(true);
                            setShowSalaryHistoryModal(true);

                            const response =
                              await salaryService.getSalaryHistory(
                                selectedEmployee.id
                              );

                            if (response.success && response.data) {
                              // Transform API data to match frontend format
                              const history = response.data.map((item) => ({
                                id: item.id,
                                effectiveDate: item.effectiveDate,
                                arrearsEffectiveDate: item.arrearsEffectiveDate,
                                ctcAmount: item.newCtc,
                                reason: item.reason,
                                designation:
                                  item.newDesignation?.name ||
                                  item.previousDesignation?.name ||
                                  "N/A",
                                createdDate: item.createdAt,
                                createdBy: item.creator?.name || "Unknown",
                              }));
                              setSalaryHistory(history);
                            } else {
                              setSalaryHistory([]);
                            }
                          } catch (error) {
                            console.error(
                              "Error loading salary history:",
                              error
                            );
                            toast.error("Failed to load salary history");
                            setSalaryHistory([]);
                          } finally {
                            setLoadingSalaryHistory(false);
                          }
                        }}
                        disabled={loadingSalaryHistory}
                        type="button"
                        className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-95 ${
                          loadingSalaryHistory
                            ? "cursor-not-allowed bg-gray-400 text-white"
                            : "rh-btn-secondary"
                        }`}
                      >
                        {loadingSalaryHistory ? "Loading..." : "View History"}
                      </button>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowSalaryRevisionModal(false);
                            setSalaryRevisionData({
                              effectiveDate: "",
                              arrearsEffectiveDate: "",
                              ctcAmount: "",
                              reason: "",
                              changeDesignation: false,
                              newDesignationId: "",
                              designationSearch: "",
                              copyEffectiveDate: false,
                            });
                          }}
                          type="button"
                          className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                            darkMode
                              ? "bg-gray-600 text-white hover:bg-gray-700"
                              : "border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!salaryRevisionData.effectiveDate) {
                              toast.error("Please select effective date");
                              return;
                            }
                            if (!salaryRevisionData.arrearsEffectiveDate) {
                              toast.error(
                                "Please select arrears effective date"
                              );
                              return;
                            }
                            if (!salaryRevisionData.ctcAmount) {
                              toast.error("Please enter CTC amount");
                              return;
                            }
                            if (!salaryRevisionData.reason) {
                              toast.error("Please select reason");
                              return;
                            }
                            if (
                              salaryRevisionData.changeDesignation &&
                              !salaryRevisionData.newDesignationId
                            ) {
                              toast.error("Please select new designation");
                              return;
                            }
                            if (
                              salaryRevisionData.effectiveDate &&
                              !isTodayOrFuture(salaryRevisionData.effectiveDate) // Use your helper function
                            ) {
                              toast.error(
                                "Salary Effective Date must be today or a future date."
                              );
                              return; // Stop execution if validation fails
                            }
                            // ✅ Date validation rules
                            if (
                              salaryRevisionData.arrearsEffectiveDate &&
                              !isYesterdayOrOlder(
                                salaryRevisionData.arrearsEffectiveDate
                              )
                            ) {
                              toast.error(
                                "Arrears effective date must be yesterday or older."
                              );
                              return;
                            }

                            if (
                              salaryRevisionData.effectiveDate &&
                              !isTodayOrFuture(salaryRevisionData.effectiveDate)
                            ) {
                              toast.error(
                                "Salary effective date must be today or a future date."
                              );
                              return;
                            }

                            // Check if revised CTC is less than current CTC
                            if (selectedEmployee.ctc) {
                              const currentCtc = parseFloat(
                                selectedEmployee.ctc
                              );
                              const revisedCtc = parseFloat(
                                salaryRevisionData.ctcAmount
                              );
                              if (
                                !isNaN(currentCtc) &&
                                !isNaN(revisedCtc) &&
                                revisedCtc < currentCtc
                              ) {
                                // Show warning popup
                                setShowSalaryReductionWarning(true);
                                return;
                              }
                            }

                            try {
                              // Save salary revision to database
                              const salaryPayload = {
                                employeeId: selectedEmployee.id,
                                effectiveDate: salaryRevisionData.effectiveDate,
                                arrearsEffectiveDate:
                                  salaryRevisionData.arrearsEffectiveDate,
                                newCtc: parseFloat(
                                  salaryRevisionData.ctcAmount
                                ),
                                reason: salaryRevisionData.reason,
                                newDesignationId:
                                  salaryRevisionData.changeDesignation
                                    ? salaryRevisionData.newDesignationId
                                    : null,
                                designationChanged:
                                  salaryRevisionData.changeDesignation,
                                remarks: "",
                              };

                              const response = await salaryService.createSalary(
                                salaryPayload
                              );

                              if (response.success) {
                                addEmployeeInsight({
                                  action: "Salary Revision",
                                  employee: selectedEmployee,
                                  pendingAt: "Comp & Ben",
                                });
                                toast.success(
                                  "Salary revision saved successfully!"
                                );

                                setShowSalaryRevisionModal(false);
                                setSalaryRevisionData({
                                  effectiveDate: "",
                                  arrearsEffectiveDate: "",
                                  ctcAmount: "",
                                  reason: "",
                                  changeDesignation: false,
                                  newDesignationId: "",
                                  designationSearch: "",
                                  copyEffectiveDate: false,
                                });

                                // Refresh employee list
                                fetchEmployees();
                              } else {
                                throw new Error(
                                  response.message ||
                                    "Salary revision save failed"
                                );
                              }
                            } catch (error) {
                              console.error(
                                "Error updating salary revision:",
                                error
                              );
                              toast.error("Error updating salary revision");
                            }
                          }}
                          className="rh-btn-primary rounded-md px-4 py-2 text-sm font-semibold transition-all hover:opacity-95"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Salary Reduction Warning Modal */}
            {showSalaryReductionWarning && selectedEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-md w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="mb-4">
                    <h3
                      className={`text-lg font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Salary Reduction Warning
                    </h3>
                    <p
                      className={`text-sm ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      You are reducing the CTC/salary from the current salary.
                      Current CTC is ₹{formatIndianNumber(selectedEmployee.ctc)}{" "}
                      and Revised CTC is ₹
                      {formatIndianNumber(salaryRevisionData.ctcAmount)}.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowSalaryReductionWarning(false);
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                      } transition-colors`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setShowSalaryReductionWarning(false);

                        // Proceed with saving after user confirms
                        try {
                          // Save salary revision to database
                          const salaryPayload = {
                            employeeId: selectedEmployee.id,
                            effectiveDate: salaryRevisionData.effectiveDate,
                            arrearsEffectiveDate:
                              salaryRevisionData.arrearsEffectiveDate,
                            newCtc: parseFloat(salaryRevisionData.ctcAmount),
                            reason: salaryRevisionData.reason,
                            newDesignationId:
                              salaryRevisionData.changeDesignation
                                ? salaryRevisionData.newDesignationId
                                : null,
                            designationChanged:
                              salaryRevisionData.changeDesignation,
                            remarks: "",
                          };

                          const response = await salaryService.createSalary(
                            salaryPayload
                          );

                          if (response.success) {
                            toast.success(
                              "Salary revision saved successfully!"
                            );

                            setShowSalaryRevisionModal(false);
                            setSalaryRevisionData({
                              effectiveDate: "",
                              arrearsEffectiveDate: "",
                              ctcAmount: "",
                              reason: "",
                              changeDesignation: false,
                              newDesignationId: "",
                              designationSearch: "",
                              copyEffectiveDate: false,
                            });

                            // Refresh employee list
                            fetchEmployees();
                          } else {
                            throw new Error(
                              response.message || "Salary revision save failed"
                            );
                          }
                        } catch (error) {
                          console.error(
                            "Error updating salary revision:",
                            error
                          );
                          toast.error("Error updating salary revision");
                        }
                      }}
                      type="button"
                      className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-95 ${
                        darkMode
                          ? "bg-redhalo-orange hover:bg-redhalo-orange"
                          : "bg-redhalo-orange hover:opacity-90"
                      }`}
                    >
                      Yes, making salary reduction
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Salary History Modal */}
            {showSalaryHistoryModal && selectedEmployee && (
              <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
                <div
                  className={`flex h-[80%] w-[90%] max-w-6xl flex-col rounded-xl p-6 shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      Salary Revision History - {selectedEmployee.user?.name}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowSalaryHistoryModal(false)}
                      className="text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="flex-1 overflow-hidden">
                    <div className="h-full overflow-y-auto rounded-lg border border-[#165b5d]/30 dark:border-[#4C4C4C]">
                      <table className="w-full text-sm">
                        <thead
                          className={`sticky top-0 z-10 ${
                            darkMode
                              ? "rh-table-header-row-dark"
                              : "rh-table-header-row"
                          }`}
                        >
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">
                              Effective Date
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Arrears Date
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              CTC Amount
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Amount in Words
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Percentage Change
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Reason
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Designation
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Created Date
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Created By
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className={`${
                            darkMode ? "divide-gray-600" : "divide-gray-200"
                          } divide-y`}
                        >
                          {salaryHistory.map((item, index) => {
                            // Get previous CTC for percentage calculation
                            const previousCtc =
                              index < salaryHistory.length - 1
                                ? salaryHistory[index + 1]?.ctcAmount
                                : selectedEmployee.ctc;
                            const percentageChange = previousCtc
                              ? calculatePercentageChange(
                                  previousCtc,
                                  item.ctcAmount
                                )
                              : null;

                            return (
                              <tr
                                key={item.id}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-600 transition duration-150`}
                              >
                                <td className="px-4 py-2">
                                  {formatDate(item.effectiveDate)}
                                </td>
                                <td className="px-4 py-2">
                                  {formatDate(item.arrearsEffectiveDate)}
                                </td>
                                <td className="px-4 py-2">
                                  ₹
                                  {item.ctcAmount
                                    ? formatIndianNumber(item.ctcAmount)
                                    : "N/A"}
                                </td>
                                <td className="px-4 py-2">
                                  {item.ctcAmount ? (
                                    <div
                                      className={`text-xs ${
                                        darkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      RS. {numberToWords(item.ctcAmount)}
                                    </div>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  {percentageChange !== null ? (
                                    <div
                                      className={`text-xs font-semibold ${
                                        percentageChange > 0
                                          ? darkMode
                                            ? "text-emerald-400"
                                            : "text-emerald-700"
                                          : percentageChange < 0
                                          ? darkMode
                                            ? "text-red-400"
                                            : "text-redhalo-red"
                                          : darkMode
                                          ? "text-gray-400"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      {percentageChange > 0
                                        ? `${Math.abs(percentageChange).toFixed(
                                            2
                                          )}% Increase`
                                        : percentageChange < 0
                                        ? `${Math.abs(percentageChange).toFixed(
                                            2
                                          )}% Decrease`
                                        : "No Change"}
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className="px-4 py-2">{item.reason}</td>
                                <td className="px-4 py-2">
                                  {item.designation}
                                </td>
                                <td className="px-4 py-2">
                                  {formatDate(item.createdDate)}
                                </td>
                                <td className="px-4 py-2">{item.createdBy}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                    <button
                      onClick={() => {
                        // Export to Excel functionality
                        const exportData = salaryHistory.map((item, index) => {
                          // Get previous CTC for percentage calculation (same logic as table display)
                          const previousCtc =
                            index < salaryHistory.length - 1
                              ? salaryHistory[index + 1]?.ctcAmount
                              : selectedEmployee.ctc;
                          const percentageChange = previousCtc
                            ? calculatePercentageChange(
                                previousCtc,
                                item.ctcAmount
                              )
                            : null;

                          let percentageText = "-";
                          if (percentageChange !== null) {
                            if (percentageChange > 0) {
                              percentageText = `${Math.abs(
                                percentageChange
                              ).toFixed(2)}% Increase`;
                            } else if (percentageChange < 0) {
                              percentageText = `${Math.abs(
                                percentageChange
                              ).toFixed(2)}% Decrease`;
                            } else {
                              percentageText = "No Change";
                            }
                          }

                          return {
                            "Effective Date": formatDate(item.effectiveDate),
                            "Arrears Effective Date": formatDate(item.arrearsEffectiveDate),
                            "CTC Amount": item.ctcAmount
                              ? formatIndianNumber(item.ctcAmount)
                              : "N/A",
                            "Amount in Words": item.ctcAmount
                              ? `RS. ${numberToWords(item.ctcAmount)}`
                              : "N/A",
                            "Percentage Change": percentageText,
                            Reason: item.reason,
                            Designation: item.designation,
                            "Created Date": formatDate(item.createdDate),
                            "Created By": item.createdBy,
                          };
                        });

                        // Create CSV content
                        const headers = Object.keys(exportData[0]);
                        const csvContent = [
                          headers.join(","),
                          ...exportData.map((row) =>
                            headers
                              .map((header) => `"${row[header]}"`)
                              .join(",")
                          ),
                        ].join("\n");

                        // Create and download file
                        const blob = new Blob([csvContent], {
                          type: "text/csv;charset=utf-8;",
                        });
                        const link = document.createElement("a");
                        const url = URL.createObjectURL(blob);
                        link.setAttribute("href", url);
                        link.setAttribute(
                          "download",
                          `salary_history_${selectedEmployee.user?.name}_${
                            new Date().toISOString().split("T")[0]
                          }.csv`
                        );
                        link.style.visibility = "hidden";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        toast.success("Salary history exported successfully!");
                      }}
                      type="button"
                      className="rh-btn-secondary px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-95"
                    >
                      Export to Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSalaryHistoryModal(false)}
                      className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Transfer Modal */}
            {showTransferModal && selectedEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-6xl w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center pb-4 mb-0 border-b">
                    <h2 className="text-lg font-semibold">
                      Employee Transfer –{" "}
                      {selectedEmployee.user?.name ||
                        selectedEmployee.name ||
                        "N/A"}{" "}
                      –{" "}
                      {selectedEmployee.empCode ||
                        selectedEmployee.code ||
                        "N/A"}
                    </h2>
                    <button
                      onClick={() => setShowTransferModal(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="space-y-3">
                    {/* Effective Date */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Effective Date of Transfer{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <DatePicker
                        value={transferData.effectiveDate}
                        min={
                          selectedEmployee.dateOfJoin ||
                          selectedEmployee.joiningDate ||
                          selectedEmployee.joinDate
                        }
                        onChange={(e) =>
                          setTransferData((prev) => ({
                            ...prev,
                            effectiveDate: e.target.value,
                          }))
                        }
                        darkMode={darkMode}
                        className={`w-full p-1.5 border rounded-md text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>

                    {/* Masters Transfer Table */}
                    <div
                      className={`border rounded-md ${
                        darkMode ? "border-gray-600" : "border-gray-300"
                      }`}
                    >
                      <div
                        className={`p-2 border-b ${
                          darkMode
                            ? "border-gray-600 bg-gray-700"
                            : "border-gray-300 bg-gray-50"
                        }`}
                      >
                        <h3
                          className={`text-sm font-semibold ${
                            darkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          Transfer Details
                        </h3>
                      </div>
                      <div className="p-2">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          {/* Header */}
                          <div
                            className={`font-semibold ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Master Name
                          </div>
                          <div
                            className={`font-semibold ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Current Assignment
                          </div>
                          <div
                            className={`font-semibold ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Revised Assignment
                          </div>

                          {/* Branch */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Branch
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.branchId,
                              branchesList,
                              selectedEmployee,
                              'branch'
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newBranchId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newBranchId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Branch</option>
                              {branchesList
                                .filter(
                                  (branch) =>
                                    branch.id !== selectedEmployee.branchId
                                )
                                .map((branch) => (
                                  <option key={branch.id} value={branch.id}>
                                    {branch.name}
                                  </option>
                                ))}
                            </select>
                          </div>

                          {/* Department */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Department
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.departmentId,
                              departmentsList,
                              selectedEmployee,
                              'department'
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newDepartmentId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newDepartmentId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Department</option>
                              {departmentsList
                                .filter(
                                  (dept) =>
                                    dept.id !== selectedEmployee.departmentId
                                )
                                .map((dept) => (
                                  <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </option>
                                ))}
                            </select>
                          </div>

                          {/* Sub-Department */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Sub-Department
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.subDepartmentId,
                              subDepartmentsList,
                              selectedEmployee,
                              'subDepartment'
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newSubDepartmentId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newSubDepartmentId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Sub-Department</option>
                              {subDepartmentsList
                                .filter(
                                  (subDept) =>
                                    subDept.id !==
                                    selectedEmployee.subDepartmentId
                                )
                                .map((subDept) => (
                                  <option key={subDept.id} value={subDept.id}>
                                    {subDept.name}
                                  </option>
                                ))}
                            </select>
                          </div>

                          {/* Designation */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Designation
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.designationId,
                              designationsList
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newDesignationId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newDesignationId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Designation</option>
                              {designationsList.map((designation) => (
                                <option
                                  key={designation.id}
                                  value={designation.id}
                                >
                                  {designation.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Grade */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Grade
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.gradeId,
                              gradesList,
                              selectedEmployee,
                              'grade'
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newGradeId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newGradeId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Grade</option>
                              {gradesList.map((grade) => (
                                <option key={grade.id} value={grade.id}>
                                  {grade.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Category */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Category
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {getNameByIdFromList(
                              selectedEmployee.categoryId,
                              categoriesList,
                              selectedEmployee,
                              'category'
                            )}
                          </div>
                          <div className="py-1">
                            <select
                              value={transferData.newCategoryId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newCategoryId: e.target.value,
                                }))
                              }
                              className={`w-full p-1 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            >
                              <option value="">Select Category</option>
                              {categoriesList.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Reporting Manager */}
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Reporting Manager
                          </div>
                          <div
                            className={`py-1 ${
                              darkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {(() => {
                              const rmId =
                                selectedEmployee.reportingManagerId ??
                                selectedEmployee.reporting_manager_id;
                              if (!rmId) return "—";
                              const rm =
                                transferEmployeesList.find(
                                  (e) =>
                                    e.id === rmId ||
                                    e.id === parseInt(rmId, 10)
                                ) ||
                                selectedEmployee.reportingManager;
                              const name =
                                rm?.user?.name &&
                                (rm?.user?.lastName ?? "") !== ""
                                  ? `${rm.user.name} ${rm.user.lastName ?? ""}`.trim()
                                  : rm?.user?.name || rm?.name || rm?.empCode || "—";
                              return name;
                            })()}
                          </div>
                          <div className="py-1">
                            <SearchableSelect
                              id="newReportingManagerId"
                              label="New Reporting Manager"
                              value={transferData.newReportingManagerId}
                              onChange={(e) =>
                                setTransferData((prev) => ({
                                  ...prev,
                                  newReportingManagerId:
                                    e.target.value ?? "",
                                }))
                              }
                              staticOptions={[
                                { value: "", label: "No change" },
                                ...transferEmployeesList
                                  .filter(
                                    (emp) =>
                                      emp.id !== selectedEmployee?.id &&
                                      (emp.user?.name || emp.empCode)
                                  )
                                  .sort((a, b) =>
                                    (a.empCode || "").localeCompare(
                                      b.empCode || "",
                                      undefined,
                                      { numeric: true }
                                    )
                                  )
                                  .map((emp) => ({
                                    value: emp.id,
                                    label: `${emp.empCode || ""} - ${(emp.user?.name || "").trim()} ${(emp.user?.lastName ?? "").trim()}`.trim() || String(emp.id),
                                  })),
                              ]}
                              darkMode={darkMode}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Transfer Reason */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Transfer Reason <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={transferData.reason}
                        onChange={(e) =>
                          setTransferData((prev) => ({
                            ...prev,
                            reason: e.target.value,
                          }))
                        }
                        className={`w-full p-1.5 border rounded-md text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      >
                        <option value="">Select Reason</option>
                        <option value="Organisation Restructure">
                          Organisation Restructure
                        </option>
                        <option value="Performance">Performance</option>
                        <option value="Promotion">Promotion</option>
                        <option value="Departmental Requirements">
                          Departmental Requirements
                        </option>
                        <option value="Skill Development">
                          Skill Development
                        </option>
                        <option value="Career Growth">Career Growth</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Remarks */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Remarks
                      </label>
                      <textarea
                        value={transferData.remarks}
                        onChange={(e) =>
                          setTransferData((prev) => ({
                            ...prev,
                            remarks: e.target.value,
                          }))
                        }
                        rows={2}
                        placeholder="Additional comments or notes..."
                        className={`w-full p-1.5 border rounded-md text-sm ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                        }`}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center p-6 border-t flex-shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            setLoadingTransferHistory(true);
                            setShowTransferHistoryModal(true);

                            const response =
                              await transferService.getTransferHistory(
                                selectedEmployee.id
                              );

                            if (response.success && response.data) {
                              // Transform API data to match frontend format
                              const history = response.data.map((item) => ({
                                id: item.id,
                                effectiveDate: item.effectiveDate,
                                fromBranch: item.fromBranch?.name || "N/A",
                                toBranch: item.toBranch?.name || "N/A",
                                fromDepartment:
                                  item.fromDepartment?.name || "N/A",
                                toDepartment: item.toDepartment?.name || "N/A",
                                fromDesignation:
                                  item.fromDesignation?.name || "N/A",
                                toDesignation:
                                  item.toDesignation?.name || "N/A",
                                reason: item.reason,
                                remarks: item.remarks,
                                createdDate: item.createdAt,
                                createdBy: item.creator?.name || "Unknown",
                              }));
                              setTransferHistory(history);
                            } else {
                              setTransferHistory([]);
                            }
                          } catch (error) {
                            console.error(
                              "Error loading transfer history:",
                              error
                            );
                            toast.error("Failed to load transfer history");
                            setTransferHistory([]);
                          } finally {
                            setLoadingTransferHistory(false);
                          }
                        }}
                        disabled={loadingTransferHistory}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          loadingTransferHistory
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : darkMode
                            ? "bg-gray-600 text-white hover:bg-gray-700"
                            : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                        } transition-colors`}
                      >
                        {loadingTransferHistory ? "Loading..." : "View History"}
                      </button>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowTransferModal(false);
                            setTransferData({
                              effectiveDate: "",
                              newBranchId: "",
                              newDepartmentId: "",
                              newSubDepartmentId: "",
                              newDesignationId: "",
                              newGradeId: "",
                              newCategoryId: "",
                              newReportingManagerId: "",
                              reason: "",
                              remarks: "",
                            });
                          }}
                          className={`px-4 py-2 rounded-md text-sm font-medium ${
                            darkMode
                              ? "bg-gray-600 text-white hover:bg-gray-700"
                              : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                          } transition-colors`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (
                              !transferData.effectiveDate ||
                              !transferData.reason
                            ) {
                              toast.error(
                                "Please fill in Effective Date and Transfer Reason"
                              );
                              return;
                            }
                            // ✅ Validation: Transfer effective date must be today or a future date
                            if (
                              transferData.effectiveDate &&
                              !isTodayOrFuture(transferData.effectiveDate)
                            ) {
                              toast.error(
                                "Transfer effective date must be today or a future date."
                              );
                              return;
                            }

                            // Check if at least one master field has been changed (including reporting manager)
                            const hasChanges =
                              transferData.newBranchId ||
                              transferData.newDepartmentId ||
                              transferData.newSubDepartmentId ||
                              transferData.newDesignationId ||
                              transferData.newGradeId ||
                              transferData.newCategoryId ||
                              transferData.newReportingManagerId;

                            if (!hasChanges) {
                              toast.error(
                                "Please select at least one master field to transfer"
                              );
                              return;
                            }

                            try {
                              // Save transfer to database
                              const transferPayload = {
                                employeeId: selectedEmployee.id,
                                effectiveDate: transferData.effectiveDate,
                                newBranchId: transferData.newBranchId,
                                newDepartmentId: transferData.newDepartmentId,
                                newSubDepartmentId:
                                  transferData.newSubDepartmentId,
                                newDesignationId: transferData.newDesignationId,
                                newGradeId: transferData.newGradeId,
                                newCategoryId: transferData.newCategoryId,
                                newReportingManagerId:
                                  transferData.newReportingManagerId,
                                reason: transferData.reason,
                                remarks: transferData.remarks,
                              };

                              const response =
                                await transferService.createTransfer(
                                  transferPayload
                                );

                              if (response.success) {
                                addEmployeeInsight({
                                  action: "Transfer",
                                  employee: selectedEmployee,
                                  pendingAt: "HRBP",
                                });
                                toast.success(
                                  "Employee transfer saved successfully!"
                                );

                                setShowTransferModal(false);
                                setTransferData({
                                  effectiveDate: "",
                                  newBranchId: "",
                                  newDepartmentId: "",
                                  newSubDepartmentId: "",
                                  newDesignationId: "",
                                  newGradeId: "",
                                  newCategoryId: "",
                                  newReportingManagerId: "",
                                  reason: "",
                                  remarks: "",
                                });

                                // Refresh employee list
                                fetchEmployees();
                              } else {
                                throw new Error(
                                  response.message || "Transfer save failed"
                                );
                              }
                            } catch (error) {
                              console.error(
                                "Error updating employee transfer:",
                                error
                              );
                              const message =
                                error.response?.data?.message ||
                                error.message ||
                                "Error updating employee transfer";
                              toast.error(message);
                            }
                          }}
                          type="button"
                          className="rounded-md px-4 py-2 text-sm font-semibold transition-all hover:opacity-95 rh-btn-primary"
                        >
                          Save Transfer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transfer History Modal */}
            {showTransferHistoryModal && selectedEmployee && (
              <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
                <div
                  className={`flex h-[80%] w-[90%] max-w-6xl flex-col rounded-xl p-6 shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      Transfer History - {selectedEmployee.user?.name}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowTransferHistoryModal(false)}
                      className="text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="flex-1 overflow-hidden">
                    <div className="h-full overflow-y-auto rounded-lg border border-[#165b5d]/30 dark:border-[#4C4C4C]">
                      <table className="w-full text-sm">
                        <thead
                          className={`sticky top-0 z-10 ${
                            darkMode
                              ? "rh-table-header-row-dark"
                              : "rh-table-header-row"
                          }`}
                        >
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">
                              Effective Date
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              From Branch
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              To Branch
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              From Department
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              To Department
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              From Designation
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              To Designation
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Reason
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Created Date
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              Created By
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className={`${
                            darkMode ? "divide-[#4C4C4C]" : "divide-gray-200"
                          } divide-y`}
                        >
                          {transferHistory.map((item, index) => (
                            <tr
                              key={item.id}
                              className={`transition duration-150 ${
                                darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]"
                              } ${
                                darkMode
                                  ? "hover:bg-[#2e2e2e]"
                                  : "hover:bg-redhalo-mint/40"
                              }`}
                            >
                              <td className="px-4 py-2">
                                {formatDate(item.effectiveDate)}
                              </td>
                              <td className="px-4 py-2">{item.fromBranch}</td>
                              <td className="px-4 py-2">{item.toBranch}</td>
                              <td className="px-4 py-2">
                                {item.fromDepartment}
                              </td>
                              <td className="px-4 py-2">{item.toDepartment}</td>
                              <td className="px-4 py-2">
                                {item.fromDesignation}
                              </td>
                              <td className="px-4 py-2">
                                {item.toDesignation}
                              </td>
                              <td className="px-4 py-2">{item.reason}</td>
                              <td className="px-4 py-2">
                                {formatDate(item.createdDate)}
                              </td>
                              <td className="px-4 py-2">{item.createdBy}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                    <button
                      onClick={() => {
                        // Export to Excel functionality
                        const exportData = transferHistory.map((item) => ({
                          "Effective Date": formatDate(item.effectiveDate),
                          "From Branch": item.fromBranch,
                          "To Branch": item.toBranch,
                          "From Department": item.fromDepartment,
                          "To Department": item.toDepartment,
                          "From Designation": item.fromDesignation,
                          "To Designation": item.toDesignation,
                          Reason: item.reason,
                          Remarks: item.remarks,
                          "Created Date": formatDate(item.createdDate),
                          "Created By": item.createdBy,
                        }));

                        // Create CSV content
                        const headers = Object.keys(exportData[0]);
                        const csvContent = [
                          headers.join(","),
                          ...exportData.map((row) =>
                            headers
                              .map((header) => `"${row[header]}"`)
                              .join(",")
                          ),
                        ].join("\n");

                        // Create and download file
                        const blob = new Blob([csvContent], {
                          type: "text/csv;charset=utf-8;",
                        });
                        const link = document.createElement("a");
                        const url = URL.createObjectURL(blob);
                        link.setAttribute("href", url);
                        link.setAttribute(
                          "download",
                          `transfer_history_${selectedEmployee.user?.name}_${
                            new Date().toISOString().split("T")[0]
                          }.csv`
                        );
                        link.style.visibility = "hidden";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        toast.success(
                          "Transfer history exported successfully!"
                        );
                      }}
                      type="button"
                      className="rh-btn-secondary px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-95"
                    >
                      Export to Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTransferHistoryModal(false)}
                      className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Completion Modal */}
            {showProfileCompletionModal && profileCompletionData && (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                <div
                  className={`w-full max-w-2xl rounded-xl p-6 shadow-xl ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      Profile Completion Details
                    </h3>
                    <button
                      onClick={() => setShowProfileCompletionModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium">Employee:</span>
                      <span className="text-redhalo-navy font-semibold">
                        {profileCompletionData.employee.user?.name}{" "}
                        {profileCompletionData.employee.user?.lastName || ""}
                      </span>
                      <span className="text-gray-500">
                        ({profileCompletionData.employee.empCode})
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span className="font-medium">Overall Completion:</span>
                      <div
                        className={`rounded-md px-3 py-1 text-sm font-bold ${
                          profileCompletionData.percentage >= 80
                            ? "bg-emerald-600 text-white"
                            : profileCompletionData.percentage >= 60
                            ? "bg-amber-500 text-white"
                            : "bg-redhalo-red text-white"
                        }`}
                      >
                        {profileCompletionData.percentage}%
                      </div>
                    </div>
                  </div>

                  {profileCompletionData.pendingSections.length > 0 ? (
                    <div>
                      <h4 className="mb-3 font-medium text-amber-800 dark:text-amber-300">
                        Sections with Blank Fields:
                      </h4>
                      <div className="space-y-2">
                        {profileCompletionData.pendingSections.map(
                          (section, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between rounded-md border border-amber-200/80 bg-amber-50/90 p-3 dark:border-amber-700/50 dark:bg-amber-950/30"
                            >
                              <div>
                                <span className="font-medium text-amber-950 dark:text-amber-100">
                                  {section.name}
                                </span>
                                <div className="text-sm text-amber-800 dark:text-amber-200/90">
                                  {section.blankFields} blank field
                                  {section.blankFields !== 1 ? "s" : ""}{" "}
                                  remaining
                                </div>
                              </div>
                              <div
                                className={`rounded-md px-2 py-1 text-sm font-bold ${
                                  section.percentage >= 80
                                    ? "bg-emerald-600 text-white"
                                    : section.percentage >= 50
                                    ? "bg-amber-500 text-white"
                                    : "bg-redhalo-red text-white"
                                }`}
                              >
                                {section.percentage}%
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="py-4 text-center">
                      <div className="font-medium text-emerald-700 dark:text-emerald-400">
                        All sections are complete!
                      </div>
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Profile is fully updated
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowProfileCompletionModal(false)}
                      className="rounded-md bg-redhalo-navy px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-redhalo-navy-light"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Resignation Modal */}
            {showResignationModal && selectedEmployee && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div
                  className={`p-6 rounded-md shadow-xl max-w-4xl w-full ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  <div className="flex justify-between items-center pb-4 mb-0 border-b">
                    <h2 className="text-lg font-semibold">
                      Employee Resignation - {selectedEmployee.user?.name}
                    </h2>
                    <button
                      onClick={() => {
                        setShowResignationModal(false);
                        setResignationData({
                          resignationDate: "",
                          relievingDate: "",
                          reason: "",
                          noticePeriodDays: 0,
                          noticePeriodWaiverDays: 0,
                          noticePeriodToDeduct: 0,
                          isRetroResignation: false,
                          proofDocument: null,
                        });
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* First Row: Resignation Date with Checkbox and Relieving Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Resignation Date with Backdated Checkbox */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Resignation Date{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        {(() => {
                          // Find applicable policy for this employee
                          const referenceDate =
                            resignationData.resignationDate ||
                            new Date().toISOString().split("T")[0];
                          const applicablePolicy = findApplicableSeparationPolicy(
                            selectedEmployee,
                            referenceDate,
                            false // Don't filter by retro resignation when checking policy
                          );
                          const allowsRetroResignation =
                            applicablePolicy?.allowResignationsPriorToCurrentDate === true;

                          return (
                            <>
                        <DatePicker
                          value={resignationData.resignationDate || ""}
                          min={
                            selectedEmployee.dateOfJoin ||
                            selectedEmployee.joiningDate ||
                            selectedEmployee.joinDate
                          }
                          onChange={(e) =>
                            handleResignationDataChange(
                              "resignationDate",
                              e.target.value
                            )
                          }
                                disabled={
                                  allowsRetroResignation &&
                                  !resignationData.isRetroResignation
                                }
                          className={`w-full p-1.5 border rounded-md text-sm ${
                                  allowsRetroResignation &&
                            !resignationData.isRetroResignation
                              ? darkMode
                                ? "bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
                                : "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                              : darkMode
                              ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                          }`}
                          validateOnType={true}
                        />
                              {/* Backdated Resignation Checkbox - Only show if policy allows it */}
                              {allowsRetroResignation && (
                                <>
                        <div className="flex items-center space-x-2 mt-2">
                          <input
                            type="checkbox"
                            id="retroResignation"
                            checked={resignationData.isRetroResignation}
                            onChange={(e) =>
                              handleResignationDataChange(
                                "isRetroResignation",
                                e.target.checked
                              )
                            }
                            className="rounded"
                          />
                          <label
                            htmlFor="retroResignation"
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Backdated Resignation
                          </label>
                        </div>
                        {!resignationData.isRetroResignation && (
                          <p
                            className={`text-sm mt-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Check "Backdated Resignation" to edit this date
                          </p>
                        )}
                                </>
                              )}
                            </>
                          );
                        })()}

                        {/* Proof Document Upload (shown only for backdated resignation) */}
                        {resignationData.isRetroResignation && (
                          <div className="mt-3">
                            <label
                              className={`block text-sm font-medium mb-1 ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Proof Document (Email Copy){" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleResignationDataChange(
                                  "proofDocument",
                                  e.target.files[0]
                                )
                              }
                              className={`w-full p-1.5 border rounded-md text-sm ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                  : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              }`}
                            />
                            {resignationData.proofDocument && (
                              <p
                                className={`mt-1 text-sm ${
                                  darkMode ? "text-emerald-400" : "text-emerald-700"
                                }`}
                              >
                                Selected: {resignationData.proofDocument.name}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Relieving Date */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Relieving Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          value={resignationData.relievingDate || ""}
                          min={
                            resignationData.resignationDate ||
                            selectedEmployee.dateOfJoin ||
                            selectedEmployee.joiningDate ||
                            selectedEmployee.joinDate
                          }
                          onChange={(e) =>
                            handleResignationDataChange(
                              "relievingDate",
                              e.target.value
                            )
                          }
                          className={`w-full p-1.5 border rounded-md text-sm ${
                            darkMode
                              ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                          }`}
                          validateOnType={true}
                        />
                      </div>
                    </div>

                    {/* Second Row: Reason */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Reason */}
                      {/* Second Row: Reason */}

                      {/* Reason */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Reason <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={resignationData.reason} // This should now store the ID
                          onChange={(e) =>
                            handleResignationDataChange(
                              "reason",
                              e.target.value // Stores the ID from the selected option's value
                            )
                          }
                          className={`w-full p-1.5 border rounded-md text-sm ${
                            darkMode
                              ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                              : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                          }`}
                        >
                          <option value="">Select Reason</option>
                          {/* Map over the fetched resignationReasonsList */}
                          {resignationReasonsList.map((reason) => (
                            <option key={reason.id} value={reason.id}>
                              {" "}
                              {/* VALUE is the ID */}
                              {reason.name} {/* Display the name */}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Notice Period Information */}
                    <div
                      className={`p-3 rounded-md border ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-300"
                      }`}
                    >
                      <h3
                        className={`text-sm font-semibold mb-2 ${
                          darkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Notice Period Details
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        {/* Notice Period Days (Auto-calculated from policy) */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Notice Period (Days)
                          </label>
                          <div
                            className={`p-1.5 rounded-md text-sm font-medium ${
                              darkMode
                                ? "bg-gray-600 text-gray-200"
                                : "bg-white text-gray-800 border"
                            }`}
                          >
                            {(() => {
                              // Find applicable policy for this employee
                              // Use resignation date if available, otherwise current date
                              const referenceDate =
                                resignationData.resignationDate ||
                                new Date().toISOString().split("T")[0];
                              const applicablePolicy = findApplicableSeparationPolicy(
                                selectedEmployee,
                                referenceDate,
                                resignationData.isRetroResignation
                              );

                              return applicablePolicy
                                ? applicablePolicy.noticePeriodDays
                                : 30;
                            })()}{" "}
                            days
                          </div>
                        </div>

                        {/* Notice Duration (Days between resignation and relieving date) */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Notice Duration
                          </label>
                          <div
                            className={`p-1.5 rounded-md text-sm font-medium ${
                              darkMode
                                ? "bg-gray-600 text-gray-200"
                                : "bg-white text-gray-800 border"
                            }`}
                          >
                            {(() => {
                              // Calculate days between resignation date and relieving date
                              if (
                                resignationData.resignationDate &&
                                resignationData.relievingDate
                              ) {
                                const resignationDate = new Date(
                                  resignationData.resignationDate
                                );
                                const relievingDate = new Date(
                                  resignationData.relievingDate
                                );
                                const timeDiff =
                                  relievingDate.getTime() -
                                  resignationDate.getTime();
                                const daysDiff = Math.ceil(
                                  timeDiff / (1000 * 3600 * 24)
                                );
                                return daysDiff;
                              }
                              return 0;
                            })()}{" "}
                            days
                          </div>
                        </div>

                        {/* Notice Period Waiver Days */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Notice Period Waiver Days
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={resignationData.noticePeriodWaiverDays}
                            onChange={(e) =>
                              handleResignationDataChange(
                                "noticePeriodWaiverDays",
                                parseInt(e.target.value) || 0
                              )
                            }
                            className={`w-full p-1.5 border rounded-md text-sm ${
                              darkMode
                                ? "bg-[#2C2C2C] border-[#4C4C4C] text-[#E0E0E0] focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                                : "border-gray-300 bg-white text-gray-900 focus:border-redhalo-red focus:outline-none focus:ring-2 focus:ring-redhalo-red/25"
                            }`}
                          />
                        </div>

                        {/* Notice Period to Deduct (Auto-calculated) */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Notice Period to Deduct
                          </label>
                          <div
                            className={`p-1.5 rounded-md text-sm font-medium ${
                              darkMode
                                ? "bg-gray-600 text-gray-200"
                                : "bg-white text-gray-800 border"
                            }`}
                          >
                            {(() => {
                              // Find applicable policy for this employee
                              // Use resignation date if available, otherwise current date
                              const referenceDate =
                                resignationData.resignationDate ||
                                new Date().toISOString().split("T")[0];
                              const applicablePolicy = findApplicableSeparationPolicy(
                                selectedEmployee,
                                referenceDate,
                                resignationData.isRetroResignation
                              );

                              const policyNoticeDays = applicablePolicy
                                ? applicablePolicy.noticePeriodDays
                                : 30;

                              // Calculate actual days worked between resignation and relieving date
                              let actualDaysWorked = 0;
                              if (
                                resignationData.resignationDate &&
                                resignationData.relievingDate
                              ) {
                                const resignationDate = new Date(
                                  resignationData.resignationDate
                                );
                                const relievingDate = new Date(
                                  resignationData.relievingDate
                                );
                                const timeDiff =
                                  relievingDate.getTime() -
                                  resignationDate.getTime();
                                actualDaysWorked = Math.ceil(
                                  timeDiff / (1000 * 3600 * 24)
                                );
                              }

                              // Calculate: Policy Notice Days - Actual Days Worked - Waiver Days
                              const deductibleDays = Math.max(
                                0,
                                policyNoticeDays -
                                  actualDaysWorked -
                                  resignationData.noticePeriodWaiverDays
                              );

                              return deductibleDays;
                            })()}{" "}
                            days
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end items-center p-6 border-t flex-shrink-0 space-x-3">
                      <button
                        onClick={() => {
                          setShowResignationModal(false);
                          setResignationData({
                            resignationDate: "",
                            relievingDate: "",
                            reason: "",
                            noticePeriodDays: 0,
                            noticePeriodWaiverDays: 0,
                            noticePeriodToDeduct: 0,
                            isRetroResignation: false,
                            proofDocument: null,
                          });
                        }}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          darkMode
                            ? "bg-gray-600 text-white hover:bg-gray-700"
                            : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                        } transition-colors`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            !resignationData.resignationDate ||
                            !resignationData.relievingDate ||
                            !resignationData.reason
                          ) {
                            toast.error("Please fill in all required fields");
                            return;
                          }
                          // ✅ Validation: Relieving date cannot be before resignation date
                          if (
                            resignationData.relievingDate &&
                            resignationData.resignationDate
                          ) {
                            if (
                              new Date(resignationData.relievingDate) <
                              new Date(resignationData.resignationDate)
                            ) {
                              toast.error(
                                "Relieving date cannot be before resignation date."
                              );
                              return;
                            }
                          }

                          if (
                            resignationData.isRetroResignation &&
                            !resignationData.proofDocument
                          ) {
                            toast.error(
                              "Please upload proof document for retro resignation"
                            );
                            return;
                          }

                          try {
                            // Validate retro resignation against policy
                            if (resignationData.isRetroResignation) {
                              const referenceDate =
                                resignationData.resignationDate ||
                                new Date().toISOString().split("T")[0];
                              const applicablePolicy =
                                findApplicableSeparationPolicy(
                                  selectedEmployee,
                                  referenceDate,
                                  true
                                );

                              if (
                                !applicablePolicy ||
                                !applicablePolicy.allowResignationsPriorToCurrentDate
                              ) {
                                toast.error(
                                  "No policy found that allows resignations prior to current date for this employee."
                                );
                                return;
                              }
                            }

                            // Calculate notice period to deduct
                            const referenceDate =
                              resignationData.resignationDate ||
                              new Date().toISOString().split("T")[0];
                            const applicablePolicy = findApplicableSeparationPolicy(
                              selectedEmployee,
                              referenceDate,
                              resignationData.isRetroResignation
                            );

                            const policyNoticeDays = applicablePolicy
                              ? applicablePolicy.noticePeriodDays
                              : 30;

                            // Calculate actual days worked between resignation and relieving date
                            let actualDaysWorked = 0;
                            if (
                              resignationData.resignationDate &&
                              resignationData.relievingDate
                            ) {
                              const resignationDate = new Date(
                                resignationData.resignationDate
                              );
                              const relievingDate = new Date(
                                resignationData.relievingDate
                              );
                              const timeDiff =
                                relievingDate.getTime() -
                                resignationDate.getTime();
                              actualDaysWorked = Math.ceil(
                                timeDiff / (1000 * 3600 * 24)
                              );
                            }

                            const noticePeriodToDeduct = Math.max(
                              0,
                              policyNoticeDays -
                                actualDaysWorked -
                                resignationData.noticePeriodWaiverDays
                            );

                            // Save resignation to database
                            const resignationPayload = {
                              employeeId: selectedEmployee.id,
                              resignationDate: resignationData.resignationDate,
                              relievingDate: resignationData.relievingDate,
                              reasonId: resignationData.reason,
                              reason: "",
                              noticePeriodDays: policyNoticeDays,
                              noticePeriodWaiverDays:
                                resignationData.noticePeriodWaiverDays,
                              noticePeriodToDeduct: noticePeriodToDeduct,
                              isRetroResignation:
                                resignationData.isRetroResignation,
                              remarks: "",
                            };

                            const response =
                              await resignationService.createResignation(
                                resignationPayload
                              );

                            if (response.success) {
                              addEmployeeInsight({
                                action: "Resignation",
                                employee: selectedEmployee,
                                pendingAt: "Department Head",
                              });
                              toast.success(
                                "Employee resignation processed successfully!"
                              );

                              setShowResignationModal(false);
                              setResignationData({
                                resignationDate: "",
                                relievingDate: "",
                                reason: "",
                                noticePeriodDays: 0,
                                noticePeriodWaiverDays: 0,
                                noticePeriodToDeduct: 0,
                                isRetroResignation: false,
                                proofDocument: null,
                              });

                              // Refresh employee list
                              fetchEmployees();
                            } else {
                              throw new Error(
                                response.message ||
                                  "Resignation processing failed"
                              );
                            }
                          } catch (error) {
                            console.error(
                              "Error processing resignation:",
                              error
                            );
                            toast.error(
                              error.message || "Error processing resignation"
                            );
                          }
                        }}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          darkMode
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-red-500 text-white hover:bg-red-600"
                        } transition-colors`}
                      >
                        Submit Resignation
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Structures Modal */}
            {showStructuresModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div
                  className={`p-4 rounded-md shadow-xl w-[80%] h-[80%] flex flex-col ${
                    darkMode
                      ? "bg-gray-800 text-white"
                      : "bg-white text-gray-800"
                  }`}
                >
                  {/* Fixed Header */}
                  <div className="flex justify-between items-center mb-3 flex-shrink-0">
                    <h2 className="text-lg font-bold">Employee Settings</h2>
                    <button
                      onClick={() => setShowStructuresModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Fixed Sub-tabs - Masters.jsx style */}
                  <div className="mb-2 flex-shrink-0">
                    <div className="flex border-b border-gray-300">
                      {[
                        "generalSettings",
                        "employeeCode",
                        "policies",
                        "salaryStructure",
                        "lettersManagement",
                      ].map((tab) => (
                        <button
                          key={tab}
                          className={`px-3 py-1 text-sm font-medium transition-colors ${
                            activeStructureTab === tab
                              ? `${
                                  darkMode
                                    ? "border-b-2 border-redhalo-red text-redhalo-red"
                                     : "border-b-2 border-redhalo-red text-redhalo-navy"
                                }`
                              : `${
                                  darkMode
                                    ? "text-gray-300 hover:text-white"
                                    : "text-gray-600 hover:text-gray-800"
                                }`
                          }`}
                          onClick={() => setActiveStructureTab(tab)}
                        >
                          {tab === "generalSettings"
                            ? "General Settings"
                            : tab === "salaryStructure"
                            ? "Structures"
                            : tab === "policies"
                            ? "Policies"
                            : tab
                                .split(/(?=[A-Z])/)
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* General Settings tab content */}
                  {activeStructureTab === "generalSettings" && (
                    <div className="flex-1 overflow-y-auto flex flex-col">
                      <div
                        className={`p-4 rounded-md ${
                          darkMode ? "bg-gray-800" : "bg-white"
                        } shadow-md`}
                      >
                        <div className="flex flex-nowrap items-center gap-3 overflow-x-auto pb-0.5 sm:gap-4">
                          <h3
                            className={`shrink-0 text-sm font-semibold sm:text-base ${
                              darkMode ? "text-white" : "text-gray-800"
                            }`}
                          >
                            Enable ESS
                          </h3>
                          <div className="flex shrink-0 items-center gap-4 sm:gap-6">
                            <label className="flex cursor-pointer items-center">
                              <input
                                type="radio"
                                name="enableESS"
                                checked={enableESS === true}
                                onChange={() => setEnableESS(true)}
                                className="mr-2"
                              />
                              <span
                                className={`text-sm whitespace-nowrap ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Yes
                              </span>
                            </label>
                            <label className="flex cursor-pointer items-center">
                              <input
                                type="radio"
                                name="enableESS"
                                checked={enableESS === false}
                                onChange={() => setEnableESS(false)}
                                className="mr-2"
                              />
                              <span
                                className={`text-sm whitespace-nowrap ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                No
                              </span>
                            </label>
                          </div>
                          {enableESS && (
                            <label className="flex shrink-0 cursor-pointer items-center gap-2">
                              <input
                                type="checkbox"
                                checked={enableESSByDefault}
                                onChange={(e) =>
                                  setEnableESSByDefault(e.target.checked)
                                }
                                className="shrink-0 rounded border-0 text-redhalo-red shadow-sm focus:ring focus:ring-redhalo-red/25"
                              />
                              <span
                                className={`text-xs sm:text-sm whitespace-nowrap ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Enable ESS by default (new employees will have
                                Send Invite checked by default)
                              </span>
                            </label>
                          )}
                          <div className="ml-auto shrink-0 pl-2">
                            <button
                            type="button"
                            onClick={async () => {
                              let companyData = localStorage.getItem("companyData");
                              let companyId = companyData?.id || null;
                              if (!companyId) {
                                try {
                                  const d = JSON.parse(
                                    localStorage.getItem("companyData") || "{}"
                                  );
                                  companyId = d.id || d.companyId || null;
                                } catch (_) {
                                  // ignore parse error
                                }
                              }
                              if (!companyId) {
                                toast.error("Company not found. Please log in again.");
                                return;
                              }
                              setGeneralSettingsSaving(true);
                              try {
                                const raw = localStorage.getItem("companyData");
                                const existing = raw ? JSON.parse(raw) : {};
                                const payload = {
                                  ...existing,
                                  enableESS,
                                  enableESSByDefault: enableESS ? enableESSByDefault : false,
                                };
                                await companyService.updateCompanyData(
                                  companyId,
                                  payload
                                );
                                const updated = {
                                  ...existing,
                                  ...payload,
                                };
                                localStorage.setItem(
                                  "companyData",
                                  JSON.stringify(updated)
                                );
                                window.dispatchEvent(
                                  new CustomEvent("companyDataUpdated", {
                                    detail: updated,
                                  })
                                );
                                toast.success("General settings saved.");
                              } catch (err) {
                                toast.error(
                                  err?.message || "Failed to save general settings"
                                );
                              } finally {
                                setGeneralSettingsSaving(false);
                              }
                            }}
                            disabled={generalSettingsSaving}
                            className={`rounded-md px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:py-1.5 sm:text-sm ${
                              darkMode
                                ? "rh-btn-primary hover:opacity-95"
                                : "rh-btn-primary"
                            } transition-colors disabled:opacity-50`}
                          >
                            {generalSettingsSaving ? "Saving..." : "Save"}
                          </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fixed Employee Code Structure Header */}
                  {activeStructureTab === "employeeCode" && (
                    <div className="flex justify-between items-center mb-3 flex-shrink-0">
                      <div>
                        <h2
                          className={`text-lg font-bold mb-1 ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          Employee Code Structure
                        </h2>
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Configure employee code generation rules and patterns.
                        </p>
                      </div>
                      <button
                        id="viewHistoryButton"
                        onClick={() => {
                          // console.log('View History Button Clicked');
                          // // This will be handled by the EmployeeCodeStructure component
                          const event = new CustomEvent("toggleHistory");
                          window.dispatchEvent(event);
                        }}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          darkMode
                            ? "bg-gray-700 text-white hover:bg-gray-600"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } transition-colors`}
                      >
                        View History
                      </button>
                    </div>
                  )}

                  {/* Manual/Automated Pattern Selection */}
                  {activeStructureTab === "employeeCode" && (
                    <div
                      className={`p-3 rounded-md ${
                        darkMode ? "bg-gray-700" : "bg-gray-50"
                      } mb-3 flex-shrink-0`}
                    >
                      <h3
                        className={`text-base font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Employee Code Pattern Type
                      </h3>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="patternType"
                            value="manual"
                            checked={patternType === "manual"}
                            onChange={(e) => setPatternType(e.target.value)}
                            className="mr-2"
                          />
                          <span
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Manual
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="patternType"
                            value="automated"
                            checked={patternType === "automated"}
                            onChange={(e) => setPatternType(e.target.value)}
                            className="mr-2"
                          />
                          <span
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Automated
                          </span>
                        </label>
                      </div>
                      {patternType === "manual" && (
                        <div className="mt-3 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label
                                className={`block text-sm font-medium mb-1 ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Effective Date{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <DatePicker
                                value={manualEffectiveDate}
                                onChange={(e) =>
                                  setManualEffectiveDate(e.target.value)
                                }
                                min={getTodayDate()}
                                required
                                darkMode={darkMode}
                                className={`w-full px-3 py-2 rounded-md border ${
                                  darkMode
                                    ? "bg-gray-700 border-gray-600 text-white focus:border-redhalo-red"
                                    : "bg-white border-gray-300 text-gray-900 focus:border-redhalo-red"
                                } focus:outline-none focus:ring-1 focus:ring-redhalo-red/35`}
                              />
                              <p
                                className={`text-sm mt-1 ${
                                  darkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Select the date from when manual pattern should
                                be effective (today or future)
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={saveManualPattern}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                              darkMode
                                ? "rh-btn-primary hover:opacity-95"
                                : "rh-btn-primary"
                            } transition-colors`}
                          >
                            Save Manual Pattern
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto">
                    <EmployeeStructuresContent
                      activeStructureTab={activeStructureTab}
                      darkMode={darkMode}
                      branchesList={branchesList}
                      departmentsList={departmentsList}
                      designationsList={designationsList}
                      subDepartmentsList={subDepartmentsList}
                      gradesList={gradesList}
                      categoriesList={categoriesList}
                      patternType={patternType}
                      employees={employees}
                      separationPolicies={separationPolicies}
                      setSeparationPolicies={setSeparationPolicies}
                      fetchSeparationPolicies={fetchSeparationPolicies}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Employee Edit Form Modal */}
            {showForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                <div
                  className={`p-6 rounded-md shadow-xl w-[90vw] h-[90vh] overflow-hidden flex flex-col ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-white text-gray-900"
                  }`}
                >
                  {/* Modal Header */}
                  <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-300 dark:border-gray-600 flex-shrink-0">
                    <h2
                      className={`text-2xl font-semibold ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`}
                    >
                      {selectedEmployee?.id
                        ? `${selectedEmployee?.editLocked ? "Employee Details" : "Edit Employee"}: ${
                            selectedEmployee?.empCode || "N/A"
                          } - ${
                            selectedEmployee?.user?.name ||
                            selectedEmployee?.user?.firstName ||
                            "N/A"
                          } ${selectedEmployee?.user?.lastName || ""}`.trim()
                        : "Add New Employee"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setIsRejoin(false);
                        setRejoinSelectedEmployeeId(null);
                        setRejoinPrefillData(null);
                        setRejoinEmployeeList([]);
                        fetchEmployees();
                      }}
                      className={`text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full ${
                        darkMode
                          ? "hover:bg-[#3C3C3C] text-gray-400 hover:text-gray-200"
                          : ""
                      }`}
                      title="Close"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {/* Modal Content - min-h-0 so nested flex-1 + overflow-y-auto can shrink and scroll above footer */}
                  <div className="flex min-h-0 flex-1 flex-col">
                    {selectedEmployee?.editLocked === true && (
                      <div
                        className={`flex-shrink-0 mb-4 p-3 border rounded-md text-sm ${
                          darkMode
                            ? "bg-amber-900/20 border-amber-700 text-amber-200"
                            : "bg-amber-50 border-amber-200 text-amber-800"
                        }`}
                      >
                        This is a locked historical employee record. You can view all details, but editing is not allowed.
                      </div>
                    )}

                    <div
                      className={`flex min-h-0 flex-1 flex-col ${
                        selectedEmployee?.editLocked === true ? "opacity-80" : ""
                      }`}
                    >
                      <EmployeeProfileDetails
                        employee={selectedEmployee?.id ? selectedEmployee : (rejoinPrefillData ?? selectedEmployee)}
                        darkMode={darkMode}
                        enableESS={enableESS}
                        enableESSByDefault={enableESS ? enableESSByDefault : false}
                        onCancel={() => {
                          setShowForm(false);
                          setIsRejoin(false);
                          setRejoinSelectedEmployeeId(null);
                          setRejoinPrefillData(null);
                          setRejoinEmployeeList([]);
                          fetchEmployees();
                        }}
                        onOnboardSuccess={() => {
                          setShowForm(false);
                          setIsRejoin(false);
                          setRejoinSelectedEmployeeId(null);
                          setRejoinPrefillData(null);
                          setRejoinEmployeeList([]);
                          fetchEmployees();
                        }}
                        isRejoinPrefill={!!rejoinPrefillData}
                        isRejoin={isRejoin}
                        setIsRejoin={setIsRejoin}
                        rejoinSelectedEmployeeId={rejoinSelectedEmployeeId}
                        setRejoinSelectedEmployeeId={setRejoinSelectedEmployeeId}
                        rejoinEmployeeList={rejoinEmployeeList}
                        rejoinListLoading={rejoinListLoading}
                        rejoinFetchingEmployee={rejoinFetchingEmployee}
                        onRejoinSearch={onRejoinSearch}
                        forceReadOnly={selectedEmployee?.editLocked === true}
                        onRejoinSelectEmployee={async (id) => {
                        setRejoinSelectedEmployeeId(id);
                        setRejoinPrefillData(null);
                        if (!id) return;
                        setRejoinFetchingEmployee(true);
                        try {
                          const response = await employeeService.getEmployee(id);
                          const data = response?.data ?? response;
                          const emp = typeof data?.id !== "undefined" ? data : null;
                          if (!emp) {
                            toast.error("Failed to load employee data.");
                            return;
                          }
                          const withName = withTitleCaseName(emp);
                          const prefill = { ...withName };
                          delete prefill.id;
                          // Master details left blank for user to update
                          prefill.branchId = null;
                          prefill.designationId = null;
                          prefill.departmentId = null;
                          prefill.subDepartmentId = null;
                          prefill.gradeId = null;
                          prefill.categoryId = null;
                          prefill.reportingManagerId = null;
                          prefill.joiningDate = null;
                          prefill.dateOfJoin = null;
                          prefill.employmentType = null;
                          prefill.email = null;
                          // Official email id kept blank
                          if (prefill.user) {
                            prefill.user = { ...prefill.user, email: null };
                          }
                          // Rejoin: new stint is always Probation; clear resignation/relief
                          prefill.employmentStatus = "Probation";
                          prefill.resignationDate = null;
                          prefill.relievedDate = null;
                          prefill.terminationDate = null;
                          // Keep source relieved date for Org Details rejoin validation.
                          prefill.rejoinSourceRelievedDate = emp.relievedDate || null;
                          prefill.reason = null;
                          // New employee code for rejoining (will be set by auto code when form loads)
                          prefill.empCode = null;
                          prefill.employeeCode = null;
                          prefill.branch = null;
                          prefill.designation = null;
                          prefill.department = null;
                          prefill.subDepartment = null;
                          prefill.grade = null;
                          prefill.category = null;
                          prefill.reportingManager = null;
                          setRejoinPrefillData(prefill);
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to load employee data.");
                        } finally {
                          setRejoinFetchingEmployee(false);
                        }
                      }}
                      showFooterButtons={true}
                      onNavigateToTransfer={() => {
                        // Close the edit modal
                        setShowForm(false);
                        // Trigger the same logic as the Transfer action
                        if (selectedEmployee) {
                          handleAction("transfer", selectedEmployee);
                        }
                        setOpenActionDropdown(null);
                      }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {activeEmployeeTab === "reports" && canViewEmployeeReports && (
          String(activeEmployeeReportsSubTab).startsWith("export-") && canExportEmployeeRecords ? (
            <div className="flex-1 p-4">
              <div
                className={`rounded-md border shadow-lg ${
                  darkMode ? "border-[#3C3C3C] bg-[#2C2C2C]" : "border-gray-200 bg-white"
                }`}
              >
                <div className={`p-6 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className={`text-2xl font-bold ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>
                        {employeeExportSections.find((s) => s.key === activeEmployeeExportSubTab)?.label || "Employees Report"}
                      </h2>
                      <p className={`text-sm mt-1 ${darkMode ? "text-[#A6A9C8]" : "text-gray-600"}`}>
                        Generate and download comprehensive employee reports
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowFilterPopup(true)}
                        className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                          darkMode
                            ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                        }`}
                      >
                        <Filter size={18} className="mr-2" />
                        Filters
                      </button>
                      {canDownloadExports && (
                        <button
                          type="button"
                          disabled={exporting}
                          onClick={() => {
                            if (activeEmployeeExportSubTab === "all") {
                              setShowAllDataExportConfirmPopup(true);
                              return;
                            }
                            handleSectionExport(activeEmployeeExportSubTab);
                          }}
                          className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${
                            exporting
                              ? "cursor-not-allowed bg-gray-400 text-white"
                              : darkMode
                              ? "bg-redhalo-navy text-white hover:bg-redhalo-navy-light"
                              : "rh-btn-primary hover:opacity-95"
                          }`}
                        >
                          <Download size={18} className="mr-2" />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center py-12 min-h-[300px]">
                  <div className={`text-center max-w-md ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    <div className="mb-4">
                      <FileText size={64} className={`mx-auto ${darkMode ? "text-gray-500" : "text-gray-300"}`} />
                    </div>
                    <h3 className={`text-lg font-medium mb-2 ${darkMode ? "text-[#E0E0E0]" : "text-black"}`}>
                      Ready to Load Employee Data
                    </h3>
                    <p className="text-sm mb-1">
                      Click <strong>Filters</strong> to refine criteria, then <strong>Download</strong> to generate the report.
                    </p>
                    <p className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                      Download will fetch data using the current filters.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden">
              <Reports
                key={`employee-reports-${activeEmployeeReportsSubTab}`}
                darkMode={darkMode}
                setCurrentPage={setCurrentPage}
                toggleDarkMode={toggleDarkMode}
                scope="employee"
                forcedSubTab={activeEmployeeReportsSubTab}
                hideSidebar
                canDownload={
                  String(activeEmployeeReportsSubTab).startsWith("export-")
                    ? canDownloadExports
                    : canDownloadReports
                }
              />
            </div>
          )
        )}
        </div>
        </div>

        {/* --- START: REVISED MODAL STRUCTURE --- */}
        {showHistory && ( // Only render the entire modal if showHistory is true
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div
              className={`p-6 rounded-md shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              }`}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Configuration History
                </h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-3">
                {/* Now the checks/mapping only run when the modal is visible */}
                {history && Array.isArray(history) && history.length > 0 ? (
                  history
                    .slice() // Should be safe now
                    .reverse()
                    .map((entry, index) => (
                      // --- Your code for rendering ONE history item ---
                      <div
                        key={entry.id}
                        className={`p-4 rounded-md border ${
                          darkMode
                            ? "border-gray-600 bg-gray-600"
                            : "border-gray-300 bg-white"
                        } ${(entry.isActive ?? entry.changeInfo?.isActive) ? "border-l-4 border-l-emerald-500" : ""}`}
                      >
                        {/* ... content of item ... */}
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap" >
                              <p
                                className={`font-semibold ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {entry.patternType === "automated"
                                  ? `Automated Pattern: ${
                                      entry.prefix
                                    }${entry.startingNumber
                                      .toString()
                                      .padStart(entry.numberOfDigits, "0")}`
                                  : entry.patternType === "manual"
                                  ? "Manual Pattern Configuration"
                                  : entry.description ||
                                    "Configuration History"}
                              </p>
                              {(entry.isActive ?? entry.changeInfo?.isActive) && (
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                    darkMode
                                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  }`}
                                  title="Currently active configuration"
                                >
                                  <CheckCircle size={14} className="shrink-0" />
                                  Active
                                </span>
                              )}
                              </div>
                              <p
                                className={`text-sm ${
                                  darkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                Effective Date:{" "}
                                {formatDate(entry.effectiveDate)}
                              </p>
                              <p
                                className={`text-sm ${
                                  darkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {entry.createdAt || entry.created_at
                                  ? `Created: ${formatDate(entry.createdAt || entry.created_at)}`
                                  : `Changed on: ${formatDate(
                                      entry.date ||
                                        entry.createdAt ||
                                        entry.created_at
                                    )}`}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {entry.patternType === "automated" && (
                                <button
                                  onClick={() => {
                                    setSelectedHistoryEntry(entry);
                                    setShowHistoryPreviewModal(true);
                                  }}
                                  className={`p-2 rounded-md ${
                                    darkMode
                                      ? "rh-btn-primary hover:opacity-95"
                                      : "rh-accent-soft hover:bg-redhalo-mint"
                                  } transition-colors`}
                                  title="View Live Preview"
                                >
                                  <Eye size={16} />
                                </button>
                              )}
                              <span
                                className={`px-2 py-1 rounded-full text-sm font-medium ...`}
                              >
                                {entry.patternType
                                  ? entry.patternType.charAt(0).toUpperCase() +
                                    entry.patternType.slice(1)
                                  : entry.organizationLevel || "Unknown"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      // --- End code for one item ---
                    ))
                ) : (
                  // Show "No history" only when modal is open but history is empty/invalid
                  <p
                    className={`text-center ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No configuration history found.
                  </p>
                )}
              </div>
              {/* End Modal Content */}
            </div>
          </div>
        )}
        {/* --- END: REVISED MODAL STRUCTURE --- */}

        {showFilterPopup && (
          <Filters
            onApply={applyAdvancedFiltersFromModal}
            onClose={() => setShowFilterPopup(false)}
            onClear={clearAdvancedFilters}
            darkMode={darkMode}
            initialFilters={advancedFiltersState}
          />
        )}

        {showHistoryPreviewModal && selectedHistoryEntry && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div
              className={`max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl p-6 shadow-xl ${
                darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Historical Configuration Preview
                </h3>
                <button
                  onClick={() => {
                    setShowHistoryPreviewModal(false);
                    setSelectedHistoryEntry(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
              <div
                className={`rounded-lg border p-4 ${
                  darkMode
                    ? "border-[#4C4C4C] bg-[#1f1f1f]"
                    : "border-gray-200 bg-gray-panel"
                }`}
              >
                <div className="text-center">
                  <p
                    className={`mb-3 text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Sample generated codes for{" "}
                    {selectedHistoryEntry.patternType || "historical"} pattern
                    configuration:
                  </p>
                  <p
                    className={`text-sm mb-4 ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Effective Date:{" "}
                    {formatDate(selectedHistoryEntry.effectiveDate)}{" "}
                    |
                    {selectedHistoryEntry.createdAt ||
                    selectedHistoryEntry.created_at
                      ? `Created: ${formatDate(
                          selectedHistoryEntry.createdAt ||
                            selectedHistoryEntry.created_at
                        )}`
                      : `Changed on: ${formatDate(
                          selectedHistoryEntry.date ||
                            selectedHistoryEntry.createdAt ||
                            selectedHistoryEntry.created_at
                        )}`}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {generateHistoryPreviewCodes(selectedHistoryEntry).map(
                      (code, index) => (
                        <div
                          key={index}
                          className={`p-2 rounded-md ${
                            darkMode ? "bg-gray-600" : "bg-white"
                          } border ${
                            darkMode ? "border-gray-500" : "border-gray-200"
                          }`}
                        >
                          <p
                            className={`font-mono text-lg font-semibold ${
                              darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                            }`}
                          >
                            {code}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Formula Manager Modal */}
        <AdvancedFormulaManager
          isOpen={showFormulaManager}
          onClose={() => setShowFormulaManager(false)}
          savedComponents={[]}
          organizationalLevels={{}}
          statutoryRates={{
            PF_RATE: 12,
            ESI_RATE: 1.75,
            GRATUITY_RATE: 4.81,
            TDS_THRESHOLD: 250000,
            MAX_PF_LIMIT: 15000,
          }}
        />
        {showImportSummaryModal && importSummary && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]">
            <div
              className={`rounded-xl shadow-lg p-6 w-[450px] text-center ${
                darkMode ? "bg-gray-800 text-white" : "bg-white"
              }`}
            >
              <h2 className="text-xl font-semibold mb-3">Import Summary</h2>
              <p className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
                {importSummary.validCount} valid records found
              </p>
              <p className="mb-4 text-lg font-medium text-redhalo-red dark:text-red-400">
                {importSummary.invalidCount} invalid records found
              </p>

              {/* {importSummary.invalidCount > 0 && (
                <div
                  className={`text-sm max-h-32 overflow-y-auto mb-4 border p-2 rounded-md text-left ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gray-50 border-gray-200"
                  }`}
                > */}
              {/* <h4 className="font-semibold mb-1">Error Examples:</h4>
                  {importSummary.invalid?.slice(0, 5).map((rec, i) => (
                    <p key={i} className="text-xs text-red-600">
                      - Row {rec?.row || "N/A"}:{" "}
                      {Array.isArray(rec?.errors)
                        ? rec.errors.join(", ")
                        : "No error message provided"}
                    </p>
                  ))} */}
              {/* {importSummary.invalid?.length > 5 && (
                    <p className="text-xs text-gray-400 mt-1">...and more.</p>
                  )} */}
              {/* </div>
              )} */}

              <p
                className={`mb-5 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Do you want to import only the {importSummary.validCount} valid
                records?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  onClick={handleShowDetailedReportWithoutImport}
                  className={`rounded-md px-6 py-2 font-semibold transition-transform hover:scale-105 ${
                    darkMode
                      ? "bg-gray-600 hover:bg-gray-500"
                      : "border border-gray-300 bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  No, Cancel
                </button>
                {importSummary.validCount > 0 && (
                  <button
                    type="button"
                    onClick={handleConfirmImport}
                    disabled={importing} // Keep the disabled check for when importing is active
                    className="rh-btn-primary px-6 py-2 font-semibold transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:scale-100"
                  >
                    {importing ? "Importing..." : "Yes, Import"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Floating Action Dropdown Menu */}
        {openActionDropdown &&
          selectedEmployee &&
          (() => {
            const employee = selectedEmployee;
            if (!employee) return null;

            const empCode = employee.empCode || employee.employeeId || "";
            const empName = employee.user?.name || employee.name || "";
            const isRelievedEmployee =
              String(employee.employmentStatus || "").trim() === "Relieved";
            const relievedActionClass = isRelievedEmployee
              ? darkMode
                ? "cursor-not-allowed border-[#3C3C3C] bg-[#2C2C2C] text-gray-500"
                : "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500"
              : "";

            return (
              <div
                className={`fixed z-[9998] overflow-hidden rounded-lg border shadow-xl ${
                  darkMode
                    ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]"
                    : "border-gray-200 bg-white text-gray-800"
                }`}
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  minWidth: "180px",
                  boxShadow: darkMode
                    ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                    : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                }}
              >
                {/* Header with Employee Code, Name, and Close Button */}
                <div
                  className={`flex items-center justify-between border-b px-4 py-2.5 ${
                    darkMode
                      ? "border-[#3C3C3C] bg-[#1C1C1C]"
                      : "border-gray-200 bg-gray-panel"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-xs font-semibold truncate ${
                        darkMode ? "text-[#E0E0E0]" : "text-gray-800"
                      }`}
                    >
                      {empCode}
                    </div>
                    <div
                      className={`text-xs truncate ${
                        darkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {empName}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenActionDropdown(null);
                    }}
                    className={`ml-2 p-1 rounded hover:opacity-70 transition-opacity ${
                      darkMode
                        ? "text-red-400 hover:bg-[#3C3C3C] hover:text-red-300"
                        : "text-red-500 hover:bg-red-50 hover:text-red-600"
                    }`}
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Action Items */}
                <div>
                  {canEditEmployeeRecords && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAction("edit", employee);
                      setOpenActionDropdown(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:opacity-90 transition-colors flex items-center ${
                      darkMode
                        ? "text-[#E0E0E0] hover:bg-[#3C3C3C]"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    View Employee
                  </button>
                  )}
                  {canRunConfirmation && (
                  <button
                    type="button"
                    disabled={isRelievedEmployee}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isRelievedEmployee) return;
                      if (employee.employmentStatus === "Probation") {
                        handleAction("confirmation", employee);
                      } else if (employee.confirmationDate) {
                        const confirmationDate = formatDate(
                          employee.confirmationDate
                        );
                        toast.error(
                          `Confirmation is already done on ${confirmationDate}`
                        );
                      } else {
                        toast.error(
                          `Confirmation is not applicable for ${employee.employmentStatus} employees`
                        );
                      }
                      setOpenActionDropdown(null);
                    }}
                    className={`flex w-full items-center border-b px-4 py-2.5 text-left text-xs font-medium transition-colors hover:opacity-90 ${
                      isRelievedEmployee
                        ? relievedActionClass
                        : employee.employmentStatus === "Probation"
                        ? darkMode
                          ? "border-[#3C3C3C] bg-[#1C1C1C] text-[#E0E0E0] hover:bg-[#3C3C3C] hover:shadow-md"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-redhalo-mint/40 hover:shadow-md"
                        : relievedActionClass ||
                          (darkMode
                            ? "cursor-not-allowed border-[#3C3C3C] bg-[#2C2C2C] text-gray-500"
                            : "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500")
                    }`}
                  >
                    Confirmation
                  </button>
                  )}
                  {canRunTransfer && (
                  <button
                    type="button"
                    disabled={isRelievedEmployee}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isRelievedEmployee) return;
                      handleAction("transfer", employee);
                      setOpenActionDropdown(null);
                    }}
                    className={`flex w-full items-center border-b px-4 py-2.5 text-left text-xs font-medium transition-colors hover:opacity-90 ${
                      isRelievedEmployee
                        ? relievedActionClass
                        : darkMode
                        ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                        : "border-gray-200 text-gray-700 hover:bg-redhalo-mint/40"
                    }`}
                  >
                    Transfer
                  </button>
                  )}
                  {canEditEmployeeRecords && (
                  <button
                    type="button"
                    disabled={isRelievedEmployee}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isRelievedEmployee) return;
                      handleAction("salaryRevision", employee);
                      setOpenActionDropdown(null);
                    }}
                    className={`flex w-full items-center border-b px-4 py-2.5 text-left text-xs font-medium transition-colors hover:opacity-90 ${
                      isRelievedEmployee
                        ? relievedActionClass
                        : darkMode
                        ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                        : "border-gray-200 text-gray-700 hover:bg-redhalo-mint/40"
                    }`}
                  >
                    Salary Revision
                  </button>
                  )}
                  {canRunResignation && (
                  <button
                    type="button"
                    disabled={isRelievedEmployee}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isRelievedEmployee) return;
                      handleAction("resignation", employee);
                      setOpenActionDropdown(null);
                    }}
                    className={`flex w-full items-center px-4 py-2.5 text-left text-xs font-medium transition-colors hover:opacity-90 ${
                      isRelievedEmployee
                        ? relievedActionClass
                        : darkMode
                        ? "text-[#E0E0E0] hover:bg-[#3C3C3C]"
                        : "text-gray-700 hover:bg-redhalo-mint/40"
                    }`}
                  >
                    Resignation
                  </button>
                  )}
                  {!canOpenAnyRowAction && (
                    <div
                      className={`px-4 py-2.5 text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      View-only access
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
